import React, { useState, useEffect, useMemo, useRef } from "react";
import { Home, UploadCloud, Trash2, Menu, X, ArrowUpRight } from "lucide-react";import { motion } from "framer-motion";
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from "react-router-dom";
import banner from "./assets/banner.jpg";
import iitLogo from "./assets/iitmandi.png";
import iitLogo2 from "./assets/iitmandi1.png";
import conflogo from "./assets/conf_logo.png";
import campus1 from "./assets/campus1.jpg";
import campus2 from "./assets/campus2.jpg";
import campus3 from "./assets/campus3.jpeg";
import conferenceLogo from "./assets/conference-logo.png";
import conf_logo from "./assets/conf_logo_2.png";
import "./App.css";

const publicPath = (path) =>
  `${process.env.PUBLIC_URL}/${path.replace(/^\/+/, "")}`;  // Uncomment this line when hosting on IIT Mandi server

const dates = [
  {
    label: "Abstract Submission",
    date: "Opening soon",
  },
  {
    label: "Acceptance Notification",
    date: "To be announced",
  },
  {
    label: "Registration Deadline",
    date: "15 October 2026",
  },
  {
    label: "Conference",
    date: "14–18 November 2026",
  },
];

function Navbar() {
  const navigate = require("react-router-dom").useNavigate();

  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const forceSolid = location.pathname !== "/";

  const scrollToSection = (id) => {
    setMobileMenuOpen(false);

    if (location.pathname !== "/") {
      navigate("/");

      setTimeout(() => {
        const element = document.getElementById(id);

        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
          });
        }
      }, 150);
    } else {
      const element = document.getElementById(id);

      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
        });
      }
    }
  };

  // We need to change versions to update tick on announcement button and increase the number whenever we add new announcement
  const ANNOUNCEMENT_VERSION = "2";

  const [showNewAnnouncement, setShowNewAnnouncement] = useState(false);
  
  useEffect(() => {
    const lastSeenVersion = localStorage.getItem(
      "quantumPeeksAnnouncementVersion"
    );
  
    setShowNewAnnouncement(lastSeenVersion !== ANNOUNCEMENT_VERSION);
  }, [ANNOUNCEMENT_VERSION]);
  
  const openAnnouncements = () => {
    localStorage.setItem(
      "quantumPeeksAnnouncementVersion",
      ANNOUNCEMENT_VERSION
    );
  
    setShowNewAnnouncement(false);
    setMobileMenuOpen(false);
    navigate("/announcements");
  };

  const goHome = () => {
    setMobileMenuOpen(false);
    navigate("/");

    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }, 100);
  };

  // Desktop navigation classes
  const desktopLinkClass = (path) =>
    `transition-all duration-200 hover:scale-110 hover:text-yellow-300 ${
      location.pathname === path
        ? "text-yellow-300 drop-shadow-[0_0_8px_rgba(253,224,71,0.65)]"
        : "text-white"
    }`;

  // Mobile navigation classes
  const mobileLinkClass = (path) =>
    `px-4 py-3 rounded-md transition ${
      location.pathname === path
        ? "bg-white/10 text-yellow-300"
        : "text-white hover:bg-white/10 hover:text-yellow-300"
    }`;

  return (
    <nav
      className={`fixed left-0 top-0 z-50 w-full transition-all duration-300 ${
        scrolled || forceSolid || mobileMenuOpen
          ? "bg-blue-900/95 text-white shadow-lg backdrop-blur-lg"
          : "bg-transparent text-white"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
        {/* Main navbar row */}
        <div className="flex items-center justify-between py-3 md:py-4">
          {/* Clickable logos */}
          <button
            type="button"
            onClick={goHome}
            className="flex items-center gap-2 sm:gap-4"
            aria-label="Go to homepage"
          >
            {/* Larger conference logo */}
            <img
              src={conflogo}
              alt="Quantum Peeks Conference Logo"
              className="
                h-16 w-16 object-contain
                transition-transform duration-300
                hover:scale-105
                sm:h-20 sm:w-20
                md:h-24 md:w-24
              "
            />

            {/* IIT Mandi logo */}
            <img
              src={iitLogo}
              alt="IIT Mandi"
              className="
                h-14 w-14 object-contain
                transition-transform duration-300
                hover:scale-105
                sm:h-16 sm:w-16
                md:h-20 md:w-20
              "
            />
          </button>

          {/* Desktop navigation */}
          <div className="hidden items-center gap-8 text-sm font-bold xl:gap-10 lg:flex">
            <button
              type="button"
              onClick={goHome}
              className={`flex items-center gap-1 ${desktopLinkClass("/")}`}
            >
              <Home size={16} strokeWidth={2} />
              Home
            </button>

            <Link
              to="/speakers"
              className={desktopLinkClass("/speakers")}
            >
              Speakers
            </Link>

            <Link
              to="/programme"
              className={desktopLinkClass("/programme")}
            >
              Programme
            </Link>

            <Link
              to="/committee"
              className={desktopLinkClass("/committee")}
            >
              Committee
            </Link>

            <button
              type="button"
              onClick={() => scrollToSection("dates")}
              className="text-white transition-transform duration-200 hover:scale-110 hover:text-yellow-300"
            >
              Dates
            </button>

            <button
              type="button"
              onClick={() => scrollToSection("venue")}
              className="text-white transition-transform duration-200 hover:scale-110 hover:text-yellow-300"
            >
              Venue
            </button>

            <button
            type="button"
            onClick={openAnnouncements}
            className={`relative ${desktopLinkClass("/announcements")}`}
            >
            Announcements

            {showNewAnnouncement && (
              <span
                className="
                  absolute
                  -top-5
                  right-0
                  translate-x-1/2
                  rounded-full
                  bg-red-600
                  border border-yellow-300
                  px-2 py-[2px]
                  text-[9px]
                  font-extrabold
                  uppercase
                  tracking-wider
                  text-yellow-300
                  animate-pulse
                  shadow-[0_0_12px_rgba(239,68,68,0.8)]
                "
              >
                NEW
              </span>
            )}
            </button>
            <button
              type="button"
              onClick={() => scrollToSection("contact")}
              className="text-white transition-transform duration-200 hover:scale-110 hover:text-yellow-300"
            >
              Contact
            </button>
          </div>

          {/* Mobile Sponsor + Hamburger */}
          <div className="flex items-center gap-2 lg:hidden">
          <button
            type="button"
            onClick={() => scrollToSection("sponsors")}
            className="
              group
              flex
              items-center
              gap-2
              rounded-full
              border
              border-yellow-300/70
              bg-indigo-950/80
              px-3
              py-2
              text-[11px]
              font-bold
              text-white
              shadow-[0_8px_25px_rgba(15,23,42,0.35)]
              backdrop-blur-md
              transition-all
              duration-300
              hover:border-yellow-300
              hover:bg-indigo-900
            "
          >
            {/* Yellow blinking dot */}
            <span className="relative flex h-2.5 w-2.5">
              <span
                className="
                  absolute
                  inline-flex
                  h-full
                  w-full
                  animate-ping
                  rounded-full
                  bg-yellow-300
                  opacity-70
                "
              />
              <span
                className="
                  relative
                  inline-flex
                  h-2.5
                  w-2.5
                  rounded-full
                  bg-yellow-300
                "
              />
            </span>

            <span>Become a Sponsor</span>
          </button>

            {/* Mobile hamburger */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen((current) => !current)}
              className="flex h-11 w-11 items-center justify-center rounded-md border border-white/30 bg-white/10 transition hover:bg-white/20"
              aria-label={
                mobileMenuOpen
                  ? "Close navigation menu"
                  : "Open navigation menu"
              }
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? (
                <X size={28} strokeWidth={2} />
              ) : (
                <Menu size={28} strokeWidth={2} />
              )}
            </button>

          </div>
        </div>
        

        {/* Mobile navigation */}
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out lg:hidden ${
            mobileMenuOpen
              ? "max-h-[600px] pb-5 opacity-100"
              : "max-h-0 opacity-0"
          }`}
        >
          <div className="flex flex-col border-t border-white/20 pt-3 font-semibold text-white">
            <button
              type="button"
              onClick={goHome}
              className={`flex items-center gap-3 text-left ${mobileLinkClass(
                "/"
              )}`}
            >
              <Home size={18} />
              Home
            </button>

            <Link
              to="/speakers"
              onClick={() => setMobileMenuOpen(false)}
              className={mobileLinkClass("/speakers")}
            >
              Speakers
            </Link>

            <Link
              to="/programme"
              onClick={() => setMobileMenuOpen(false)}
              className={mobileLinkClass("/programme")}
            >
              Programme
            </Link>

            <Link
              to="/committee"
              onClick={() => setMobileMenuOpen(false)}
              className={mobileLinkClass("/committee")}
            >
              Committee
            </Link>

            <button
              type="button"
              onClick={() => scrollToSection("dates")}
              className="rounded-md px-4 py-3 text-left text-white transition hover:bg-white/10 hover:text-yellow-300"
            >
              Important Dates
            </button>

            <button
              type="button"
              onClick={() => scrollToSection("venue")}
              className="rounded-md px-4 py-3 text-left text-white transition hover:bg-white/10 hover:text-yellow-300"
            >
              Venue
            </button>

            <Link
              to="/announcements"
              onClick={() => setMobileMenuOpen(false)}
              className={mobileLinkClass("/announcements")}
            >
              Announcements
            </Link>

            <button
              type="button"
              onClick={() => scrollToSection("contact")}
              className="rounded-md px-4 py-3 text-left text-white transition hover:bg-white/10 hover:text-yellow-300"
            >
              Contact
            </button>

            <Link
              to="/registration"
              onClick={() => setMobileMenuOpen(false)}
              className={`mx-4 mt-3 rounded-md py-3 text-center font-bold transition ${
                location.pathname === "/registration"
                  ? "bg-yellow-300 text-blue-950 ring-2 ring-white/60"
                  : "bg-yellow-400 text-blue-950 hover:bg-yellow-300"
              }`}
            >
              Register
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
}


function Hero() {

  const scrollToSection = (id) => {
    const element = document.getElementById(id);

    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };
  return (
    <section
      className="relative min-h-screen flex items-center justify-center text-center text-white bg-cover bg-center"
      style={{ backgroundImage: `url(${banner})` }}
    >
      {/* Dark blue transparent overlay like conference sites */}
      <div className="absolute inset-0 bg-gradient-to-b from-blue-800/50 via-blue-900/60 to-blue-950/70"></div>

      {/* Sponsor button */}
      <button
        type="button"
        onClick={() => scrollToSection("sponsors")}
        className="
          hidden
          lg:inline-flex
          group
          absolute
          top-[30px]
          right-[70px]          
          z-10000
          inline-flex
          items-center
          gap-2
          rounded-full
          border
          border-yellow-300/70
          bg-indigo-950/80
          px-4
          py-2.5
          text-xs
          font-bold
          text-white
          shadow-[0_12px_35px_rgba(15,23,42,0.35)]
          backdrop-blur-md
          transition-all
          duration-300
          hover:-translate-y-1
          hover:border-yellow-300
          hover:bg-indigo-900

          sm:top-[10px]
          sm:right-[10px]
          sm:px-5
          sm:py-3
          sm:text-sm

          lg:top-[100px]
          lg:right-[88px]
        "
      >
        <span className="relative flex h-2.5 w-2.5">
          <span
            className="
              absolute
              inline-flex
              h-full
              w-full
              animate-ping
              rounded-full
              bg-yellow-300
              opacity-70
            "
          />

          <span
            className="
              relative
              inline-flex
              h-2.5
              w-2.5
              rounded-full
              bg-yellow-300
            "
          />
        </span>

        <span>Become a Sponsor</span>

        <span
          className="
            text-yellow-300
            transition-transform
            duration-300
            group-hover:translate-x-1
          "
        >
          →
        </span>
      </button>

      <div className="relative max-w-4xl px-8">

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="text-5xl md:text-7xl font-bold tracking-tight"
        >
          Quantum Peeks 2026
          <span className="block text-2xl md:text-3xl font-semibold mt-3">
            A Himalayan Summit on Coherence in Light and Matter
          </span>
        </motion.h1>

        <div className="mt-8 px-15 text-gray-200 text-lg space-y-1">
          <p className="font-bold text-yellow-400 text-2xl">November 14 -18, 2026</p>
          <p className="font-bold text-2xl">
          Indian Institute of Technology Mandi
        </p>
        </div>


        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-5">

        {/* Register Button */}
        <Link
        to="/registration"
        className="
          group
          relative
          inline-flex
          items-center
          justify-center
          overflow-hidden
          rounded-full
          px-10
          py-4
          font-bold
          text-slate-900
          bg-gradient-to-r
          from-yellow-300
          via-amber-300
          to-yellow-500
          border
          border-yellow-200/80
          shadow-[0_12px_35px_rgba(234,179,8,0.35)]
          transition-all
          duration-500
          hover:-translate-y-1
          hover:scale-105
          hover:shadow-[0_18px_50px_rgba(250,204,21,0.55)]
        "
        >
        {/* Shine */}
        <span
          className="
            absolute inset-0 overflow-hidden rounded-full
          "
        >
          <span
            className="
              absolute
              inset-y-0
              -left-1/2
              w-1/3
              skew-x-[-25deg]
              bg-gradient-to-r
              from-transparent
              via-white/70
              to-transparent
              transition-all
              duration-700
              group-hover:left-[130%]
            "
          />
        </span>

        {/* Soft glow */}
        <span
          className="
            absolute
            inset-0
            rounded-full
            opacity-0
            group-hover:opacity-100
            bg-yellow-200/40
            blur-lg
            transition-opacity
            duration-500
          "
        />

        <span className="relative z-10 flex items-center gap-3 text-lg">
          Register

        <svg
        className="
          w-5 h-5
          transition-transform duration-300
          group-hover:translate-x-1
          group-hover:-translate-y-1
        "
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2.5"
          d="M7 17L17 7M17 7H9M17 7V15"
        />
        </svg>
        </span>
        </Link>

        {/* Submit Abstract Button */}
        <Link
          to="/abstract"
          className="
            group relative
            inline-flex min-w-[230px]
            items-center justify-center
            overflow-visible
            rounded-full
            border border-cyan-300/40
            bg-gradient-to-r
            from-indigo-700
            via-blue-600
            to-cyan-500
            px-8 py-4
            text-base font-semibold
            text-white
            shadow-[0_14px_40px_rgba(37,99,235,0.35)]
            transition-all duration-500
            hover:-translate-y-1
            hover:scale-[1.02]
            hover:shadow-[0_20px_55px_rgba(34,211,238,0.4)]
          "
        >
          {/* Animated glow */}
          <span
            className="
              absolute -inset-[2px]
              -z-10
              rounded-full
              bg-gradient-to-r
              from-cyan-400
              via-blue-500
              to-indigo-500
              opacity-0 blur-md
              transition-opacity duration-500
              group-hover:opacity-70
            "
          />

          {/* Shine */}
          <span className="absolute inset-0 overflow-hidden rounded-full">
            <span
              className="
                absolute inset-y-0 -left-1/2
                w-1/3 skew-x-[-20deg]
                bg-gradient-to-r
                from-transparent via-white/40 to-transparent
                transition-all duration-700
                group-hover:left-[120%]
              "
            />
          </span>

          <span className="relative z-10 flex items-center gap-3">
            Submit Abstract

            <svg
              className="
                h-5 w-5
                transition-transform duration-300
                group-hover:-translate-y-1
              "
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 4v12m0-12l-4 4m4-4l4 4M5 20h14"
              />
            </svg>
          </span>

          {/* OPEN Badge */}
          {/* COMING SOON Star Badge */}
          <div
            className="
              absolute
              -top-5
              -right-8
              w-16
              h-16
              animate-float-star
              pointer-events-none
              z-20
            "
          >
            {/* Glow */}
            <div
              className="
                absolute
                inset-0
                rounded-full
                bg-red-500/45
                blur-xl
              "
            />

            {/* Gold outer star */}
            <div
              className="absolute inset-0"
              style={{
                clipPath:
                  "polygon(50% 0%,61% 35%,98% 35%,68% 57%,79% 91%,50% 72%,21% 91%,32% 57%,2% 35%,39% 35%)",
                background:
                  "linear-gradient(145deg,#fde68a,#facc15,#b45309)",
                filter:
                  "drop-shadow(0 6px 14px rgba(220,38,38,0.5))",
              }}
            />

            {/* Red inner star */}
            <div
              className="
                absolute
                inset-[3px]
                flex
                items-center
                justify-center
                text-center
                text-white
                font-black
              "
              style={{
                clipPath:
                  "polygon(50% 0%,61% 35%,98% 35%,68% 57%,79% 91%,50% 72%,21% 91%,32% 57%,2% 35%,39% 35%)",
                background:
                  "radial-gradient(circle at 30% 25%, #ffb3b3 0%, #ef4444 35%, #dc2626 65%, #991b1b 100%)",
              }}
            >
              <span
                className="text-[7px] leading-[0.9] tracking-[0.05em]"
                style={{
                  textShadow:
                    "0 1px 3px rgba(0,0,0,.8), 0 0 5px rgba(255,255,255,.35)",
                }}
              >
                {/* COMING
                <br />
                SOON */}
                OPEN
              </span>
            </div>

            {/* Sparkle */}
            <div
              className="
                absolute
                top-2
                left-3
                w-1.5
                h-1.5
                rounded-full
                bg-white
                shadow-[0_0_8px_white]
              "
            />
          </div>
        </Link>

        </div>

      </div>
    </section>
  );
}

function ProgrammePage() {
  return (
    // <div className="min-h-screen bg-gray-50">
    <div className="min-h-screen bg-gray-50 pt-24">

      {/* Banner */}
      <section
        className="relative h-64 md:h-40 flex items-center justify-center bg-cover bg-center"
        style={{
          // backgroundImage: "url('/programme-banner.jpg')", // add later
          // backgroundImage: `url(${publicPath("programme-banner.jpg")})`,
        }}
      >
        <div className="absolute inset-0 bg-blue-900/70"></div>

        <div className="relative text-center text-white px-6">
          <h1 className="text-5xl font-bold mb-4">Programme</h1>
          <p className="text-xl text-blue-100">
            Quantum Peeks 2026
          </p>
        </div>
      </section>
      

      {/* Main Section */}
      <section className="max-w-5xl mx-auto px-6 py-20">

        <Link
          to="/"
          className="
            group mb-12 my-10 inline-flex items-center gap-3
            text-sm font-semibold text-slate-600
            transition-colors duration-300
            hover:text-blue-700
          "
        >
          <span className="transition-transform duration-300 group-hover:-translate-x-1">
            ←
          </span>
          Back to Home
        </Link>

        <div className="bg-white rounded-3xl shadow-xl p-12 text-center">

          <div className="w-24 h-24 mx-auto rounded-full bg-blue-100 flex items-center justify-center mb-8">
            <svg
              className="w-12 h-12 text-blue-700"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8 7V3m8 4V3M5 11h14M5 5h14a2 2 0 012 2v12a2 2 0 01-2
                2H5a2 2 0 01-2-2V7a2 2 0 012-2z"
              />
            </svg>
          </div>

          <h2 className="text-4xl font-bold text-gray-800 mb-6">
            Programme Coming Soon
          </h2>

          <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
            The detailed scientific programme, including keynote lectures,
            invited talks, contributed presentations, poster sessions, and
            social events, will be announced soon.
          </p>

          <div className="mt-10 grid md:grid-cols-4 gap-6">

          <div className="bg-red-50 rounded-xl p-6">
              <h3 className="font-semibold text-blue-800 mb-2">
              Plenary Lectures
              </h3>
              <p className="text-gray-600 text-sm">
                Schedule will be updated after confirmation.
              </p>
            </div>


            <div className="bg-blue-50 rounded-xl p-6">
              <h3 className="font-semibold text-blue-800 mb-2">
                Invited Talks
              </h3>
              <p className="text-gray-600 text-sm">
                Schedule will be updated after confirmation.
              </p>
            </div>

            <div className="bg-cyan-50 rounded-xl p-6">
              <h3 className="font-semibold text-cyan-800 mb-2">
                Contributed Talks
              </h3>
              <p className="text-gray-600 text-sm">
                Presentation schedule will be available soon.
              </p>
            </div>

            <div className="bg-indigo-50 rounded-xl p-6">
              <h3 className="font-semibold text-indigo-800 mb-2">
                Poster Sessions
              </h3>
              <p className="text-gray-600 text-sm">
                Poster timings and venue details will be announced.
              </p>
            </div>

          </div>

          <div className="mt-12">
            <span className="inline-flex items-center rounded-full bg-blue-600 px-6 py-3 text-white font-medium shadow-lg">
              Programme details will be available shortly.
            </span>
          </div>

        </div>

      </section>

    </div>
  );
}


// function ProgrammePage() {
//   const [expandedPlenary, setExpandedPlenary] = useState(null);

//   const scrollToProgrammeSection = (sectionId) => {
//     const section = document.getElementById(sectionId);

//     if (section) {
//       section.scrollIntoView({
//         behavior: "smooth",
//         block: "start",
//       });
//     }
//   };

//   const programmeDays = [
//     {
//       day: "Day 1",
//       date: "14 November 2026",
//       title: "Arrival and Opening",
//       events: [
//         {
//           time: "09:00 – 12:30",
//           type: "Registration",
//           title: "Participant Registration",
//           speaker: "",
//           venue: "Conference Reception Desk",
//         },
//         {
//           time: "12:30 – 14:00",
//           type: "Lunch",
//           title: "Welcome Lunch",
//           speaker: "",
//           venue: "Dining Hall",
//         },
//         {
//           time: "14:00 – 14:30",
//           type: "Opening",
//           title: "Inaugural Ceremony",
//           speaker: "Organising Committee",
//           venue: "Main Auditorium",
//         },
//         {
//           time: "14:30 – 15:30",
//           type: "Plenary",
//           title: "Opening Plenary Lecture",
//           speaker: "Prof. Franco Dalfovo",
//           venue: "Main Auditorium",
//         },
//         {
//           time: "15:30 – 16:00",
//           type: "Break",
//           title: "Tea and Coffee Break",
//           speaker: "",
//           venue: "Conference Foyer",
//         },
//         {
//           time: "16:00 – 17:30",
//           type: "Invited",
//           title: "Invited Talks – Session I",
//           speaker: "Speakers to be announced",
//           venue: "Main Auditorium",
//         },
//         {
//           time: "18:00 – 19:00",
//           type: "Social",
//           title: "Welcome Reception",
//           speaker: "",
//           venue: "IIT Mandi Campus",
//         },
//       ],
//     },
//     {
//       day: "Day 2",
//       date: "15 November 2026",
//       title: "Quantum Matter and Many-Body Physics",
//       events: [
//         {
//           time: "09:00 – 10:00",
//           type: "Plenary",
//           title: "Plenary Lecture II",
//           speaker: "Prof. Adolfo del Campo",
//           venue: "Main Auditorium",
//         },
//         {
//           time: "10:00 – 10:30",
//           type: "Break",
//           title: "Tea and Coffee Break",
//           speaker: "",
//           venue: "Conference Foyer",
//         },
//         {
//           time: "10:30 – 12:30",
//           type: "Invited",
//           title: "Invited Talks – Session II",
//           speaker: "Speakers to be announced",
//           venue: "Main Auditorium",
//         },
//         {
//           time: "12:30 – 14:00",
//           type: "Lunch",
//           title: "Lunch Break",
//           speaker: "",
//           venue: "Dining Hall",
//         },
//         {
//           time: "14:00 – 15:30",
//           type: "Contributed",
//           title: "Contributed Talks – Session I",
//           speaker: "Selected participants",
//           venue: "Main Auditorium",
//         },
//         {
//           time: "15:30 – 16:00",
//           type: "Break",
//           title: "Tea and Coffee Break",
//           speaker: "",
//           venue: "Conference Foyer",
//         },
//         {
//           time: "16:00 – 18:00",
//           type: "Poster",
//           title: "Poster Session I",
//           speaker: "Poster presenters",
//           venue: "Poster Hall",
//         },
//       ],
//     },
//     {
//       day: "Day 3",
//       date: "16 November 2026",
//       title: "Quantum Optics and Coherence",
//       events: [
//         {
//           time: "09:00 – 10:00",
//           type: "Plenary",
//           title: "Plenary Lecture III",
//           speaker: "Speaker to be announced",
//           venue: "Main Auditorium",
//         },
//         {
//           time: "10:00 – 10:30",
//           type: "Break",
//           title: "Tea and Coffee Break",
//           speaker: "",
//           venue: "Conference Foyer",
//         },
//         {
//           time: "10:30 – 12:30",
//           type: "Invited",
//           title: "Invited Talks – Session III",
//           speaker: "Speakers to be announced",
//           venue: "Main Auditorium",
//         },
//         {
//           time: "12:30 – 14:00",
//           type: "Lunch",
//           title: "Lunch Break",
//           speaker: "",
//           venue: "Dining Hall",
//         },
//         {
//           time: "14:00 – 15:30",
//           type: "Contributed",
//           title: "Contributed Talks – Session II",
//           speaker: "Selected participants",
//           venue: "Main Auditorium",
//         },
//         {
//           time: "15:30 – 16:00",
//           type: "Break",
//           title: "Tea and Coffee Break",
//           speaker: "",
//           venue: "Conference Foyer",
//         },
//         {
//           time: "16:00 – 18:00",
//           type: "Poster",
//           title: "Poster Session II",
//           speaker: "Poster presenters",
//           venue: "Poster Hall",
//         },
//         {
//           time: "18:30 onwards",
//           type: "Social",
//           title: "Cultural Evening",
//           speaker: "",
//           venue: "IIT Mandi Campus",
//         },
//       ],
//     },
//     {
//       day: "Day 4",
//       date: "17 November 2026",
//       title: "Emerging Directions and Applications",
//       events: [
//         {
//           time: "09:00 – 10:00",
//           type: "Plenary",
//           title: "Plenary Lecture IV",
//           speaker: "Speaker to be announced",
//           venue: "Main Auditorium",
//         },
//         {
//           time: "10:00 – 10:30",
//           type: "Break",
//           title: "Tea and Coffee Break",
//           speaker: "",
//           venue: "Conference Foyer",
//         },
//         {
//           time: "10:30 – 12:30",
//           type: "Invited",
//           title: "Invited Talks – Session IV",
//           speaker: "Speakers to be announced",
//           venue: "Main Auditorium",
//         },
//         {
//           time: "12:30 – 14:00",
//           type: "Lunch",
//           title: "Lunch Break",
//           speaker: "",
//           venue: "Dining Hall",
//         },
//         {
//           time: "14:00 – 15:30",
//           type: "Panel",
//           title: "Panel Discussion: Future of Quantum Science",
//           speaker: "Panel members to be announced",
//           venue: "Main Auditorium",
//         },
//         {
//           time: "15:30 – 16:00",
//           type: "Break",
//           title: "Tea and Coffee Break",
//           speaker: "",
//           venue: "Conference Foyer",
//         },
//         {
//           time: "16:00 – 17:30",
//           type: "Contributed",
//           title: "Contributed Talks – Session III",
//           speaker: "Selected participants",
//           venue: "Main Auditorium",
//         },
//         {
//           time: "19:00 onwards",
//           type: "Social",
//           title: "Conference Dinner",
//           speaker: "",
//           venue: "Venue to be announced",
//         },
//       ],
//     },
//     {
//       day: "Day 5",
//       date: "18 November 2026",
//       title: "Closing Sessions",
//       events: [
//         {
//           time: "09:00 – 10:00",
//           type: "Plenary",
//           title: "Closing Plenary Lecture",
//           speaker: "Speaker to be announced",
//           venue: "Main Auditorium",
//         },
//         {
//           time: "10:00 – 10:30",
//           type: "Break",
//           title: "Tea and Coffee Break",
//           speaker: "",
//           venue: "Conference Foyer",
//         },
//         {
//           time: "10:30 – 12:00",
//           type: "Invited",
//           title: "Invited Talks – Final Session",
//           speaker: "Speakers to be announced",
//           venue: "Main Auditorium",
//         },
//         {
//           time: "12:00 – 12:30",
//           type: "Closing",
//           title: "Closing Ceremony and Vote of Thanks",
//           speaker: "Organising Committee",
//           venue: "Main Auditorium",
//         },
//         {
//           time: "12:30 – 14:00",
//           type: "Lunch",
//           title: "Farewell Lunch",
//           speaker: "",
//           venue: "Dining Hall",
//         },
//       ],
//     },
//   ];

//   const plenaryLectures = [
//     {
//       date: "14 November 2026",
//       time: "14:30 – 15:30",
//       speaker: "Prof. Franco Dalfovo",
//       affiliation:
//         "Pitaevskii BEC Center, University of Trento, Italy",
//       title: "Opening Plenary Lecture",
//       abstract:
//         "Ultracold atomic gases continue to provide an exceptional platform for exploring the physics of quantum many-body systems under highly controlled conditions. This lecture will review recent theoretical advances in Bose–Einstein condensates and interacting quantum fluids, with particular emphasis on nonequilibrium dynamics, collective excitations, and spontaneous symmetry breaking. Topics will include the emergence of coherence during phase transitions, universal scaling behavior in driven quantum systems, and the formation of topological defects in superfluids. Connections between microscopic interactions, macroscopic quantum phenomena, and current experimental developments will be discussed, highlighting the role of ultracold gases as quantum simulators for fundamental problems in condensed matter and statistical physics.",
//       image: "/speakers/Franco_Dalfovo.jpg",
//     },
//     {
//       date: "15 November 2026",
//       time: "09:00 – 10:00",
//       speaker: "Prof. Adolfo del Campo",
//       affiliation: "University of Luxembourg, Luxembourg",
//       title: "Plenary Lecture II",
//       abstract:
//         "Quantum systems driven far from equilibrium exhibit universal dynamical behavior that bridges condensed matter physics, quantum information, and statistical mechanics. This lecture will provide an overview of recent advances in nonequilibrium quantum dynamics, focusing on universal scaling phenomena, defect formation across continuous phase transitions, and shortcuts to adiabaticity. Particular emphasis will be placed on the interplay between quantum criticality, coherence, and topology in ultracold atomic systems. The presentation will highlight both theoretical developments and experimental progress, illustrating how controlled quantum platforms enable fundamental tests of nonequilibrium physics while opening new directions for quantum technologies.",
//       image: "/speakers/Adolfo_del_Campo.jpeg",
//     },
//     {
//       date: "16 November 2026",
//       time: "09:00 – 10:00",
//       speaker: "To be announced",
//       affiliation: "Institution to be announced",
//       title: "Plenary Lecture III",
//       abstract:
//         "The lecture title and abstract will be announced after confirmation.",
//       image: publicPath("speakers/default-speaker.jpg"),
//     },
//     {
//       date: "17 November 2026",
//       time: "09:00 – 10:00",
//       speaker: "To be announced",
//       affiliation: "Institution to be announced",
//       title: "Plenary Lecture IV",
//       abstract:
//         "The lecture title and abstract will be announced after confirmation.",
//       image: publicPath("speakers/default-speaker.jpg"),
//     },
//     {
//       date: "18 November 2026",
//       time: "09:00 – 10:00",
//       speaker: "To be announced",
//       affiliation: "Institution to be announced",
//       title: "Closing Plenary Lecture",
//       abstract:
//         "The lecture title and abstract will be announced after confirmation.",
//       image: publicPath("speakers/default-speaker.jpg"),
//     },
//   ];

//   const invitedTalks = [
//     {
//       session: "Invited Session I",
//       date: "14 November 2026",
//       time: "16:00 – 16:30",
//       speaker: "To be announced",
//       title: "Invited Talk Title",
//     },
//     {
//       session: "Invited Session I",
//       date: "14 November 2026",
//       time: "16:30 – 17:00",
//       speaker: "To be announced",
//       title: "Invited Talk Title",
//     },
//     {
//       session: "Invited Session I",
//       date: "14 November 2026",
//       time: "17:00 – 17:30",
//       speaker: "To be announced",
//       title: "Invited Talk Title",
//     },
//     {
//       session: "Invited Session II",
//       date: "15 November 2026",
//       time: "10:30 – 11:00",
//       speaker: "To be announced",
//       title: "Invited Talk Title",
//     },
//     {
//       session: "Invited Session II",
//       date: "15 November 2026",
//       time: "11:00 – 11:30",
//       speaker: "To be announced",
//       title: "Invited Talk Title",
//     },
//     {
//       session: "Invited Session II",
//       date: "15 November 2026",
//       time: "11:30 – 12:00",
//       speaker: "To be announced",
//       title: "Invited Talk Title",
//     },
//   ];

//   const contributedTalks = [
//     {
//       code: "CT-01",
//       date: "15 November 2026",
//       time: "14:00 – 14:15",
//       presenter: "To be announced",
//       title: "Contributed Presentation Title",
//     },
//     {
//       code: "CT-02",
//       date: "15 November 2026",
//       time: "14:15 – 14:30",
//       presenter: "To be announced",
//       title: "Contributed Presentation Title",
//     },
//     {
//       code: "CT-03",
//       date: "15 November 2026",
//       time: "14:30 – 14:45",
//       presenter: "To be announced",
//       title: "Contributed Presentation Title",
//     },
//     {
//       code: "CT-04",
//       date: "15 November 2026",
//       time: "14:45 – 15:00",
//       presenter: "To be announced",
//       title: "Contributed Presentation Title",
//     },
//   ];

//   const posterSessions = [
//     {
//       session: "Poster Session I",
//       date: "15 November 2026",
//       time: "16:00 – 18:00",
//       venue: "Poster Hall",
//       posterNumbers: "P01 – P40",
//     },
//     {
//       session: "Poster Session II",
//       date: "16 November 2026",
//       time: "16:00 – 18:00",
//       venue: "Poster Hall",
//       posterNumbers: "P41 – P80",
//     },
//   ];

//   const getEventStyle = (type) => {
//     const styles = {
//       Plenary:
//         "bg-purple-100 text-purple-800 border-purple-200",
//       Invited:
//         "bg-blue-100 text-blue-800 border-blue-200",
//       Contributed:
//         "bg-cyan-100 text-cyan-800 border-cyan-200",
//       Poster:
//         "bg-indigo-100 text-indigo-800 border-indigo-200",
//       Break:
//         "bg-amber-100 text-amber-800 border-amber-200",
//       Lunch:
//         "bg-orange-100 text-orange-800 border-orange-200",
//       Social:
//         "bg-pink-100 text-pink-800 border-pink-200",
//       Opening:
//         "bg-green-100 text-green-800 border-green-200",
//       Closing:
//         "bg-red-100 text-red-800 border-red-200",
//       Registration:
//         "bg-gray-100 text-gray-800 border-gray-200",
//       Panel:
//         "bg-teal-100 text-teal-800 border-teal-200",
//     };

//     return (
//       styles[type] ||
//       "bg-gray-100 text-gray-800 border-gray-200"
//     );
//   };

//   const BackToNavigation = ({ color = "blue" }) => {
//     const styles = {
//       blue:
//         "border-blue-200 text-blue-700 hover:border-blue-400",
//       purple:
//         "border-purple-200 text-purple-700 hover:border-purple-400",
//       cyan:
//         "border-cyan-200 text-cyan-700 hover:border-cyan-400",
//       indigo:
//         "border-indigo-200 text-indigo-700 hover:border-indigo-400",
//     };

//     return (
//       <div className="mt-10 flex justify-center">
//         <button
//           type="button"
//           onClick={() =>
//             scrollToProgrammeSection("programme-overview")
//           }
//           className={`inline-flex items-center gap-2 rounded-full border bg-white px-5 py-2.5 text-sm font-bold shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${styles[color]}`}
//         >
//           <span>↑</span>
//           Programme navigation
//         </button>
//       </div>
//     );
//   };

//   const ProgrammeTableHeader = ({ title, subtitle }) => (
//     <div className="relative overflow-hidden bg-gradient-to-r from-blue-950 via-indigo-900 to-blue-800 px-6 py-6 text-white">
//       <div className="absolute -right-10 -top-10 h-36 w-36 rounded-full bg-white/10" />
//       <div className="absolute -bottom-14 -left-10 h-40 w-40 rounded-full bg-cyan-300/10" />

//       <div className="relative flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
//         <div className="flex items-center gap-4">
//           <img
//             src={conferenceLogo}
//             alt="Quantum Peeks 2026"
//             className="h-16 w-auto object-contain drop-shadow-lg"
//           />

//           <div>
//             <h3 className="text-2xl font-bold">{title}</h3>

//             <p className="mt-1 text-sm text-blue-100">
//               {subtitle}
//             </p>
//           </div>
//         </div>

//         <div className="rounded-full border border-white/30 bg-white/10 px-4 py-2 text-sm font-semibold backdrop-blur-sm">
//           Quantum Peeks 2026
//         </div>
//       </div>
//     </div>
//   );

//   const navigationCards = [
//     {
//       target: "daily-programme",
//       count: programmeDays.length,
//       title: "Conference Days",
//       text:
//         "View the complete programme from registration and inauguration through the closing session.",
//       action: "View daily schedule",
//       icon: "📅",
//       card:
//         "border-slate-200 from-white to-slate-50 focus:ring-slate-200",
//       circle: "bg-slate-900",
//       decoration: "bg-slate-200/60",
//     },
//     {
//       target: "plenary-lectures",
//       count: plenaryLectures.length,
//       title: "Plenary Lectures",
//       text:
//         "Broad scientific perspectives presented by distinguished researchers.",
//       action: "Meet plenary speakers",
//       icon: "🎓",
//       card:
//         "border-purple-200 from-white to-purple-50 focus:ring-purple-200",
//       circle:
//         "bg-gradient-to-br from-purple-700 to-indigo-700",
//       decoration: "bg-purple-200/60",
//     },
//     {
//       target: "invited-talks",
//       count: invitedTalks.length,
//       title: "Invited Talks",
//       text:
//         "Focused scientific presentations from invited experts across quantum science.",
//       action: "Explore invited talks",
//       icon: "🎤",
//       card:
//         "border-blue-200 from-white to-blue-50 focus:ring-blue-200",
//       circle:
//         "bg-gradient-to-br from-blue-700 to-cyan-600",
//       decoration: "bg-blue-200/60",
//     },
//     {
//       target: "contributed-talks",
//       count: "3",
//       title: "Contributed Sessions",
//       text:
//         "Selected oral presentations from participating students and researchers.",
//       action: "View contributed talks",
//       icon: "💡",
//       card:
//         "border-cyan-200 from-white to-cyan-50 focus:ring-cyan-200",
//       circle:
//         "bg-gradient-to-br from-cyan-700 to-teal-600",
//       decoration: "bg-cyan-200/60",
//     },
//     {
//       target: "poster-sessions",
//       count: posterSessions.length,
//       title: "Poster Sessions",
//       text:
//         "Interactive poster presentations, networking, and focused scientific discussions.",
//       action: "View poster sessions",
//       icon: "🖼️",
//       card:
//         "border-indigo-200 from-white to-indigo-50 focus:ring-indigo-200",
//       circle:
//         "bg-gradient-to-br from-indigo-700 to-violet-700",
//       decoration: "bg-indigo-200/60",
//     },
//   ];

//   return (
//     <div className="min-h-screen bg-slate-50 pt-24">
//       {/* Hero banner */}
//       <section className="relative flex min-h-[460px] items-center justify-center overflow-hidden bg-slate-950">
//         <svg
//           className="absolute inset-0 h-full w-full"
//           viewBox="0 0 1600 600"
//           preserveAspectRatio="xMidYMid slice"
//           aria-hidden="true"
//         >
//           <defs>
//             <linearGradient
//               id="programmeSky"
//               x1="0"
//               y1="0"
//               x2="1"
//               y2="1"
//             >
//               <stop offset="0%" stopColor="#020617" />
//               <stop offset="42%" stopColor="#172554" />
//               <stop offset="72%" stopColor="#312e81" />
//               <stop offset="100%" stopColor="#0e7490" />
//             </linearGradient>

//             <linearGradient
//               id="farMountains"
//               x1="0"
//               y1="0"
//               x2="0"
//               y2="1"
//             >
//               <stop
//                 offset="0%"
//                 stopColor="#818cf8"
//                 stopOpacity="0.75"
//               />
//               <stop
//                 offset="100%"
//                 stopColor="#312e81"
//                 stopOpacity="0.95"
//               />
//             </linearGradient>

//             <linearGradient
//               id="middleMountains"
//               x1="0"
//               y1="0"
//               x2="0"
//               y2="1"
//             >
//               <stop offset="0%" stopColor="#4338ca" />
//               <stop offset="100%" stopColor="#172554" />
//             </linearGradient>

//             <linearGradient
//               id="frontMountains"
//               x1="0"
//               y1="0"
//               x2="0"
//               y2="1"
//             >
//               <stop offset="0%" stopColor="#172554" />
//               <stop offset="100%" stopColor="#020617" />
//             </linearGradient>

//             <linearGradient
//               id="snowGradient"
//               x1="0"
//               y1="0"
//               x2="0"
//               y2="1"
//             >
//               <stop
//                 offset="0%"
//                 stopColor="#ffffff"
//                 stopOpacity="0.95"
//               />
//               <stop
//                 offset="100%"
//                 stopColor="#bae6fd"
//                 stopOpacity="0.15"
//               />
//             </linearGradient>

//             <radialGradient id="programmeGlow">
//               <stop
//                 offset="0%"
//                 stopColor="#67e8f9"
//                 stopOpacity="0.45"
//               />
//               <stop
//                 offset="100%"
//                 stopColor="#67e8f9"
//                 stopOpacity="0"
//               />
//             </radialGradient>

//             <filter id="softGlow">
//               <feGaussianBlur stdDeviation="10" />
//             </filter>
//           </defs>

//           <rect
//             width="1600"
//             height="600"
//             fill="url(#programmeSky)"
//           />

//           <ellipse
//             cx="800"
//             cy="210"
//             rx="430"
//             ry="210"
//             fill="url(#programmeGlow)"
//             filter="url(#softGlow)"
//           />

//           <g fill="#ffffff">
//             <circle cx="130" cy="90" r="2" opacity="0.8" />
//             <circle cx="250" cy="155" r="1.5" opacity="0.55" />
//             <circle cx="390" cy="80" r="2.2" opacity="0.8" />
//             <circle cx="520" cy="140" r="1.4" opacity="0.65" />
//             <circle cx="690" cy="65" r="2" opacity="0.85" />
//             <circle cx="860" cy="115" r="1.5" opacity="0.7" />
//             <circle cx="1010" cy="75" r="2.1" opacity="0.75" />
//             <circle cx="1180" cy="145" r="1.4" opacity="0.65" />
//             <circle cx="1350" cy="85" r="2.2" opacity="0.85" />
//             <circle cx="1490" cy="165" r="1.6" opacity="0.65" />
//           </g>

//           <path
//             d="M0 390 L130 295 L235 345 L380 215 L505 330 L650 190 L800 315 L945 205 L1080 325 L1225 230 L1375 335 L1500 250 L1600 340 L1600 600 L0 600 Z"
//             fill="url(#farMountains)"
//             opacity="0.8"
//           />

//           <path
//             d="M305 285 L380 215 L438 275 L405 260 L380 235 L355 265 Z
//                M575 255 L650 190 L725 255 L690 235 L650 210 L615 240 Z
//                M885 260 L945 205 L1010 270 L975 250 L945 225 L915 250 Z
//                M1165 280 L1225 230 L1290 295 L1250 270 L1225 248 L1200 272 Z
//                M1440 300 L1500 250 L1560 310 L1525 290 L1500 267 L1475 290 Z"
//             fill="url(#snowGradient)"
//           />

//           <path
//             d="M0 465 L150 365 L260 425 L430 320 L560 440 L720 300 L885 440 L1045 335 L1180 455 L1340 350 L1480 440 L1600 365 L1600 600 L0 600 Z"
//             fill="url(#middleMountains)"
//             opacity="0.95"
//           />

//           <path
//             d="M0 520 L180 410 L330 500 L530 390 L700 520 L880 405 L1040 510 L1240 390 L1420 500 L1600 425 L1600 600 L0 600 Z"
//             fill="url(#frontMountains)"
//           />
//         </svg>

//         <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 via-blue-950/35 to-slate-950/80" />

//         <div className="absolute left-[5%] top-[18%] hidden h-44 w-44 md:block">
//           <div className="absolute inset-0 rounded-full border border-cyan-300/30" />
//           <div className="absolute inset-4 rotate-45 rounded-full border border-purple-300/30" />
//           <div className="absolute inset-8 -rotate-45 rounded-full border border-blue-300/30" />

//           <div className="absolute left-1/2 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-cyan-300 shadow-[0_0_30px_rgba(103,232,249,0.95)]" />

//           <div className="absolute left-1/2 top-0 h-3 w-3 -translate-x-1/2 rounded-full bg-purple-300 shadow-[0_0_20px_rgba(216,180,254,0.9)]" />

//           <div className="absolute bottom-4 right-4 h-3 w-3 rounded-full bg-blue-300 shadow-[0_0_20px_rgba(147,197,253,0.9)]" />
//         </div>

//         <div className="absolute bottom-0 left-0 h-24 w-full opacity-50">
//           <svg
//             className="h-full w-full"
//             viewBox="0 0 1600 150"
//             preserveAspectRatio="none"
//             aria-hidden="true"
//           >
//             <path
//               d="M0,90 C180,20 320,145 500,80 C680,15 830,145 1010,75 C1180,10 1400,140 1600,60"
//               fill="none"
//               stroke="#22d3ee"
//               strokeWidth="2"
//               opacity="0.65"
//             />

//             <path
//               d="M0,115 C210,45 360,150 560,95 C750,35 920,145 1110,90 C1300,35 1450,120 1600,85"
//               fill="none"
//               stroke="#a78bfa"
//               strokeWidth="2"
//               opacity="0.55"
//             />
//           </svg>
//         </div>

//         {/* Right-side atomic structure */}
//         <div
//             className="
//               absolute
//               right-[2%]
//               top-[48%]
//               -translate-y-1/2
//               w-[340px]
//               h-[340px]
//               xl:w-[320px]
//               xl:h-[320px]
//               pointer-events-none
//             "
//           >
//           <div className="absolute inset-0 rounded-full bg-cyan-400/10 blur-3xl" />

//           <svg
//             viewBox="0 0 400 400"
//             className="relative h-full w-full drop-shadow-[0_0_35px_rgba(34,211,238,0.45)]"
//             aria-hidden="true"
//           >
//             <defs>
//               <radialGradient id="atomNucleusGlow">
//                 <stop offset="0%" stopColor="#fef08a" stopOpacity="1" />
//                 <stop offset="45%" stopColor="#22d3ee" stopOpacity="0.9" />
//                 <stop offset="100%" stopColor="#22d3ee" stopOpacity="0" />
//               </radialGradient>

//               <linearGradient
//                 id="atomOrbitGradient"
//                 x1="0"
//                 y1="0"
//                 x2="1"
//                 y2="1"
//               >
//                 <stop offset="0%" stopColor="#67e8f9" />
//                 <stop offset="50%" stopColor="#a78bfa" />
//                 <stop offset="100%" stopColor="#60a5fa" />
//               </linearGradient>

//               <filter id="atomBlur">
//                 <feGaussianBlur stdDeviation="8" />
//               </filter>
//             </defs>

//             {/* Outer glow */}
//             <circle
//               cx="200"
//               cy="200"
//               r="145"
//               fill="url(#atomNucleusGlow)"
//               opacity="0.18"
//               filter="url(#atomBlur)"
//             />

//             {/* Electron orbits */}
//             <g
//               fill="none"
//               stroke="url(#atomOrbitGradient)"
//               strokeWidth="3"
//               opacity="0.9"
//             >
//               <ellipse
//                 cx="200"
//                 cy="200"
//                 rx="155"
//                 ry="62"
//                 transform="rotate(0 200 200)"
//               />

//               <ellipse
//                 cx="200"
//                 cy="200"
//                 rx="155"
//                 ry="62"
//                 transform="rotate(60 200 200)"
//               />

//               <ellipse
//                 cx="200"
//                 cy="200"
//                 rx="155"
//                 ry="62"
//                 transform="rotate(-60 200 200)"
//               />
//             </g>

//             {/* Nucleus glow */}
//             <circle
//               cx="200"
//               cy="200"
//               r="54"
//               fill="#22d3ee"
//               opacity="0.25"
//               filter="url(#atomBlur)"
//             />

//             {/* Nucleus particles */}
//             <g>
//               <circle cx="181" cy="187" r="19" fill="#facc15" />
//               <circle cx="216" cy="184" r="18" fill="#c4b5fd" />
//               <circle cx="198" cy="214" r="20" fill="#60a5fa" />
//               <circle cx="226" cy="215" r="15" fill="#f472b6" />
//               <circle cx="172" cy="216" r="15" fill="#22d3ee" />
//             </g>

//             {/* Nucleus highlights */}
//             <g fill="#ffffff" opacity="0.55">
//               <circle cx="175" cy="181" r="5" />
//               <circle cx="210" cy="178" r="5" />
//               <circle cx="191" cy="207" r="5" />
//               <circle cx="221" cy="210" r="4" />
//             </g>

//             {/* Electrons */}
//             <g>
//               <circle
//                 cx="355"
//                 cy="200"
//                 r="11"
//                 fill="#67e8f9"
//                 className="animate-pulse"
//               />

//               <circle
//                 cx="123"
//                 cy="66"
//                 r="11"
//                 fill="#c4b5fd"
//                 className="animate-pulse"
//               />

//               <circle
//                 cx="120"
//                 cy="333"
//                 r="11"
//                 fill="#facc15"
//                 className="animate-pulse"
//               />

//               <circle
//                 cx="278"
//                 cy="68"
//                 r="9"
//                 fill="#60a5fa"
//                 className="animate-pulse"
//               />

//               <circle
//                 cx="278"
//                 cy="332"
//                 r="9"
//                 fill="#f472b6"
//                 className="animate-pulse"
//               />
//             </g>

//             {/* Electron glows */}
//             <g fill="#ffffff" opacity="0.75">
//               <circle cx="352" cy="197" r="3" />
//               <circle cx="120" cy="63" r="3" />
//               <circle cx="117" cy="330" r="3" />
//               <circle cx="275" cy="65" r="2.5" />
//               <circle cx="275" cy="329" r="2.5" />
//             </g>

//             {/* Small surrounding particles */}
//             <g>
//               <circle cx="45" cy="145" r="4" fill="#67e8f9" opacity="0.7" />
//               <circle cx="340" cy="85" r="5" fill="#facc15" opacity="0.7" />
//               <circle cx="365" cy="300" r="4" fill="#c4b5fd" opacity="0.8" />
//               <circle cx="60" cy="275" r="5" fill="#60a5fa" opacity="0.7" />
//             </g>
//           </svg>
//         </div>

//         <div className="relative z-10 mx-auto max-w-5xl px-6 py-16 text-center text-white">
//           <div className="mx-auto mb-6 flex h-28 w-28 items-center justify-center rounded-full border border-white/25 bg-white/10 p-2 shadow-2xl backdrop-blur-md sm:h-32 sm:w-32">
//             <img
//               src={conferenceLogo}
//               alt="Quantum Peeks 2026"
//               className="h-full w-full object-contain"
//             />
//           </div>

//           <p className="mb-3 text-sm font-bold uppercase tracking-[0.3em] text-cyan-200 sm:tracking-[0.4em]">
//             14–18 November 2026
//           </p>

//           <h1 className="text-4xl font-extrabold tracking-tight drop-shadow-lg sm:text-5xl md:text-6xl">
//             Scientific Programme
//           </h1>

//           <p className="mx-auto mt-5 max-w-3xl text-lg leading-relaxed text-blue-100 md:text-xl">
//             Exploring coherence, quantum matter, light, and
//             nonequilibrium phenomena amidst the Himalayas at IIT
//             Mandi.
//           </p>

//           <div className="mt-8 flex flex-wrap justify-center gap-3">
//             {[
//               {
//                 label: "Daily Programme",
//                 target: "daily-programme",
//               },
//               {
//                 label: "Plenary Lectures",
//                 target: "plenary-lectures",
//               },
//               {
//                 label: "Invited Talks",
//                 target: "invited-talks",
//               },
//               {
//                 label: "Contributed Talks",
//                 target: "contributed-talks",
//               },
//               {
//                 label: "Poster Sessions",
//                 target: "poster-sessions",
//               },
//             ].map((item) => (
//               <button
//                 key={item.target}
//                 type="button"
//                 onClick={() =>
//                   scrollToProgrammeSection(item.target)
//                 }
//                 className="group inline-flex items-center gap-2 rounded-full border border-cyan-200/30 bg-white/10 px-4 py-2 text-sm font-semibold text-cyan-50 shadow-lg backdrop-blur-md transition-all duration-300 hover:-translate-y-1 hover:border-cyan-200/70 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-cyan-200"
//               >
//                 {item.label}

//                 <span className="transition-transform duration-300 group-hover:translate-y-0.5">
//                   ↓
//                 </span>
//               </button>
//             ))}
//           </div>
//         </div>
//       </section>

//       <main className="mx-auto max-w-7xl px-5 py-14 sm:px-6 lg:px-8">
//         <div className="mb-10 flex flex-wrap items-center justify-between gap-4">
//           <Link
//             to="/"
//             className="inline-flex items-center gap-2 font-semibold text-blue-700 transition hover:text-blue-900 hover:underline"
//           >
//             <span>←</span>
//             <span>Home</span>
//           </Link>

//           <div className="rounded-full border border-blue-200 bg-blue-50 px-5 py-2 text-sm font-semibold text-blue-800">
//             Detailed schedule subject to minor changes
//           </div>
//         </div>

//         {/* Navigation cards */}
//         <section
//           id="programme-overview"
//           className="mb-20 scroll-mt-32"
//         >
//           <div className="rounded-[2rem] border border-white/80 bg-white/90 p-5 shadow-[0_25px_80px_rgba(15,23,42,0.12)] backdrop-blur-xl sm:p-8">
//             <div className="mb-8 flex flex-col justify-between gap-4 md:flex-row md:items-end">
//               <div>
//                 <p className="text-sm font-bold uppercase tracking-[0.24em] text-blue-700">
//                   Explore the programme
//                 </p>

//                 <h2 className="mt-2 text-2xl font-extrabold text-blue-950 sm:text-3xl">
//                   Navigate directly to each section
//                 </h2>

//                 <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-600 sm:text-base">
//                   Select a category to view the detailed schedule,
//                   speakers, presentation information, and session
//                   timings.
//                 </p>
//               </div>

//               <div className="inline-flex w-fit items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-xs font-bold text-blue-700">
//                 <span className="h-2 w-2 animate-pulse rounded-full bg-blue-600" />
//                 Click any card to explore
//               </div>
//             </div>

//             <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
//               {navigationCards.map((card) => (
//                 <button
//                   key={card.target}
//                   type="button"
//                   onClick={() =>
//                     scrollToProgrammeSection(card.target)
//                   }
//                   className={`group relative overflow-hidden rounded-2xl border bg-gradient-to-br p-6 text-left shadow-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl focus:outline-none focus:ring-4 ${card.card}`}
//                 >
//                   <div
//                     className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${card.decoration} transition-transform duration-500 group-hover:scale-150`}
//                   />

//                   <div className="relative">
//                     <div className="mb-5 flex items-start justify-between">
//                       <div
//                         className={`flex h-14 w-14 items-center justify-center rounded-2xl text-2xl text-white shadow-lg ${card.circle}`}
//                       >
//                         {card.icon}
//                       </div>

//                       <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white bg-white text-gray-700 shadow-sm transition group-hover:translate-x-1">
//                         →
//                       </span>
//                     </div>

//                     <p className="text-3xl font-extrabold text-slate-900">
//                       {card.count}
//                     </p>

//                     <h3 className="mt-1 text-lg font-bold text-gray-900">
//                       {card.title}
//                     </h3>

//                     <p className="mt-3 text-sm leading-relaxed text-gray-600">
//                       {card.text}
//                     </p>

//                     <p className="mt-5 text-xs font-bold uppercase tracking-[0.16em] text-slate-700">
//                       {card.action}
//                     </p>
//                   </div>
//                 </button>
//               ))}
//             </div>
//           </div>
//         </section>

//         {/* Daily schedule */}
//         <section
//           id="daily-programme"
//           className="mb-20 scroll-mt-32"
//         >
//           <div className="mb-10 text-center">
//             <p className="text-sm font-bold uppercase tracking-[0.25em] text-blue-700">
//               Conference Timeline
//             </p>

//             <h2 className="mt-3 text-3xl font-extrabold text-blue-950 md:text-4xl">
//               Day-by-Day Programme
//             </h2>

//             <p className="mx-auto mt-4 max-w-3xl text-gray-600">
//               A provisional overview of the scientific and social
//               programme.
//             </p>
//           </div>

//           <div className="space-y-10">
//             {programmeDays.map((programmeDay) => (
//               <article
//                 key={programmeDay.day}
//                 className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_18px_60px_rgba(15,23,42,0.08)]"
//               >
//                 <div className="relative overflow-hidden bg-gradient-to-r from-blue-950 via-blue-900 to-indigo-800 px-6 py-6 text-white md:px-8">
//                   <div className="absolute -right-12 -top-16 h-44 w-44 rounded-full bg-white/10" />

//                   <div className="relative flex flex-col justify-between gap-4 md:flex-row md:items-center">
//                     <div>
//                       <span className="inline-flex rounded-full border border-white/20 bg-white/15 px-4 py-1 text-sm font-bold text-cyan-100 backdrop-blur">
//                         {programmeDay.day}
//                       </span>

//                       <h3 className="mt-3 text-2xl font-bold md:text-3xl">
//                         {programmeDay.date}
//                       </h3>

//                       <p className="mt-1 text-blue-100">
//                         {programmeDay.title}
//                       </p>
//                     </div>

//                     <img
//                       src={conferenceLogo}
//                       alt="Quantum Peeks 2026"
//                       className="hidden h-16 w-auto object-contain drop-shadow-lg md:block"
//                     />
//                   </div>
//                 </div>

//                 <div className="divide-y divide-slate-100">
//                   {programmeDay.events.map((event, index) => (
//                     <div
//                       key={`${programmeDay.day}-${index}`}
//                       className="grid gap-4 px-5 py-5 transition duration-300 hover:bg-slate-50 md:grid-cols-[150px_120px_1fr_180px] md:items-center md:px-8"
//                     >
//                       <div className="font-bold text-blue-950">
//                         {event.time}
//                       </div>

//                       <div>
//                         <span
//                           className={`inline-flex rounded-full border px-3 py-1 text-xs font-bold ${getEventStyle(
//                             event.type
//                           )}`}
//                         >
//                           {event.type}
//                         </span>
//                       </div>

//                       <div>
//                         <h4 className="font-bold text-gray-900">
//                           {event.title}
//                         </h4>

//                         {event.speaker && (
//                           <p className="mt-1 text-sm text-gray-600">
//                             {event.speaker}
//                           </p>
//                         )}
//                       </div>

//                       <div className="text-sm font-medium text-gray-500 md:text-right">
//                         {event.venue}
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               </article>
//             ))}
//           </div>

//           <BackToNavigation color="blue" />
//         </section>

//         {/* Plenary lectures */}
//         <section
//           id="plenary-lectures"
//           className="mb-20 scroll-mt-32"
//         >
//           <div className="mb-10 text-center">
//             <p className="text-sm font-bold uppercase tracking-[0.25em] text-purple-700">
//               Distinguished Speakers
//             </p>

//             <h2 className="mt-3 text-3xl font-extrabold text-blue-950 md:text-4xl">
//               Plenary Lectures
//             </h2>

//             <p className="mx-auto mt-4 max-w-3xl text-gray-600">
//               Plenary lectures featuring distinguished researchers
//               and broad perspectives across quantum science.
//             </p>
//           </div>

//           <div className="space-y-8">
//             {plenaryLectures.map((lecture, index) => (
//               <article
//                 key={index}
//                 className="group relative overflow-hidden rounded-3xl border border-purple-200 bg-white shadow-[0_20px_70px_rgba(76,29,149,0.10)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_28px_90px_rgba(76,29,149,0.18)] md:h-[520px]"
//               >
//                 <div className="absolute inset-0 bg-gradient-to-r from-purple-50 via-white to-blue-50 opacity-95" />

//                 <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-purple-200/30 blur-3xl" />

//                 <div className="absolute -bottom-20 left-1/3 h-48 w-48 rounded-full bg-blue-200/30 blur-3xl" />

//                 <div className="relative grid h-full grid-cols-1 md:grid-cols-[340px_1fr]">
//                   <div className="relative min-h-[340px] overflow-hidden bg-gradient-to-br from-purple-900 to-blue-900 md:min-h-full">
//                     <img
//                       src={lecture.image}
//                       alt={lecture.speaker}
//                       className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
//                     />

//                     <div className="absolute inset-0 bg-gradient-to-t from-blue-950/85 via-blue-950/10 to-transparent" />

//                     <div className="absolute bottom-5 left-5">
//                       <span className="inline-flex rounded-full border border-white/30 bg-white/15 px-4 py-2 text-sm font-bold text-white shadow-lg backdrop-blur-md">
//                         Plenary{" "}
//                         {String(index + 1).padStart(2, "0")}
//                       </span>
//                     </div>
//                   </div>

//                   <div className="relative flex h-full flex-col p-6 sm:p-8 md:p-10">
//                     <img
//                       src={conf_logo}
//                       alt="Quantum Peeks 2026"
//                       className="absolute right-6 top-6 hidden h-24 w-auto object-contain drop-shadow-md md:block"
//                     />

//                     <div>
//                       <div className="mb-5 flex flex-wrap gap-3 md:pr-32">
//                         <span className="rounded-full bg-purple-100 px-4 py-2 text-sm font-bold text-purple-800">
//                           {lecture.date}
//                         </span>

//                         <span className="rounded-full bg-blue-100 px-4 py-2 text-sm font-bold text-blue-800">
//                           {lecture.time}
//                         </span>
//                       </div>

//                       <div className="mb-6 flex justify-center md:hidden">
//                         <img
//                           src={conf_logo}
//                           alt="Quantum Peeks 2026"
//                           className="h-20 w-auto object-contain drop-shadow-md"
//                         />
//                       </div>

//                       <h3 className="text-2xl font-extrabold leading-tight text-blue-950 md:pr-28 md:text-3xl">
//                         {lecture.title}
//                       </h3>

//                       <div className="mt-5 border-l-4 border-purple-500 pl-4">
//                         <p className="text-xl font-bold text-purple-900">
//                           {lecture.speaker}
//                         </p>

//                         <p className="mt-1 text-sm font-medium text-gray-600">
//                           {lecture.affiliation}
//                         </p>
//                       </div>

//                       <div className="mt-6">
//                         <h4 className="mb-2 text-sm font-bold uppercase tracking-[0.16em] text-blue-800">
//                           Abstract
//                         </h4>

//                         <p className="line-clamp-4 text-base leading-7 text-gray-700">
//                           {lecture.abstract}
//                         </p>

//                         {lecture.abstract &&
//                           lecture.abstract.length > 250 && (
//                             <button
//                               type="button"
//                               onClick={() =>
//                                 setExpandedPlenary(index)
//                               }
//                               className="mt-3 inline-flex items-center gap-2 rounded-full bg-purple-100 px-4 py-2 text-sm font-bold text-purple-700 transition hover:bg-purple-700 hover:text-white"
//                             >
//                               Read More
//                               <span>→</span>
//                             </button>
//                           )}
//                       </div>
//                     </div>

//                     <div className="mt-auto flex flex-wrap items-center justify-between gap-4 border-t border-purple-100 pt-5">
//                       <span className="text-sm font-semibold text-gray-500">
//                         Quantum Peeks 2026 · IIT Mandi
//                       </span>

//                       <span className="inline-flex items-center rounded-full bg-gradient-to-r from-purple-700 to-blue-700 px-5 py-2 text-sm font-bold text-white shadow-md">
//                         Plenary Lecture
//                       </span>
//                     </div>
//                   </div>
//                 </div>
//               </article>
//             ))}
//           </div>

//           <BackToNavigation color="purple" />
//         </section>

//         {/* Invited talks */}
//         <section
//           id="invited-talks"
//           className="mb-20 scroll-mt-32"
//         >
//           <div className="overflow-hidden rounded-3xl border border-blue-200 bg-white shadow-[0_20px_70px_rgba(30,64,175,0.10)]">
//             <ProgrammeTableHeader
//               title="Invited Talks"
//               subtitle="Focused scientific presentations from invited experts"
//             />

//             <div className="overflow-x-auto">
//               <table className="w-full min-w-[850px] border-collapse">
//                 <thead>
//                   <tr className="bg-blue-50 text-left text-sm uppercase tracking-wide text-blue-900">
//                     <th className="px-6 py-4">Session</th>
//                     <th className="px-6 py-4">Date</th>
//                     <th className="px-6 py-4">Time</th>
//                     <th className="px-6 py-4">Speaker</th>
//                     <th className="px-6 py-4">Talk Title</th>
//                   </tr>
//                 </thead>

//                 <tbody className="divide-y divide-blue-100">
//                   {invitedTalks.map((talk, index) => (
//                     <tr
//                       key={index}
//                       className="transition hover:bg-blue-50/60"
//                     >
//                       <td className="px-6 py-5">
//                         <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-bold text-blue-800">
//                           {talk.session}
//                         </span>
//                       </td>

//                       <td className="whitespace-nowrap px-6 py-5 font-semibold text-gray-800">
//                         {talk.date}
//                       </td>

//                       <td className="whitespace-nowrap px-6 py-5 text-gray-600">
//                         {talk.time}
//                       </td>

//                       <td className="px-6 py-5 font-bold text-blue-900">
//                         {talk.speaker}
//                       </td>

//                       <td className="px-6 py-5 text-gray-700">
//                         {talk.title}
//                       </td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           </div>

//           <BackToNavigation color="blue" />
//         </section>

//         {/* Contributed talks */}
//         <section
//           id="contributed-talks"
//           className="mb-20 scroll-mt-32"
//         >
//           <div className="overflow-hidden rounded-3xl border border-cyan-200 bg-white shadow-[0_20px_70px_rgba(8,145,178,0.10)]">
//             <ProgrammeTableHeader
//               title="Contributed Talks"
//               subtitle="Selected oral presentations from registered participants"
//             />

//             <div className="overflow-x-auto">
//               <table className="w-full min-w-[850px] border-collapse">
//                 <thead>
//                   <tr className="bg-cyan-50 text-left text-sm uppercase tracking-wide text-cyan-900">
//                     <th className="px-6 py-4">Code</th>
//                     <th className="px-6 py-4">Date</th>
//                     <th className="px-6 py-4">Time</th>
//                     <th className="px-6 py-4">Presenter</th>
//                     <th className="px-6 py-4">
//                       Presentation Title
//                     </th>
//                   </tr>
//                 </thead>

//                 <tbody className="divide-y divide-cyan-100">
//                   {contributedTalks.map((talk, index) => (
//                     <tr
//                       key={index}
//                       className="transition hover:bg-cyan-50/60"
//                     >
//                       <td className="px-6 py-5">
//                         <span className="rounded-lg bg-cyan-100 px-3 py-2 text-xs font-bold text-cyan-800">
//                           {talk.code}
//                         </span>
//                       </td>

//                       <td className="whitespace-nowrap px-6 py-5 font-semibold text-gray-800">
//                         {talk.date}
//                       </td>

//                       <td className="whitespace-nowrap px-6 py-5 text-gray-600">
//                         {talk.time}
//                       </td>

//                       <td className="px-6 py-5 font-bold text-cyan-900">
//                         {talk.presenter}
//                       </td>

//                       <td className="px-6 py-5 text-gray-700">
//                         {talk.title}
//                       </td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           </div>

//           <BackToNavigation color="cyan" />
//         </section>

//         {/* Poster sessions */}
//         <section
//           id="poster-sessions"
//           className="mb-20 scroll-mt-32"
//         >
//           <div className="overflow-hidden rounded-3xl border border-indigo-200 bg-white shadow-[0_20px_70px_rgba(67,56,202,0.10)]">
//             <ProgrammeTableHeader
//               title="Poster Sessions"
//               subtitle="Interactive sessions for discussion, networking, and scientific exchange"
//             />

//             <div className="grid gap-6 p-6 md:grid-cols-2 md:p-8">
//               {posterSessions.map((poster, index) => (
//                 <article
//                   key={index}
//                   className="group relative overflow-hidden rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-6 transition duration-300 hover:-translate-y-1 hover:shadow-xl"
//                 >
//                   <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-indigo-200/40 transition-transform duration-500 group-hover:scale-150" />

//                   <div className="relative">
//                     <div className="mb-5 flex items-center justify-between">
//                       <span className="rounded-full bg-indigo-700 px-4 py-1.5 text-sm font-bold text-white">
//                         {poster.session}
//                       </span>

//                       <span className="text-3xl">📌</span>
//                     </div>

//                     <div className="space-y-3 text-sm">
//                       {[
//                         ["Date", poster.date],
//                         ["Time", poster.time],
//                         ["Venue", poster.venue],
//                         ["Poster Numbers", poster.posterNumbers],
//                       ].map(([label, value]) => (
//                         <div
//                           key={label}
//                           className="flex justify-between gap-4 border-b border-indigo-100 pb-3 last:border-b-0"
//                         >
//                           <span className="font-semibold text-gray-500">
//                             {label}
//                           </span>

//                           <span className="text-right font-bold text-gray-900">
//                             {value}
//                           </span>
//                         </div>
//                       ))}
//                     </div>
//                   </div>
//                 </article>
//               ))}
//             </div>
//           </div>

//           <BackToNavigation color="indigo" />
//         </section>
//       </main>

//       {/* Plenary abstract popup */}
//       {expandedPlenary !== null && (
//         <div
//           className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/80 px-4 py-8 backdrop-blur-md"
//           onClick={() => setExpandedPlenary(null)}
//         >
//           <div
//             className="relative flex h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-3xl border border-purple-200 bg-white shadow-[0_30px_120px_rgba(0,0,0,0.45)]"
//             onClick={(event) => event.stopPropagation()}
//           >
//             {/* Fixed popup header */}
//             <div className="relative shrink-0 bg-gradient-to-r from-purple-900 via-indigo-900 to-blue-900 px-6 py-6 text-white sm:px-8">
//               <button
//                 type="button"
//                 onClick={() => setExpandedPlenary(null)}
//                 className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-white/30 bg-white/10 text-2xl text-white transition hover:bg-white hover:text-purple-900"
//                 aria-label="Close abstract"
//               >
//                 ×
//               </button>

//               <div className="flex items-center gap-5 pr-12">
//                 <img
//                   src={conferenceLogo}
//                   alt="Quantum Peeks 2026"
//                   className="hidden h-20 w-auto shrink-0 object-contain drop-shadow-lg sm:block"
//                 />

//                 <div>
//                   <p className="text-sm font-bold uppercase tracking-[0.2em] text-purple-200">
//                     Plenary Lecture
//                   </p>

//                   <h3 className="mt-2 text-2xl font-extrabold leading-tight sm:text-3xl">
//                     {plenaryLectures[expandedPlenary].title}
//                   </h3>
//                 </div>
//               </div>
//             </div>

//             {/* Popup body */}
//             <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[270px_1fr]">
//               {/* Fixed speaker panel */}
//               <div className="hidden overflow-hidden bg-gradient-to-b from-purple-50 to-blue-50 p-6 md:block">
//                 <div className="mx-auto h-64 w-full max-w-[230px] overflow-hidden rounded-2xl border-4 border-white shadow-xl">
//                   <img
//                     src={
//                       plenaryLectures[expandedPlenary].image
//                     }
//                     alt={
//                       plenaryLectures[expandedPlenary].speaker
//                     }
//                     className="h-full w-full object-cover object-top"
//                   />
//                 </div>

//                 <div className="mt-5 text-center">
//                   <p className="text-xl font-bold text-purple-900">
//                     {
//                       plenaryLectures[expandedPlenary]
//                         .speaker
//                     }
//                   </p>

//                   <p className="mt-2 text-sm leading-relaxed text-gray-600">
//                     {
//                       plenaryLectures[expandedPlenary]
//                         .affiliation
//                     }
//                   </p>
//                 </div>

//                 <div className="mt-6 space-y-3">
//                   <div className="rounded-xl border border-purple-100 bg-white p-4 text-center shadow-sm">
//                     <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
//                       Date
//                     </p>

//                     <p className="mt-1 text-sm font-bold text-purple-800">
//                       {
//                         plenaryLectures[expandedPlenary]
//                           .date
//                       }
//                     </p>
//                   </div>

//                   <div className="rounded-xl border border-blue-100 bg-white p-4 text-center shadow-sm">
//                     <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
//                       Time
//                     </p>

//                     <p className="mt-1 text-sm font-bold text-blue-800">
//                       {
//                         plenaryLectures[expandedPlenary]
//                           .time
//                       }
//                     </p>
//                   </div>
//                 </div>
//               </div>

//               {/* Abstract side */}
//               <div className="flex min-h-0 flex-col bg-white">
//                 {/* Mobile speaker information */}
//                 <div className="shrink-0 border-b border-purple-100 bg-gradient-to-r from-purple-50 to-blue-50 p-5 md:hidden">
//                   <div className="flex items-center gap-4">
//                     <img
//                       src={
//                         plenaryLectures[expandedPlenary]
//                           .image
//                       }
//                       alt={
//                         plenaryLectures[expandedPlenary]
//                           .speaker
//                       }
//                       className="h-20 w-20 shrink-0 rounded-xl object-cover object-top shadow-md"
//                     />

//                     <div>
//                       <p className="font-bold text-purple-900">
//                         {
//                           plenaryLectures[expandedPlenary]
//                             .speaker
//                         }
//                       </p>

//                       <p className="mt-1 text-sm text-gray-600">
//                         {
//                           plenaryLectures[expandedPlenary]
//                             .affiliation
//                         }
//                       </p>

//                       <p className="mt-2 text-xs font-semibold text-blue-800">
//                         {
//                           plenaryLectures[expandedPlenary]
//                             .date
//                         }
//                         {" · "}
//                         {
//                           plenaryLectures[expandedPlenary]
//                             .time
//                         }
//                       </p>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Only this area scrolls */}
//                 <div className="min-h-0 flex-1 overflow-y-auto p-6 sm:p-8 md:p-10">
//                   <h4 className="text-sm font-bold uppercase tracking-[0.2em] text-purple-700">
//                     Abstract
//                   </h4>

//                   <div className="mt-4 h-1 w-20 rounded-full bg-gradient-to-r from-purple-600 to-blue-600" />

//                   <p className="mt-6 whitespace-pre-line text-base leading-8 text-gray-700">
//                     {
//                       plenaryLectures[expandedPlenary]
//                         .abstract
//                     }
//                   </p>
//                 </div>

//                 {/* Fixed footer */}
//                 <div className="shrink-0 border-t border-gray-200 bg-white px-6 py-5 sm:px-8">
//                   <div className="flex flex-wrap items-center justify-between gap-4">
//                     <span className="text-sm font-semibold text-gray-500">
//                       Quantum Peeks 2026 · IIT Mandi
//                     </span>

//                     <button
//                       type="button"
//                       onClick={() =>
//                         setExpandedPlenary(null)
//                       }
//                       className="rounded-full bg-gradient-to-r from-purple-700 to-blue-700 px-7 py-3 font-bold text-white shadow-lg transition hover:scale-105 hover:shadow-xl"
//                     >
//                       Close Abstract
//                     </button>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

function AbstractUploader({ onFileReady }) {
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [error, setError] = useState("");

  // const allowedExtensions = [".pdf", ".doc", ".docx", ".tex"];
  const allowedExtensions = [".doc", ".docx"];


  const formatSize = (bytes) => {
    if (!bytes) return "0 KB";

    const kb = bytes / 1024;

    if (kb < 1024) {
      return `${kb.toFixed(1)} KB`;
    }

    return `${(kb / 1024).toFixed(2)} MB`;
  };

  const getFileExtension = (filename) => {
    const lastDotIndex = filename.lastIndexOf(".");

    if (lastDotIndex === -1) {
      return "";
    }

    return filename.substring(lastDotIndex).toLowerCase();
  };

  const handleFile = (selectedFile) => {
    if (!selectedFile) return;

    setError("");

    const extension = getFileExtension(selectedFile.name);

    if (!allowedExtensions.includes(extension)) {
      setFile(null);
      setProgress(0);
      setUploading(false);
      setUploaded(false);

      if (onFileReady) {
        onFileReady(null);
      }

      setError(
        // "Invalid file type. Please upload a PDF, DOC, DOCX, or TEX file."
        "Invalid file type. Please upload a DOC or DOCX file."
      );

      return;
    }

    // Optional maximum file size: 10 MB
    const maximumFileSize = 10 * 1024 * 1024;

    if (selectedFile.size > maximumFileSize) {
      setFile(null);
      setProgress(0);
      setUploading(false);
      setUploaded(false);

      if (onFileReady) {
        onFileReady(null);
      }

      setError("The abstract file must be smaller than 10 MB.");

      return;
    }

    setFile(selectedFile);
    setProgress(0);
    setUploading(false);
    setUploaded(false);

    // The file becomes ready only after the user clicks Upload.
    if (onFileReady) {
      onFileReady(null);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();

    const selectedFile = event.dataTransfer.files?.[0];

    handleFile(selectedFile);
  };

  const startUpload = () => {
    if (!file || uploading || uploaded) return;

    setUploading(true);
    setProgress(0);
    setError("");

    const interval = setInterval(() => {
      setProgress((previousProgress) => {
        const nextProgress = previousProgress + 10;

        if (nextProgress >= 100) {
          clearInterval(interval);

          setUploading(false);
          setUploaded(true);

          if (onFileReady) {
            onFileReady(file);
          }

          return 100;
        }

        return nextProgress;
      });
    }, 200);
  };

  const removeFile = () => {
    setFile(null);
    setProgress(0);
    setUploading(false);
    setUploaded(false);
    setError("");

    if (onFileReady) {
      onFileReady(null);
    }
  };

  const uploadedSize = file
    ? (file.size * progress) / 100
    : 0;

  return (
    <div className="space-y-4">
      {!file && (
        <div
          onDrop={handleDrop}
          onDragOver={(event) => event.preventDefault()}
          className="flex flex-col items-center justify-center w-full h-44 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition"
        >
          <label className="flex flex-col items-center justify-center w-full h-full cursor-pointer px-4 text-center">
            <UploadCloud
              size={42}
              className="text-blue-600 mb-3"
            />

            <p className="text-sm font-medium text-gray-700">
              Click or drag your abstract file here
            </p>

            <p className="text-xs text-gray-500 mt-1">
              {/* Accepted formats: PDF, DOC, DOCX, and TEX */}
              Accepted formats: DOC and DOCX
            </p>

            <p className="text-xs text-gray-400 mt-1">
              Maximum file size: 10 MB
            </p>

            <input
              type="file"
              // accept=".pdf,.doc,.docx,.tex,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/x-tex,application/x-tex"
              accept=".doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/x-tex,application/x-tex"
              className="hidden"
              onChange={(event) =>
                handleFile(event.target.files?.[0])
              }
            />
          </label>
        </div>
      )}

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm font-medium text-red-700">
          {error}
        </div>
      )}

      {file && (
        <div className="bg-gray-50 border rounded-lg p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <UploadCloud
                size={22}
                className="text-blue-600 flex-shrink-0"
              />

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-700 truncate">
                  {file.name}
                </p>

                {uploading && (
                  <div className="mt-2">
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 transition-all duration-200"
                        style={{
                          width: `${progress}%`,
                        }}
                      />
                    </div>

                    <p className="text-xs text-gray-500 mt-1">
                      {formatSize(uploadedSize)} /{" "}
                      {formatSize(file.size)}
                    </p>
                  </div>
                )}

                {!uploading && !uploaded && (
                  <p className="text-xs text-gray-500 mt-1">
                    {formatSize(file.size)} selected
                  </p>
                )}

                {uploaded && (
                  <p className="text-xs text-green-600 font-semibold mt-1">
                    ✓ Uploaded successfully ·{" "}
                    {formatSize(file.size)}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {!uploaded && (
                <button
                  type="button"
                  onClick={startUpload}
                  disabled={uploading}
                  className="px-4 py-2 text-xs font-semibold text-white rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition"
                >
                  {uploading
                    ? `Uploading ${progress}%`
                    : "Upload"}
                </button>
              )}

              <button
                type="button"
                onClick={removeFile}
                className="flex items-center justify-center gap-1 px-3 py-2 text-xs rounded-md border border-red-200 hover:bg-red-50 text-red-600 transition"
                aria-label="Remove abstract file"
              >
                <Trash2 size={15} />
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AbstractFormPage() {
  const abstractSubmissionOpen = true; // Change to true/false when abstract submission is open/closed
  const [abstractFile, setAbstractFile] = useState(null);
  const [abstractResetKey, setAbstractResetKey] = useState(0);
  const [submitStatus, setSubmitStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const ABSTRACT_SCRIPT_URL =
  "https://script.google.com/macros/s/AKfycbzXSggsK0qlipgDSQSJtrmazMqpIxX_LOsXgFPWJrILh_VfeEBd-zNlPYnxdz6Z1tH9/exec"; // Replace with actual Google Apps Script URL

  const initialFormData = {
    title: "Mr.",
    name: "",
    institution: "",
    email: "",
    category: "PhD",
    preference: "Oral Presentation",
    abstractTitle: "",
    keywords: "",
  };

  const [formData, setFormData] = useState(initialFormData);

  const handleChange = (event) => {
    const { name, value } = event.target;
  
    setFormData((previousData) => ({
      ...previousData,
      [name]: value,
    }));
  
    if (
      name === "preference" &&
      value === "Participation Only"
    ) {
      setAbstractFile(null);
      setAbstractResetKey((previousKey) => previousKey + 1);
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const result = reader.result;

        if (typeof result !== "string") {
          reject(new Error("Unable to read the abstract file."));
          return;
        }

        resolve(result.split(",")[1]);
      };

      reader.onerror = () => {
        reject(new Error("Unable to read the abstract file."));
      };

      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setSubmitStatus("submitting");
    setErrorMessage("");

    try {
      if (
        formData.preference !== "Participation Only" &&
        !abstractFile
      ) {
        throw new Error(
          "Please select your abstract file and click Upload before submitting."
        );
      }

      const abstractBase64 =
      formData.preference === "Participation Only"
        ? ""
        : await fileToBase64(abstractFile);

        const payload = {
          title: formData.title,
          name: formData.name,
          institution: formData.institution,
          email: formData.email,
          category: formData.category,
          preference: formData.preference,
          abstractTitle: formData.abstractTitle,
          keywords: formData.keywords,
        
          abstractBase64,
        
          abstractName: abstractFile?.name || "",
          abstractType: abstractFile?.type || "",
        };
      const response = await fetch(ABSTRACT_SCRIPT_URL, {
        method: "POST",
        body: JSON.stringify(payload),
      });

      const responseText = await response.text();

      let result;

      try {
        result = JSON.parse(responseText);
      } catch {
        throw new Error(
          "The server returned an invalid response."
        );
      }

      if (!result.success) {
        throw new Error(
          result.message || "Abstract submission failed."
        );
      }

      setSubmitStatus("success");
      setFormData(initialFormData);
      setAbstractFile(null);
      setAbstractResetKey((previousKey) => previousKey + 1);

      setTimeout(() => {
        setSubmitStatus("idle");
      }, 15000);
    } catch (error) {
      console.error("Abstract submission error:", error);

      setErrorMessage(
        error.message ||
          "Unable to submit the abstract. Please try again."
      );

      setSubmitStatus("error");

      setTimeout(() => {
        setSubmitStatus("idle");
        setErrorMessage("");
      }, 15000);
    }
  };

  if (!abstractSubmissionOpen) {
    return (
      <div className="pt-28 pb-24 max-w-[1100px] mx-auto px-6">
        <Link
          to="/"
          className="
            group mb-12 my-10 inline-flex items-center gap-3
            text-sm font-semibold text-slate-600
            transition-colors duration-300
            hover:text-blue-700
          "
        >
          <span className="transition-transform duration-300 group-hover:-translate-x-1">
            ←
          </span>
          Back to Home
        </Link>
  
        <h1 className="text-4xl font-bold mb-6">
          {/* Abstract Submission Closed */}
          Abstract Submission Opening Soon
        </h1>
  
        <div className="bg-red-50 border border-red-200 rounded-xl p-8">
          <p className="text-lg text-gray-700">
            {/* The abstract submission portal for <strong>Quantum Peeks 2026</strong> is now closed. Thank you for your interest in participating in the conference. */}
            The abstract submission portal for <strong>Quantum Peeks 2026</strong> will open shortly. Please stay tuned for the official announcement regarding the submission schedule and guidelines.
            Thank you for your interest in participating in the conference.
          </p>
          <p className="mt-6 text-gray-700">
            For any further queries regarding abstract submission, please contact us at{" "}
            <a
              href="mailto:quantumpeeks2026@iitmandi.ac.in"
              className="text-blue-600 font-semibold hover:underline"
            >
              quantumpeeks2026@iitmandi.ac.in
            </a>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-32 max-w-[800px] mx-auto px-6">
        <Link
          to="/"
          className="
            group mb-12 my-10 inline-flex items-center gap-3
            text-sm font-semibold text-slate-600
            transition-colors duration-300
            hover:text-blue-700
          "
        >
          <span className="transition-transform duration-300 group-hover:-translate-x-1">
            ←
          </span>
          Back to Home
        </Link>

      <div className="text-center mb-10">
        <h1 className="text-4xl font-bold text-blue-950">
          Abstract Submission
        </h1>

        <p className="text-gray-600 mt-4 text-lg">
          Please prepare your abstract using the official
          Quantum Peeks 2026 template before submitting it.
        </p>
      </div>

      {/* Official abstract templates */}
      <section className="mb-10 overflow-hidden rounded-xl border border-blue-200 bg-blue-50 shadow-sm">
        <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-blue-800 px-6 py-5 text-white">
          <h2 className="text-2xl font-bold">
            Official Abstract Format
          </h2>

          <p className="text-blue-100 mt-2">
            Download and use the official conference template.
          </p>
        </div>

          <div className="max-w-xl mx-auto p-6">
          {/* Word template */}
          <a
            // href="/templates/QuantumPeeks_Abstract_Template.docx"
            href={publicPath(
              "templates/QuantumPeeks_Abstract_Template.docx"
            )}
            download="QuantumPeeks_Abstract_Template.docx"
            className="group flex items-center justify-between gap-4 rounded-lg border border-blue-200 bg-white p-5 shadow-sm hover:-translate-y-1 hover:border-blue-500 hover:shadow-lg transition-all duration-300"
          >
            <div>
              <p className="text-lg font-bold text-blue-950">
                Word Template
              </p>

              <p className="mt-1 text-sm text-gray-500">
                Microsoft Word format
              </p>

              <p className="mt-2 text-xs font-semibold text-blue-600">
                .DOCX
              </p>
            </div>

            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-2xl transition group-hover:bg-blue-600 group-hover:text-white">
              ↓
            </div>
          </a>

          {/* LaTeX template */}
          {/* <a
            href="/templates/QuantumPeeks_Abstract_Template.tex" // Replace with the actual path to the LaTeX template and uncomment the publicPath function if hosting on IIT Mandi server
            // href={publicPath(
            //   "templates/QuantumPeeks_Abstract_Template.tex"
            // )}
            download="QuantumPeeks_Abstract_Template.tex"
            className="group flex items-center justify-between gap-4 rounded-lg border border-blue-200 bg-white p-5 shadow-sm hover:-translate-y-1 hover:border-blue-500 hover:shadow-lg transition-all duration-300"
          >
            <div>
              <p className="text-lg font-bold text-blue-950">
                LaTeX Template
              </p>

              <p className="text-sm text-gray-500 mt-1">
                LaTeX source format
              </p>

              <p className="text-xs font-semibold text-blue-600 mt-2">
                .TEX
              </p>
            </div>

            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-2xl group-hover:bg-blue-600 group-hover:text-white transition">
              ↓
            </div>
          </a> */}
        </div>

        <div className="mx-6 mb-6 rounded-md border-l-4 border-yellow-500 bg-yellow-50 p-4 text-sm text-gray-700">
          <strong>Important:</strong> Please do not change the
          page size, margins, fonts, or general formatting of
          the official template.
        </div>
      </section>

      {/* Submission form */}
      <form
        onSubmit={handleSubmit}
        className="space-y-6 bg-white border rounded-xl p-6 md:p-8 shadow-lg"
      >
        <div>
          <label
            htmlFor="abstract-title-prefix"
            className="block font-semibold mb-2"
          >
            Title
          </label>

          <select
            id="abstract-title-prefix"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="Mr.">Mr.</option>
            <option value="Ms.">Ms.</option>
            <option value="Mrs.">Mrs.</option>
            <option value="Dr.">Dr.</option>
            <option value="Prof.">Prof.</option>
          </select>
        </div>

        <div>
          <label
            htmlFor="abstract-name"
            className="block font-semibold mb-2"
          >
            Full Name
          </label>

          <input
            id="abstract-name"
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter the participant's full name"
            required
          />
        </div>

        <div>
          <label
            htmlFor="abstract-institution"
            className="block font-semibold mb-2"
          >
            Institution / Affiliation
          </label>

          <input
            id="abstract-institution"
            type="text"
            name="institution"
            value={formData.institution}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="University, institute, or organization"
            required
          />
        </div>

        <div>
          <label
            htmlFor="abstract-email"
            className="block font-semibold mb-2"
          >
            Email Address
          </label>

          <input
            id="abstract-email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="your@email.com"
            required
          />
        </div>

        <div>
          <label
            htmlFor="abstract-category"
            className="block font-semibold mb-2"
          >
            Category
          </label>

          <select
            id="abstract-category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="PhD">PhD</option>

            <option value="Postdoctoral Researcher">
              Postdoctoral Researcher
            </option>

            <option value="Research Associate (RA)">
              Research Associate (RA)
            </option>

            <option value="Faculty / Scientist">
              Faculty / Scientist
            </option>

            <option value="Industry">
              Industry
            </option>
          </select>
        </div>


        <div>
          <label
            htmlFor="preference"
            className="block font-semibold mb-3"
          >
            Presentation Preference
          </label>

          <select
            id="preference"
            name="preference"
            value={formData.preference}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg
                      bg-white focus:outline-none focus:ring-2
                      focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="Oral Presentation">
              Oral Presentation
            </option>

            <option value="Poster Presentation">
              Poster Presentation
            </option>

            <option value="Participation Only">
              Participation Only
            </option>
          </select>

          <p className="mt-2 text-sm text-gray-500">
            {/* The final presentation format will be decided by the Scientific Committee. */}
          </p>
        </div>

        {formData.preference !== "Participation Only" && (
          <>
            <div>
              <label
                htmlFor="abstract-title"
                className="block font-semibold mb-2"
              >
                Abstract Title
              </label>

              <input
                id="abstract-title"
                type="text"
                name="abstractTitle"
                value={formData.abstractTitle}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter the title of the abstract"
                required
              />
            </div>

            <div>
              <label
                htmlFor="abstract-keywords"
                className="block font-semibold mb-2"
              >
                Keywords{" "}
                <span className="text-sm font-normal text-gray-500">
                  (optional)
                </span>
              </label>

              <input
                id="abstract-keywords"
                type="text"
                name="keywords"
                value={formData.keywords}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="For example: quantum gases, optics, entanglement"
              />

              <p className="text-xs text-gray-500 mt-2">
                Separate multiple keywords using commas.
              </p>
            </div>
          </>
        )}

        {formData.preference !== "Participation Only" && (
          <div>
            <label className="block font-semibold mb-3">
              Upload Abstract
            </label>

            <AbstractUploader
              key={abstractResetKey}
              onFileReady={setAbstractFile}
            />
          </div>
        )}

        <div className="text-center pt-4">
          {submitStatus === "success" ? (
            <div className="inline-flex items-center justify-center px-8 py-3 rounded-md bg-green-100 border border-green-500 text-green-700 font-semibold">
              ✓ Abstract submitted successfully!
            </div>
          ) : submitStatus === "error" ? (
            <div className="rounded-md bg-red-50 border border-red-400 p-4 text-red-700 font-semibold">
              {errorMessage}
            </div>
          ) : (
            <button
              type="submit"
              disabled={submitStatus === "submitting"}
              className="group px-10 py-3 font-semibold text-white rounded-md bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-600 transition-all duration-500 shadow-md hover:shadow-2xl hover:scale-105 relative overflow-hidden disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              <span className="relative z-10">
                {submitStatus === "submitting"
                  ? "Submitting..."
                  : "Submit Abstract"}
              </span>

              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-0 group-hover:opacity-100 -translate-x-full group-hover:translate-x-full transition-all duration-700" />
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function About() {
  const topics = [
    {
      title: "Quantum Technologies",
      description:
        "Quantum information, quantum simulation, sensing, and emerging technological platforms.",
      icon: (
        <svg
          className="h-7 w-7"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 3v3m0 12v3M3 12h3m12 0h3M5.64 5.64l2.12 2.12m8.48 8.48 2.12 2.12m0-12.72-2.12 2.12M7.76 16.24l-2.12 2.12"
          />
          <circle cx="12" cy="12" r="4" />
        </svg>
      ),
      style:
        "from-purple-50 to-indigo-50 border-purple-200 text-purple-800",
      iconStyle:
        "bg-gradient-to-br from-purple-700 to-indigo-700 text-white",
    },
    {
      title: "Quantum Many-Body Systems",
      description:
        "Correlations, collective phenomena, strongly interacting systems, and emergent quantum behaviour.",
      icon: (
        <svg
          className="h-7 w-7"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle cx="6" cy="7" r="2" />
          <circle cx="18" cy="7" r="2" />
          <circle cx="12" cy="17" r="2" />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M7.8 8.2 10.5 15m5.7-6.8L13.5 15M8 7h8"
          />
        </svg>
      ),
      style:
        "from-blue-50 to-cyan-50 border-blue-200 text-blue-800",
      iconStyle:
        "bg-gradient-to-br from-blue-700 to-cyan-600 text-white",
    },
    {
      title: "Quantum Fluids",
      description:
        "Ultracold gases, superfluidity, vortices, collective excitations, and quantum hydrodynamics.",
      icon: (
        <svg
          className="h-7 w-7"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M4 7c2-2 4-2 6 0s4 2 6 0 4-2 4-2M4 12c2-2 4-2 6 0s4 2 6 0 4-2 4-2M4 17c2-2 4-2 6 0s4 2 6 0 4-2 4-2"
          />
        </svg>
      ),
      style:
        "from-cyan-50 to-teal-50 border-cyan-200 text-cyan-800",
      iconStyle:
        "bg-gradient-to-br from-cyan-700 to-teal-600 text-white",
    },
    {
      title: "Nonequilibrium Dynamics",
      description:
        "Driven quantum systems, quenches, universal dynamics, thermalisation, and quantum criticality.",
      icon: (
        <svg
          className="h-7 w-7"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 12h4l2-6 4 12 2-6h6"
          />
        </svg>
      ),
      style:
        "from-indigo-50 to-violet-50 border-indigo-200 text-indigo-800",
      iconStyle:
        "bg-gradient-to-br from-indigo-700 to-violet-700 text-white",
    },
    {
      title: "Light–Matter Interactions",
      description:
        "Quantum optics, cavity systems, photonics, coherent control, and hybrid quantum platforms.",
      icon: (
        <svg
          className="h-7 w-7"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 18h6m-5 3h4M8 14a6 6 0 1 1 8 0c-1.2.9-1.7 1.8-1.8 3h-4.4c-.1-1.2-.6-2.1-1.8-3Z"
          />
        </svg>
      ),
      style:
        "from-amber-50 to-yellow-50 border-amber-200 text-amber-800",
      iconStyle:
        "bg-gradient-to-br from-amber-500 to-orange-600 text-white",
    },
    {
      title: "Atomic and Ultrafast Physics",
      description:
        "Atomic, molecular, ultrafast, strong-field, and attosecond processes.",
      icon: (
        <svg
          className="h-7 w-7"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <ellipse cx="12" cy="12" rx="9" ry="3.5" />
          <ellipse
            cx="12"
            cy="12"
            rx="9"
            ry="3.5"
            transform="rotate(60 12 12)"
          />
          <ellipse
            cx="12"
            cy="12"
            rx="9"
            ry="3.5"
            transform="rotate(-60 12 12)"
          />
          <circle cx="12" cy="12" r="1.8" fill="currentColor" />
        </svg>
      ),
      style:
        "from-rose-50 to-pink-50 border-rose-200 text-rose-800",
      iconStyle:
        "bg-gradient-to-br from-rose-600 to-pink-600 text-white",
    },
  ];

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-white via-slate-50 to-blue-50/40 py-24">
      {/* Scientific background */}
      <div className="pointer-events-none absolute inset-0">
      <div className="absolute left-[4%] top-[12%] h-48 w-48 rounded-full bg-cyan-100/60" />

      <div className="absolute bottom-[8%] right-[4%] h-64 w-64 rounded-full bg-purple-100/50" />

        <svg
          className="absolute inset-0 h-full w-full opacity-[0.06]"
          viewBox="0 0 1600 900"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <g stroke="#1d4ed8" strokeWidth="1.5" fill="none">
            <path d="M0 180 C250 80 430 290 680 160 C930 30 1170 260 1600 100" />
            <path d="M0 650 C270 500 520 760 790 580 C1060 400 1260 690 1600 510" />
          </g>

          <g fill="#0ea5e9">
            <circle cx="130" cy="185" r="5" />
            <circle cx="420" cy="205" r="4" />
            <circle cx="700" cy="150" r="5" />
            <circle cx="1110" cy="190" r="4" />
            <circle cx="1410" cy="120" r="5" />
          </g>
        </svg>
      </div>

      <div className="relative mx-auto max-w-[1400px] px-6">
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true, margin: "-100px" }}
        >
          {/* Section heading */}
          <div className="mx-auto mb-14 max-w-4xl text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white/80 px-4 py-2 text-xs font-bold uppercase tracking-[0.24em] text-blue-700 shadow-sm backdrop-blur-md">
              <span className="h-2 w-2 animate-pulse rounded-full bg-cyan-500" />
              About Quantum Peeks 2026
            </div>

            <h2 className="mt-6 text-4xl font-extrabold tracking-tight text-blue-950 sm:text-5xl">
              A Meeting at the Frontiers of Quantum Science
            </h2>

            <p className="mx-auto mt-5 max-w-3xl text-lg leading-8 text-gray-600">
              Bringing together ideas from quantum matter, light, dynamics,
              technology, and ultrafast science in the Himalayan setting of
              IIT Mandi.
            </p>
          </div>

          {/* Main content card — optimized for smooth scrolling */}
          <div className="relative mb-16 overflow-hidden rounded-[2rem] border border-blue-100 bg-white shadow-[0_18px_55px_rgba(15,23,42,0.10)]">
            {/* Lightweight decorative circles */}
            <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-purple-100/70" />

            <div className="pointer-events-none absolute -bottom-16 left-1/4 h-44 w-44 rounded-full bg-cyan-100/60" />

            <div className="relative grid grid-cols-1 lg:grid-cols-[1.3fr_0.7fr]">
              {/* Conference description */}
              <div className="relative z-10 p-7 sm:p-10 lg:p-14">
                <p className="text-sm font-bold uppercase tracking-[0.22em] text-blue-700">
                  The Conference
                </p>

                <h3 className="mt-4 text-3xl font-extrabold leading-tight text-blue-950">
                  Connecting Fundamental Physics with Emerging Quantum Technologies
                </h3>

                <p className="mt-6 text-base leading-8 text-gray-600 sm:text-lg">
                  <strong className="font-bold text-blue-950">
                    Quantum Peeks 2026
                  </strong>{" "}
                  is an international conference dedicated to recent advances in
                  quantum science and emerging quantum technologies. The meeting will
                  bring together leading researchers, early-career scientists, and
                  students working across complementary areas of modern quantum
                  physics.
                </p>

                <p className="mt-5 text-base leading-8 text-gray-600 sm:text-lg">
                  The scientific programme will cover quantum simulation, quantum
                  many-body physics, quantum fluids, nonequilibrium quantum dynamics,
                  light–matter interactions, photonic quantum technologies, atomic
                  and molecular physics, ultrafast phenomena, and attosecond science.
                </p>

                <p className="mt-5 text-base leading-8 text-gray-600 sm:text-lg">
                  Through plenary lectures, invited talks, contributed presentations,
                  poster sessions, and informal scientific interactions, the
                  conference aims to encourage new collaborations and promote
                  interdisciplinary research at the forefront of quantum science.
                </p>

                <div className="mt-8 flex flex-wrap gap-3">
                  {[
                    "Plenary Lectures",
                    "Invited Talks",
                    "Contributed Talks",
                    "Poster Sessions",
                  ].map((item) => (
                    <span
                      key={item}
                      className="rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-sm font-bold text-blue-800"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              {/* Scientific illustration */}
              <div className="relative flex min-h-[420px] items-center justify-center overflow-hidden bg-gradient-to-br from-blue-950 via-indigo-900 to-cyan-900 p-8 sm:p-10">
                {/* Lightweight orbit background */}
                <svg
                  className="pointer-events-none absolute inset-0 h-full w-full opacity-20"
                  viewBox="0 0 500 500"
                  aria-hidden="true"
                >
                  <circle
                    cx="250"
                    cy="250"
                    r="190"
                    fill="none"
                    stroke="#67e8f9"
                    strokeWidth="1"
                    strokeDasharray="8 12"
                  />

                  <circle
                    cx="250"
                    cy="250"
                    r="145"
                    fill="none"
                    stroke="#c4b5fd"
                    strokeWidth="1"
                  />

                  <circle
                    cx="250"
                    cy="250"
                    r="95"
                    fill="none"
                    stroke="#60a5fa"
                    strokeWidth="1"
                    strokeDasharray="4 8"
                  />
                </svg>

                {/* Atom */}
                <div className="relative h-[280px] w-[280px] sm:h-[310px] sm:w-[310px]">
                  {/* Static glow instead of blur/drop-shadow */}
                  <div className="absolute inset-[22%] rounded-full bg-cyan-300/15" />

                  <svg
                    viewBox="0 0 400 400"
                    className="relative h-full w-full"
                    aria-hidden="true"
                  >
                    <g fill="none" strokeWidth="3" opacity="0.9">
                      <ellipse
                        cx="200"
                        cy="200"
                        rx="150"
                        ry="58"
                        stroke="#67e8f9"
                      />

                      <ellipse
                        cx="200"
                        cy="200"
                        rx="150"
                        ry="58"
                        transform="rotate(60 200 200)"
                        stroke="#c4b5fd"
                      />

                      <ellipse
                        cx="200"
                        cy="200"
                        rx="150"
                        ry="58"
                        transform="rotate(-60 200 200)"
                        stroke="#60a5fa"
                      />
                    </g>

                    <circle
                      cx="200"
                      cy="200"
                      r="46"
                      fill="#22d3ee"
                      opacity="0.2"
                    />

                    <circle cx="183" cy="188" r="18" fill="#facc15" />
                    <circle cx="214" cy="186" r="17" fill="#c4b5fd" />
                    <circle cx="198" cy="216" r="19" fill="#60a5fa" />
                    <circle cx="224" cy="214" r="14" fill="#f472b6" />
                    <circle cx="174" cy="216" r="14" fill="#22d3ee" />

                    {/* electrons */}
                    <circle
                    cx="350"
                    cy="200"
                    r="10"
                    fill="#67e8f9"
                    className="animate-pulse"
                    />

                    <circle
                    cx="125"
                    cy="70"
                    r="10"
                    fill="#c4b5fd"
                    className="animate-pulse [animation-delay:300ms]"
                    />

                    <circle
                    cx="125"
                    cy="330"
                    r="10"
                    fill="#facc15"
                    className="animate-pulse [animation-delay:600ms]"
                    />
                  </svg>
                </div>

                {/* Bottom label */}
                <div className="absolute bottom-8 left-8 right-8 rounded-2xl border border-white/15 bg-blue-950/80 p-5 text-center text-white shadow-lg">
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-cyan-200">
                    Scientific Theme
                  </p>

                  <p className="mt-2 text-lg font-bold">
                    Coherence in Light and Matter
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Topics section */}
          <div>
            <div className="mb-10 text-center">
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-blue-700">
                Scientific Scope
              </p>

              <h3 className="mt-3 text-3xl font-extrabold text-blue-950 sm:text-4xl">
                Topics of Interest
              </h3>

              <p className="mx-auto mt-4 max-w-3xl text-gray-600">
                The conference spans complementary areas of quantum science,
                from fundamental many-body phenomena to emerging technologies.
              </p>
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {topics.map((topic, index) => (
                <motion.article
                  key={topic.title}
                  initial={{ y: 35, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{
                    duration: 0.55,
                    delay: index * 0.08,
                    ease: "easeOut",
                  }}
                  viewport={{ once: true, margin: "-60px" }}
                  className={`group relative overflow-hidden rounded-2xl border bg-gradient-to-br p-6 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-xl ${topic.style}`}
                >
                  <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-white/40 transition-transform duration-500 group-hover:scale-150" />

                  <div className="relative">
                    <div
                      className={`flex h-14 w-14 items-center justify-center rounded-2xl shadow-lg ${topic.iconStyle}`}
                    >
                      {topic.icon}
                    </div>

                    <h4 className="mt-5 text-xl font-extrabold text-gray-900">
                      {topic.title}
                    </h4>

                    <p className="mt-3 text-sm leading-7 text-gray-600">
                      {topic.description}
                    </p>

                    <div className="mt-5 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] opacity-70">
                      <span className="h-px w-8 bg-current" />
                      Quantum Peeks 2026
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function ImportantDates() {
  return (
    <section
      id="dates"
      className="py-24 bg-gradient-to-r from-blue-900 via-indigo-900 to-blue-800 text-white"
    >
      <div className="max-w-[1100px] mx-auto px-6">
        <h2 className="text-4xl font-bold mb-12 text-center">
          Important Dates
        </h2>

        <div className="space-y-6">
          {/* Abstract Submission */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 bg-white/10 border border-white/20 rounded-xl p-6">
            <span className="text-lg font-semibold">
              Abstract Submission
            </span>

            <div className="sm:text-right">
              <p className="text-yellow-400 text-xl font-bold">
                25 July – 31 August 2026
              </p>

              {/*
                WHEN THE DEADLINE IS EXTENDED:

                1. Comment out or delete the yellow date above.
                2. Uncomment the block below.
              */}

              {/*
              <div>
                <p className="text-gray-300 text-sm">
                  1 July –
                  <span className="ml-1 text-red-400 line-through decoration-2">
                    31 August 2026
                  </span>
                </p>

                <p className="text-red-400 text-xl font-bold mt-1">
                  Extended until 10 September 2026
                </p>
              </div>
              */}
            </div>
          </div>

          {/* Acceptance Notification */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 bg-white/10 border border-white/20 rounded-xl p-6">
            <span className="text-lg font-semibold">
              Acceptance Notification
            </span>

            <div className="sm:text-right">
              <p className="text-yellow-400 text-xl font-bold">
                3 September 2026
              </p>

              {/*
              <p className="text-red-400 text-xl font-bold">
                <span className="text-gray-300 line-through decoration-red-500 decoration-2 mr-3">
                  3 September 2026
                </span>
                8 September 2026
              </p>
              */}
            </div>
          </div>

          {/* Registration */}
<div className="bg-white/10 border border-white/20 rounded-xl p-6">
  <div className="flex flex-col sm:flex-row sm:justify-between gap-5">
    <span className="text-lg font-semibold">
      Registration
    </span>

    <div className="sm:text-right space-y-4">
      {/* Full registration period */}
      <div>
        <p className="text-blue-200 text-sm font-semibold uppercase tracking-wide">
          Registration Period
        </p>

        <p className="text-yellow-400 text-xl font-bold mt-1">
          3 September – 15 October 2026
        </p>
      </div>

      {/* Early registration deadline */}
      <div className="pt-4 border-t border-white/20">
        <p className="text-blue-200 text-sm font-semibold uppercase tracking-wide">
          Early Registration Deadline
        </p>

        <p className="text-yellow-400 text-xl font-bold mt-1">
          30 September 2026
        </p>

        {/*
          WHEN EARLY REGISTRATION IS EXTENDED:

          Comment out the yellow date above and uncomment below.
        */}

        {/*
        <div className="mt-1">
          <p className="text-red-400 line-through decoration-2">
            30 September 2026
          </p>

          <p className="text-red-400 text-xl font-bold mt-1">
            Extended until 7 October 2026
          </p>
        </div>
        */}
      </div>
    </div>
  </div>
</div>

          {/* Conference */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 bg-white/10 border border-white/20 rounded-xl p-8">
            <span className="text-lg font-semibold">
              Conference
            </span>

            <p className="text-yellow-400 text-xl font-bold sm:text-right">
              14–18 November 2026
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}


function VenueSection() {
  return (
    <section id="venue" className="py-24 max-w-[1400px] mx-auto px-6">
      <h2 className="text-4xl font-bold mb-8">Venue</h2>

      <p className="text-gray-700 text-lg mb-6">
        Indian Institute of Technology Mandi is located in the beautiful
        Himalayan region of Himachal Pradesh. The conference will be hosted
        on the scenic IIT Mandi campus surrounded by the Himalayas.
      </p>

<div className="grid md:grid-cols-2 gap-6 mt-8">

<div className="overflow-hidden rounded-xl shadow-lg">
  <img
    src={campus1}
    alt="IIT Mandi Campus"
    className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
  />
</div>

<div className="overflow-hidden rounded-xl shadow-lg">
  <img
    src={campus2}
    alt="IIT Mandi Campus"
    className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
  />
</div>

</div>
    </section>
  );
}

function WeatherSection() {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const getWeatherIcon = (code) => {
    if (code === 0) return "☀️";
    if (code === 1 || code === 2) return "🌤️";
    if (code === 3) return "☁️";

    if (code === 45 || code === 48) return "🌫️";

    if ([51, 53, 55, 56, 57].includes(code)) return "🌦️";

    if ([61, 63, 65, 66, 67].includes(code)) return "🌧️";

    if ([71, 73, 75, 77].includes(code)) return "❄️";

    if ([80, 81, 82].includes(code)) return "🌧️";

    if ([85, 86].includes(code)) return "🌨️";

    if ([95, 96, 99].includes(code)) return "⛈️";

    return "🌤️";
  };

  const getWeatherDescription = (code) => {
    if (code === 0) return "Clear sky";
    if (code === 1) return "Mainly clear";
    if (code === 2) return "Partly cloudy";
    if (code === 3) return "Overcast";

    if (code === 45 || code === 48) return "Foggy";

    if ([51, 53, 55].includes(code)) return "Drizzle";
    if ([56, 57].includes(code)) return "Freezing drizzle";

    if ([61, 63, 65].includes(code)) return "Rain";
    if ([66, 67].includes(code)) return "Freezing rain";

    if ([71, 73, 75, 77].includes(code)) return "Snow";

    if ([80, 81, 82].includes(code)) return "Rain showers";
    if ([85, 86].includes(code)) return "Snow showers";

    if ([95, 96, 99].includes(code)) return "Thunderstorm";

    return "Weather conditions";
  };

  useEffect(() => {
    const controller = new AbortController();

    const fetchWeather = async () => {
      try {
        setLoading(true);
        setError("");

        const latitude = 31.7754;
        const longitude = 76.9861;

        const url =
          `https://api.open-meteo.com/v1/forecast` +
          `?latitude=${latitude}` +
          `&longitude=${longitude}` +
          `&current=temperature_2m,relative_humidity_2m,apparent_temperature,wind_speed_10m,weather_code` +
          `&daily=weather_code,temperature_2m_max,temperature_2m_min` +
          `&temperature_unit=celsius` +
          `&wind_speed_unit=kmh` +
          `&timezone=Asia%2FKolkata` +
          `&forecast_days=7`;

        const response = await fetch(url, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Weather request failed: ${response.status}`);
        }

        const data = await response.json();

        if (!data.current || !data.daily) {
          throw new Error("Incomplete weather data received.");
        }

        setWeather(data);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Weather API error:", err);
          setError("Weather information is currently unavailable.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();

    return () => controller.abort();
  }, []);

  const forecast =
    weather?.daily?.time?.map((date, index) => ({
      date,
      day: new Date(`${date}T00:00:00`).toLocaleDateString("en-IN", {
        weekday: "short",
      }),
      code: weather.daily.weather_code[index],
      max: Math.round(weather.daily.temperature_2m_max[index]),
      min: Math.round(weather.daily.temperature_2m_min[index]),
    })) ?? [];

  const currentCode = weather?.current?.weather_code;

  return (
    <section id="weather" className="py-24 bg-blue-900 text-white">
      <div className="max-w-[1200px] mx-auto px-6">
        <h2 className="text-4xl font-bold mb-6">
          Weather at IIT Mandi
        </h2>

        <p className="text-lg text-blue-100 mb-6 leading-relaxed">
          Indian Institute of Technology Mandi is located in the Himalayan
          region of Himachal Pradesh at an elevation of approximately 1,000
          metres. The climate is generally milder than the plains. During
          November and December, the weather is usually cool during the day,
          while evenings and mornings can be chilly.
        </p>

        <p className="text-lg text-blue-100 mb-10 leading-relaxed">
          Participants are advised to carry winter clothing such as jackets
          and sweaters. The mountain weather can change quickly, so layered
          clothing is recommended.
        </p>

        <div className="bg-gradient-to-br from-blue-800/60 to-blue-950/70 border border-white/20 p-6 md:p-8 shadow-xl">
          {loading && (
            <div className="py-16 text-center">
              <div className="mx-auto mb-5 h-10 w-10 rounded-full border-4 border-blue-300 border-t-yellow-400 animate-spin" />

              <p className="text-blue-100 text-lg">
                Loading weather information...
              </p>
            </div>
          )}

          {!loading && error && (
            <div className="py-14 text-center">
              <p className="text-5xl mb-4">🌦️</p>

              <p className="text-xl font-semibold text-yellow-400 mb-2">
                Weather unavailable
              </p>

              <p className="text-blue-100">
                {error}
              </p>
            </div>
          )}

          {!loading && !error && weather && (
            <>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-10 mb-10">
                <div className="text-center md:text-left">
                  <h3 className="text-2xl font-bold mb-3">
                    Current Weather
                  </h3>

                  <div className="flex items-center justify-center md:justify-start gap-4">
                    <span className="text-6xl" aria-hidden="true">
                      {getWeatherIcon(currentCode)}
                    </span>

                    <p className="text-6xl font-bold text-yellow-400">
                      {Math.round(weather.current.temperature_2m)}°C
                    </p>
                  </div>

                  <p className="text-blue-100 mt-4 text-lg">
                    {getWeatherDescription(currentCode)}
                  </p>

                  <p className="text-blue-300 mt-1 text-sm">
                    Last updated:{" "}
                    {new Date(weather.current.time).toLocaleString("en-IN", {
                      weekday: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    })}
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 text-center w-full md:w-auto">
                  <div className="bg-white/10 border border-white/10 px-6 py-5">
                    <p className="text-3xl mb-2">💧</p>

                    <p className="text-yellow-400 text-xl font-bold">
                      {weather.current.relative_humidity_2m}%
                    </p>

                    <p className="text-blue-200 text-sm mt-1">
                      Humidity
                    </p>
                  </div>

                  <div className="bg-white/10 border border-white/10 px-6 py-5">
                    <p className="text-3xl mb-2">🌬️</p>

                    <p className="text-yellow-400 text-xl font-bold">
                      {Math.round(weather.current.wind_speed_10m)} km/h
                    </p>

                    <p className="text-blue-200 text-sm mt-1">
                      Wind speed
                    </p>
                  </div>

                  <div className="bg-white/10 border border-white/10 px-6 py-5">
                    <p className="text-3xl mb-2">🌡️</p>

                    <p className="text-yellow-400 text-xl font-bold">
                      {Math.round(weather.current.apparent_temperature)}°C
                    </p>

                    <p className="text-blue-200 text-sm mt-1">
                      Feels like
                    </p>
                  </div>
                </div>
              </div>

              <h3 className="text-xl font-bold mb-6">
                7-Day Forecast
              </h3>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-7 gap-4 text-center">
                {forecast.map((day) => (
                  <div
                    key={day.date}
                    className="bg-white/10 border border-white/10 p-4 transition duration-300 hover:bg-white/20 hover:-translate-y-1"
                  >
                    <p className="font-semibold">
                      {day.day}
                    </p>

                    <p className="text-3xl mt-2" aria-hidden="true">
                      {getWeatherIcon(day.code)}
                    </p>

                    <p className="text-yellow-400 text-lg font-bold mt-3">
                      {day.max}°
                    </p>

                    <p className="text-blue-200 text-sm">
                      {day.min}°
                    </p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <p className="text-blue-200 mt-8 text-sm">
          Live weather and forecast data provided by Open-Meteo. Forecasts
          may differ from local conditions within the IIT Mandi campus.
        </p>
      </div>
    </section>
  );
}


function ContactSection() {
  return (
    <section
      id="contact"
      className="bg-[#f3f1ec] py-2 text-slate-900"
    >
      <div className="mx-auto max-w-[900px] px-10 text-center">

        <h2 className="mt-4 text-5xl font-bold tracking-[-0.045em] sm:text-6xl">
          Contact
        </h2>

        <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-slate-600">
          For any queries regarding the conference, including abstract
          submission and registration, please contact
          us.
        </p>

        <div className="mt-3 space-y-4">

          {/* Secretariat */}
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">
              Conference Secretariat
            </p>

            <p className="mt-3 text-xl font-semibold text-slate-900">
              Quantum Peeks 2026
            </p>

            <p className="mt-2 text-lg leading-8 text-slate-700">
              School of Physical Sciences
              <br />
              Indian Institute of Technology Mandi
              <br />
              Kamand, Mandi – 175075
              <br />
              Himachal Pradesh, India
            </p>
          </div>

          {/* Email */}
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">
              Email
            </p>

          <a
          href="mailto:quantumpeeks2026@iitmandi.ac.in"
          className="mt-3 mb-6 inline-block text-xl font-semibold text-[#b32025] transition-colors duration-300 hover:text-[#8d1c20]"          >
          quantumpeeks2026@iitmandi.ac.in
          </a>
          </div>

        </div>

      </div>
    </section>
  );
}


// function ContactSection() {
//   return (
//     <section id="contact" className="py-24 bg-gray-100">
//       <div className="max-w-[1000px] mx-auto px-6 text-center">
//         <h2 className="text-4xl font-bold mb-6">Contact</h2>

//         <p className="text-lg text-gray-700 mb-4">
//           For any questions regarding the conference, registration, or
//           abstract submission, please contact the organizing committee.
//         </p>

//         <div className="mt-6 space-y-2 text-lg">
//           <p><span className="font-semibold">Email:</span> quantumpeeks2026@iitmandi.ac.in</p>
//           <p><span className="font-semibold">Address:</span> Indian Institute of Technology Mandi</p>
//         </div>
//       </div>
//     </section>
//   );
// }

// function SponsorsSection() {
//   const sponsors = [
//     {
//       name: "Sponsor Name",
//       logo: "/sponsors/logo1.png",
//     },
//     {
//       name: "Sponsor Name",
//       logo: "/sponsors/logo2.png",
//     },
//     {
//       name: "Sponsor Name",
//       logo: "/sponsors/logo3.png",
//     },
//     {
//       name: "Sponsor Name",
//       logo: "/sponsors/logo4.png",
//     },
//   ];

//   return (
//     <section id="sponsors" className="py-24 bg-gray-50">
//       <div className="max-w-[1300px] mx-auto px-6">

//         {/* Heading */}
//         <div className="text-center mb-16">
//           <h2 className="text-4xl font-bold text-blue-900">
//             Sponsors & Partners
//           </h2>

//           <div className="w-24 h-1 bg-yellow-400 mx-auto mt-4 rounded-full"></div>

//           <p className="mt-6 text-gray-600 max-w-3xl mx-auto text-lg">
//             QuantumPeeks 2026 gratefully acknowledges the generous support of
//             our sponsors and institutional partners.
//           </p>
//         </div>

//         {/* Sponsor Logos */}
//         <div className="grid grid-cols-2 md:grid-cols-4 gap-10">

//           {sponsors.map((sponsor, index) => (
//             <div
//               key={index}
//               className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-8 flex flex-col items-center justify-center"
//             >
//               <img
//                 src={sponsor.logo}
//                 alt={sponsor.name}
//                 className="h-24 object-contain mb-5"
//               />

//               <h3 className="text-lg font-semibold text-blue-900 text-center">
//                 {sponsor.name}
//               </h3>
//             </div>
//           ))}

//         </div>

//       </div>
//     </section>
//   );
// }

function AttractionsSection() {
  const attractions = [
    {
      name: "Prashar Lake",
      description:
        "A spectacular high-altitude Himalayan lake known for its floating island, peaceful mountain surroundings and traditional pagoda-style Prashar Rishi Temple.",
      // image: "/attractions/parashar.avif",
      image: publicPath("attractions/parashar.avif"),
      map: "https://www.google.com/maps/search/?api=1&query=Prashar+Lake+Himachal+Pradesh",
    },
    {
      name: "Barot Valley",
      description:
        "A scenic Himalayan valley surrounded by forests and mountains, known for riverside landscapes, walking trails and a quiet natural atmosphere.",
      // image: "/attractions/barot.avif",
      image: publicPath("attractions/barot.avif"),
      map: "https://www.google.com/maps/search/?api=1&query=Barot+Valley+Himachal+Pradesh",
    },
    {
      name: "Manali",
      description:
        "A popular Himalayan destination offering snow-covered peaks, scenic valleys, temples, cafés and a wide range of outdoor activities.",
      // image: "/attractions/manali.avif",
      image: publicPath("attractions/manali.avif"),
      map: "https://www.google.com/maps/search/?api=1&query=Manali+Himachal+Pradesh",
    },
    {
      name: "Dharamshala",
      description:
        "Serving as the winter capital of Himachal Pradesh, Dharamshala is known for its Tibetan culture, Buddhist monasteries and spectacular views of the Dhauladhar mountains",
      // image: "/attractions/dharamshala.avif",
      image: publicPath("attractions/dharamshala.avif"),
      map: "https://www.google.com/maps/search/?api=1&query=Dharamshala+Himachal+Pradesh",
    },
    {
      name: "Shimla",
      description:
        "The capital of Himachal Pradesh, known for colonial architecture, Mall Road, pine-covered hills and beautiful Himalayan surroundings.",
      // image: "/attractions/shimla.avif",
      image: publicPath("attractions/shimla.avif"),
      map: "https://www.google.com/maps/search/?api=1&query=Shimla+Himachal+Pradesh",
    },
  ];

  return (
    <section id="attractions" className="py-24 bg-gray-50">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Heading */}
        <div className="text-center mb-14">
          <h2 className="text-4xl font-bold text-blue-900">
            Tourist Attractions
          </h2>

          <div className="w-24 h-1 bg-yellow-400 mx-auto mt-4 rounded-full" />

          <p className="mt-6 text-gray-600 text-lg max-w-3xl mx-auto leading-relaxed">
          Discover some of the most scenic, cultural and iconic destinations in Mandi and the surrounding Himalayan region of Himachal Pradesh.
          </p>
        </div>

        {/* Attraction cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
          {attractions.map((place) => (
            <div
              key={place.name}
              className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 flex flex-col"
            >
              {/* Image */}
              <div className="h-56 overflow-hidden bg-gray-200">
                <img
                  src={place.image}
                  alt={place.name}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                />
              </div>

              {/* Information */}
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-xl font-bold text-blue-900 mb-3">
                  {place.name}
                </h3>

                <p className="text-gray-600 text-sm leading-relaxed flex-grow">
                  {place.description}
                </p>

                <a
                  href={place.map}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-6 text-center px-5 py-2.5 rounded-md bg-blue-900 text-white font-semibold hover:bg-yellow-400 hover:text-blue-950 transition"
                >
                  View on Map
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* More attractions */}
        <div className="text-center mt-14">
          <p className="text-gray-600 max-w-3xl mx-auto leading-relaxed mb-6">
            Mandi district offers many more lakes, temples, trekking routes,
            heritage sites and scenic viewpoints.
          </p>

          <a
            href="https://hpmandi.nic.in/tourist-places/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-lg bg-yellow-400 text-blue-950 font-semibold shadow-md hover:bg-yellow-500 hover:-translate-y-1 transition-all duration-300"
          >
            Explore More Places in Mandi
            <span aria-hidden="true">→</span>
          </a>
        </div>

      </div>
    </section>
  );
}

function ConferenceHighlightsSection() {
  const highlights = [
    {
      type: "Plenary Lecture",
      badge: "Featured",
      date: "14 November 2026",
      time: "14:30 – 15:30",
      title: "Opening Plenary Lecture",
      speaker: "Prof. Franco Dalfovo",
      affiliation:
        "Pitaevskii BEC Center, University of Trento, Italy",
      description:
        "A plenary lecture exploring Bose–Einstein condensates, quantum fluids, collective excitations, and nonequilibrium many-body dynamics.",
      // image: "/speakers/Franco_Dalfovo.jpg",
      image: publicPath("speakers/Franco_Dalfovo.jpg"),
      topics: [
        "Bose–Einstein Condensates",
        "Quantum Fluids",
        "Nonequilibrium Dynamics",
      ],
      accent:
        "from-purple-950 via-indigo-900 to-blue-900",
      badgeStyle:
        "border-purple-200 bg-purple-100 text-purple-800",
    },
    {
      type: "Plenary Lecture",
      badge: "Featured",
      date: "15 November 2026",
      time: "09:00 – 10:00",
      title: "Plenary Lecture II",
      speaker: "Prof. Adolfo del Campo",
      affiliation: "University of Luxembourg, Luxembourg",
      description:
        "A plenary presentation on nonequilibrium quantum dynamics, universal scaling, defect formation, and shortcuts to adiabaticity.",
      image: publicPath("/speakers/Adolfo_del_Campo.jpeg"),
      topics: [
        "Quantum Dynamics",
        "Universal Scaling",
        "Shortcuts to Adiabaticity",
      ],
      accent:
        "from-blue-950 via-indigo-900 to-cyan-900",
      badgeStyle:
        "border-blue-200 bg-blue-100 text-blue-800",
    },
    {
      type: "Programme Update",
      badge: "Announcement",
      date: "14–18 November 2026",
      time: "IIT Mandi",
      title: "Scientific Programme",
      speaker: "Quantum Peeks 2026",
      affiliation:
        "A Himalayan Summit on Coherence in Light and Matter",
      description:
        "The conference programme will include plenary lectures, invited talks, contributed presentations, poster sessions, panel discussions, and scientific networking.",
      // image: "/programme/programme-highlight.jpg",
      // image: publicPath("programme/programme-highlight.jpg"),
      topics: [
        "Plenary Lectures",
        "Invited Talks",
        "Poster Sessions",
      ],
      accent:
        "from-cyan-950 via-blue-900 to-indigo-900",
      badgeStyle:
        "border-cyan-200 bg-cyan-100 text-cyan-800",
    },
  ];

  const slideDuration = 6000;
  const transitionDuration = 900;

  /*
    Internal slide structure:

    [last clone] [slide 1] [slide 2] [slide 3] [first clone]

    Initial position is 1, which corresponds to the real first slide.
  */
  const circularSlides =
    highlights.length > 0
      ? [
          highlights[highlights.length - 1],
          ...highlights,
          highlights[0],
        ]
      : [];

  const [slidePosition, setSlidePosition] = useState(1);
  const [transitionEnabled, setTransitionEnabled] =
    useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  /*
    Converts the internal slide position into the real slide index:

    internal position 1 -> real index 0
    internal position 2 -> real index 1
    internal position 3 -> real index 2
  */
  const activeSlide =
    highlights.length > 0
      ? (slidePosition - 1 + highlights.length) %
        highlights.length
      : 0;

  const moveToNextSlide = () => {
    if (isAnimating || highlights.length <= 1) {
      return;
    }

    setIsAnimating(true);
    setTransitionEnabled(true);
    setSlidePosition((current) => current + 1);
  };

  const moveToPreviousSlide = () => {
    if (isAnimating || highlights.length <= 1) {
      return;
    }

    setIsAnimating(true);
    setTransitionEnabled(true);
    setSlidePosition((current) => current - 1);
  };

  const goToSlide = (index) => {
    if (
      isAnimating ||
      index === activeSlide ||
      highlights.length <= 1
    ) {
      return;
    }

    setIsAnimating(true);
    setTransitionEnabled(true);
    setSlidePosition(index + 1);
  };

  /*
    Automatic movement.

    A timeout is used instead of setInterval so that each new timer
    begins after the previous transition has completed.
  */
  useEffect(() => {
    if (
      isPaused ||
      isAnimating ||
      highlights.length <= 1
    ) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setIsAnimating(true);
      setTransitionEnabled(true);
      setSlidePosition((current) => current + 1);
    }, slideDuration);

    return () => window.clearTimeout(timer);
  }, [
    activeSlide,
    isPaused,
    isAnimating,
    highlights.length,
  ]);

  /*
    This event must only respond to the carousel track's transform.

    Child elements such as the speaker image also use CSS transitions.
    Their transitionend events bubble upward, so they must be ignored.
  */
  const handleTrackTransitionEnd = (event) => {
    if (event.target !== event.currentTarget) {
      return;
    }

    if (event.propertyName !== "transform") {
      return;
    }

    /*
      The track reached the cloned first slide.

      Disable animation and reset silently to the real first slide.
    */
    if (slidePosition === highlights.length + 1) {
      setTransitionEnabled(false);
      setSlidePosition(1);

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          setTransitionEnabled(true);
          setIsAnimating(false);
        });
      });

      return;
    }

    /*
      The track reached the cloned last slide.

      Disable animation and reset silently to the real last slide.
    */
    if (slidePosition === 0) {
      setTransitionEnabled(false);
      setSlidePosition(highlights.length);

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          setTransitionEnabled(true);
          setIsAnimating(false);
        });
      });

      return;
    }

    setIsAnimating(false);
  };

  return (
    <section className="relative overflow-hidden bg-slate-950 py-20 sm:py-24">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-blue-950 to-indigo-950" />

      <div className="absolute left-[5%] top-[12%] h-64 w-64 rounded-full bg-cyan-400/10 blur-3xl" />

      <div className="absolute bottom-[5%] right-[5%] h-80 w-80 rounded-full bg-purple-500/10 blur-3xl" />

      {/* Animated particles */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <span className="absolute left-[8%] top-[20%] h-2 w-2 animate-pulse rounded-full bg-cyan-300 shadow-[0_0_18px_rgba(103,232,249,0.9)]" />

        <span className="absolute left-[18%] top-[72%] h-1.5 w-1.5 animate-pulse rounded-full bg-purple-300 shadow-[0_0_16px_rgba(216,180,254,0.9)] [animation-delay:300ms]" />

        <span className="absolute right-[12%] top-[24%] h-2.5 w-2.5 animate-pulse rounded-full bg-blue-300 shadow-[0_0_20px_rgba(147,197,253,0.9)] [animation-delay:500ms]" />

        <span className="absolute bottom-[15%] right-[25%] h-1.5 w-1.5 animate-pulse rounded-full bg-cyan-300 shadow-[0_0_16px_rgba(103,232,249,0.9)] [animation-delay:700ms]" />

        <span className="absolute left-[45%] top-[10%] h-1.5 w-1.5 animate-pulse rounded-full bg-yellow-200 shadow-[0_0_16px_rgba(254,240,138,0.9)] [animation-delay:900ms]" />
      </div>

      {/* Decorative waves */}
      <div className="absolute inset-0 opacity-20">
        <svg
          className="h-full w-full"
          viewBox="0 0 1600 700"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path
            d="M0 520 C220 380 390 630 620 470 C850 310 1040 600 1260 420 C1400 310 1510 340 1600 290"
            fill="none"
            stroke="#67e8f9"
            strokeWidth="2"
          />

          <path
            d="M0 590 C250 470 430 680 700 520 C970 360 1190 650 1600 430"
            fill="none"
            stroke="#a78bfa"
            strokeWidth="2"
          />
        </svg>
      </div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className="mb-10 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-200/20 bg-cyan-300/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-cyan-200 backdrop-blur-md">
              <span className="h-2 w-2 animate-pulse rounded-full bg-cyan-300" />

              Latest from Quantum Peeks
            </div>

            <h2 className="mt-5 text-3xl font-extrabold text-white sm:text-4xl md:text-5xl">
              Programme Highlights
            </h2>

            <p className="mt-4 max-w-2xl text-base leading-relaxed text-blue-100">
              Featured plenary lectures, programme announcements,
              and important updates from Quantum Peeks 2026.
            </p>
          </div>

          <Link
            to="/programme"
            className="group inline-flex w-fit items-center gap-2 rounded-full border border-cyan-200/30 bg-white/10 px-6 py-3 font-bold text-white shadow-lg backdrop-blur-md transition-all duration-300 hover:-translate-y-1 hover:border-cyan-200/60 hover:bg-white/20"
          >
            View Full Programme

            <span className="transition-transform duration-300 group-hover:translate-x-1">
              →
            </span>
          </Link>
        </div>

        {/* Circular carousel */}
        <div
          className="relative"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className="overflow-hidden rounded-[2rem] border border-white/15 bg-white/10 shadow-[0_30px_100px_rgba(0,0,0,0.38)] backdrop-blur-xl">
            {/* Moving carousel track */}
            <div
              className="flex"
              style={{
                transform: `translate3d(-${
                  slidePosition * 100
                }%, 0, 0)`,

                transition: transitionEnabled
                  ? `transform ${transitionDuration}ms cubic-bezier(0.22, 1, 0.36, 1)`
                  : "none",

                willChange: "transform",
              }}
              onTransitionEnd={handleTrackTransitionEnd}
            >
              {circularSlides.map((highlight, index) => {
                const isActiveInternalSlide =
                  index === slidePosition;

                return (
                  <article
                    key={`${highlight.title}-${index}`}
                    className="w-full shrink-0"
                  >
                    <div className="grid min-h-[500px] grid-cols-1 lg:grid-cols-[420px_1fr]">
                      {/* Image */}
                      <div className="relative min-h-[360px] overflow-hidden lg:min-h-full">
                        <img
                          src={highlight.image}
                          alt={highlight.speaker}
                          className={`h-full w-full object-cover object-top transition-transform duration-[6000ms] ease-out ${
                            isActiveInternalSlide
                              ? "scale-110"
                              : "scale-100"
                          }`}
                        />

                        <div
                          className={`absolute inset-0 bg-gradient-to-t ${highlight.accent} opacity-70`}
                        />

                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />

                        {/* Shine */}
                        <div
                          className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-[1800ms] ${
                            isActiveInternalSlide
                              ? "translate-x-full"
                              : "-translate-x-full"
                          }`}
                        />

                        <div className="absolute left-6 top-6">
                          <span
                            className={`inline-flex rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-wider shadow-lg ${highlight.badgeStyle}`}
                          >
                            {highlight.badge}
                          </span>
                        </div>

                        <div className="absolute bottom-6 left-6 right-6">
                          <p className="text-sm font-bold uppercase tracking-[0.18em] text-cyan-200">
                            {highlight.type}
                          </p>

                          <h3 className="mt-2 text-2xl font-extrabold text-white sm:text-3xl">
                            {highlight.speaker}
                          </h3>

                          <p className="mt-2 max-w-sm text-sm leading-relaxed text-blue-100">
                            {highlight.affiliation}
                          </p>
                        </div>
                      </div>

                      {/* Details */}
                      <div
                        className={`relative flex flex-col justify-center overflow-hidden bg-gradient-to-br ${highlight.accent} p-7 text-white sm:p-10 lg:p-12`}
                      >
                        {/* Slide count */}
                        <div className="absolute right-7 top-7 hidden items-center gap-2 text-sm font-bold text-white/60 sm:flex">
                          <span className="text-xl text-white">
                            {String(activeSlide + 1).padStart(
                              2,
                              "0"
                            )}
                          </span>

                          <span className="h-px w-8 bg-white/30" />

                          <span>
                            {String(highlights.length).padStart(
                              2,
                              "0"
                            )}
                          </span>
                        </div>

                        <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full border border-white/10" />

                        <div className="absolute -bottom-24 left-1/3 h-64 w-64 rounded-full bg-cyan-300/10 blur-3xl" />

                        <div className="pointer-events-none absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-cyan-300/10 blur-[90px]" />

                        <div className="relative">
                          {/* Date and time */}
                          <div className="mb-6 flex flex-wrap gap-3">
                            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-bold backdrop-blur-md">
                              <svg
                                className="h-4 w-4"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M8 7V3m8 4V3M5 11h14M5 5h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z"
                                />
                              </svg>

                              {highlight.date}
                            </span>

                            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-bold backdrop-blur-md">
                              <svg
                                className="h-4 w-4"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>

                              {highlight.time}
                            </span>
                          </div>

                          <p className="text-sm font-bold uppercase tracking-[0.24em] text-cyan-200">
                            {highlight.type}
                          </p>

                          <h3 className="mt-3 max-w-3xl text-3xl font-extrabold leading-tight sm:text-4xl">
                            {highlight.title}
                          </h3>

                          <p className="mt-6 max-w-3xl text-base leading-8 text-blue-100 sm:text-lg">
                            {highlight.description}
                          </p>

                          {/* Topic tags */}
                          {highlight.topics?.length > 0 && (
                            <div className="mt-6 flex flex-wrap gap-2">
                              {highlight.topics.map((topic) => (
                                <span
                                  key={topic}
                                  className="rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-semibold text-blue-100 backdrop-blur-md transition hover:bg-white/20"
                                >
                                  {topic}
                                </span>
                              ))}
                            </div>
                          )}

                          <div className="mt-8 flex flex-wrap items-center gap-4">
                            <Link
                              to="/programme"
                              className="group inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 font-bold text-blue-950 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                            >
                              Programme Details

                              <span className="transition-transform duration-300 group-hover:translate-x-1">
                                →
                              </span>
                            </Link>

                            <span className="text-sm font-semibold text-blue-200">
                              Quantum Peeks 2026 · IIT Mandi
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

          {/* Previous button */}
          <button
            type="button"
            onClick={moveToPreviousSlide}
            disabled={isAnimating}
            className="absolute left-3 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-slate-950/60 text-2xl text-white shadow-xl backdrop-blur-md transition-all duration-300 hover:scale-110 hover:bg-white hover:text-blue-950 disabled:cursor-wait disabled:opacity-60 sm:left-5"
            aria-label="Previous highlight"
          >
            ‹
          </button>

          {/* Next button */}
          <button
            type="button"
            onClick={moveToNextSlide}
            disabled={isAnimating}
            className="absolute right-3 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-slate-950/60 text-2xl text-white shadow-xl backdrop-blur-md transition-all duration-300 hover:scale-110 hover:bg-white hover:text-blue-950 disabled:cursor-wait disabled:opacity-60 sm:right-5"
            aria-label="Next highlight"
          >
            ›
          </button>
        </div>

        {/* Indicators */}
        <div className="mt-7 flex items-center justify-center gap-3">
          {highlights.map((highlight, index) => (
            <button
              key={`${highlight.title}-indicator`}
              type="button"
              onClick={() => goToSlide(index)}
              disabled={isAnimating}
              className={`h-2.5 rounded-full transition-all duration-300 disabled:cursor-wait ${
                activeSlide === index
                  ? "w-10 bg-cyan-300 shadow-[0_0_14px_rgba(103,232,249,0.75)]"
                  : "w-2.5 bg-white/30 hover:bg-white/60"
              }`}
              aria-label={`Show highlight ${index + 1}`}
              aria-current={
                activeSlide === index ? "true" : undefined
              }
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function SponsorshipSection() {
  return (
    <section
      id="sponsors"
      className="relative overflow-hidden bg-[#f8f6f2] py-32 text-slate-900"
    >
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden">

        {/* giant text */}
        <div className="absolute right-[-60px] top-8 hidden text-[240px] font-black tracking-[-0.08em] text-slate-200/40 lg:block">
          SPONSOR
        </div>

        {/* gradient blobs */}
        <div className="absolute -left-32 top-24 h-[420px] w-[420px] rounded-full bg-red-100 blur-3xl opacity-60" />

        <div className="absolute right-[-120px] bottom-[-100px] h-[360px] w-[360px] rounded-full bg-yellow-100 blur-3xl opacity-50" />

        {/* decorative line */}
        <div className="absolute left-10 top-24 h-32 w-[3px] bg-gradient-to-b from-red-700 via-red-500 to-transparent" />

      </div>

      <div className="relative mx-auto max-w-[1250px] px-6">

        {/* label */}
        <div className="mb-16 flex items-center gap-5">

          <span className="h-[3px] w-16 bg-red-700" />

          <p className="text-xs font-bold uppercase tracking-[0.38em] text-red-700">
            Sponsorship Opportunities
          </p>

        </div>

        <div className="grid gap-20 lg:grid-cols-[1.15fr_0.85fr]">

          {/* LEFT */}

          <div>

          <h2 className="font-serif text-5xl font-semibold leading-[1.02] tracking-[-0.05em] sm:text-6xl lg:text-[82px]">
          Become a
          <br />

          <span className="bg-gradient-to-r from-red-700 via-red-500 to-orange-500 bg-clip-text text-transparent italic">
            Sponsor
          </span>

          <br />

          of Quantum Peeks 2026
          </h2>

          <p className="mt-8 max-w-2xl text-xl leading-9 text-slate-600">
          A partnership opportunity for organizations committed to scientific research
          and academic exchange.
          </p>

          <div className="mt-10 h-[2px] w-36 bg-gradient-to-r from-red-700 via-red-400 to-transparent" />

          <p className="mt-10 max-w-xl text-xl leading-10 text-slate-600">
          Showcase your organization to researchers, faculty members,
          postdoctoral fellows, and students from leading institutions.
          Join us in supporting scientific exchange at the Indian Institute
          of Technology Mandi.
          </p>

          </div>

          {/* RIGHT */}

          <div className="space-y-8">

            {[
              {
                title: "Academic Visibility",
                desc: "Showcase your organization to researchers from premier institutions."
              },
              {
                title: "Meaningful Networking",
                desc: "Interact with faculty, industry experts and young scientists."
              },
              {
                title: "Support Scientific Research",
                desc: "Contribute towards advancing frontier research and innovation."
              }
            ].map((item) => (
              <div
                key={item.title}
                className="
                  group
                  rounded-3xl
                  border
                  border-slate-200
                  bg-white/80
                  backdrop-blur-xl
                  p-7
                  transition-all
                  duration-500
                  hover:-translate-y-2
                  hover:border-red-300
                  hover:shadow-2xl
                "
              >
                <div className="flex items-start gap-5">

                  <div
                    className="
                      flex
                      h-14
                      w-14
                      items-center
                      justify-center
                      rounded-full
                      bg-red-50
                      text-xl
                      text-red-700
                      transition
                      group-hover:scale-110
                    "
                  >
                    ✦
                  </div>

                  <div>

                    <h3 className="text-xl font-semibold">
                      {item.title}
                    </h3>

                    <p className="mt-2 leading-8 text-slate-600">
                      {item.desc}
                    </p>

                  </div>

                </div>
              </div>
            ))}

            <div className="pt-6">

              <a
                href="mailto:quantumpeeks2026@iitmandi.ac.in?subject=Sponsorship%20Enquiry"
                className="
                  group
                  inline-flex
                  items-center
                  gap-5
                  rounded-full
                  bg-gradient-to-r
                  from-red-700
                  to-red-500
                  px-9
                  py-5
                  font-semibold
                  text-white
                  shadow-xl
                  transition-all
                  duration-500
                  hover:scale-105
                  hover:shadow-red-300/40
                "
              >
                Request Sponsorship Details

                <span className="transition-transform duration-500 group-hover:translate-x-2">
                  →
                </span>
              </a>

              <p className="mt-7 text-sm tracking-[0.2em] text-slate-500">
                Or contact: quantumpeeks2026@iitmandi.ac.in
              </p>

            </div>

          </div>

        </div>

      </div>
    </section>
  );
}


// function ConferenceAnnouncementTicker() {
//   const announcements = [
//     {
//       label: "Now Open",
//       text: "Abstract submission is now open until 31 August 2026.",
//       link: "/abstract",
//       linkText: "Submit Abstract",
//       icon: "✦",
//       labelStyle:
//         "border-green-300/40 bg-green-400/15 text-green-200",
//     },
//     {
//       label: "Registration",
//       text: "Registration for Quantum Peeks 2026 is now open.",
//       link: "/registration",
//       linkText: "Register Now",
//       icon: "◆",
//       labelStyle:
//         "border-cyan-300/40 bg-cyan-400/15 text-cyan-200",
//     },
//     {
//       label: "Programme",
//       text: "The scientific programme will be announced shortly.",
//       link: "/programme",
//       linkText: "View Programme",
//       icon: "●",
//       labelStyle:
//         "border-purple-300/40 bg-purple-400/15 text-purple-200",
//     },
//     {
//       label: "Conference",
//       text: "Quantum Peeks 2026 will be held at IIT Mandi from 14–18 November 2026.",
//       link: "/programme",
//       linkText: "Learn More",
//       icon: "✧",
//       labelStyle:
//         "border-yellow-300/40 bg-yellow-400/15 text-yellow-200",
//     },
//   ];

//   return (
//     <section
//       className="relative z-30 overflow-hidden border-y border-cyan-200/20 bg-gradient-to-r from-slate-950 via-blue-950 to-indigo-950"
//       aria-label="Conference announcements"
//     >
//       {/* Background glow */}
//       <div className="pointer-events-none absolute inset-0">
//         <div className="absolute left-[12%] top-1/2 h-20 w-40 -translate-y-1/2 rounded-full bg-cyan-400/10 blur-2xl" />

//         <div className="absolute right-[18%] top-1/2 h-20 w-40 -translate-y-1/2 rounded-full bg-purple-400/10 blur-2xl" />
//       </div>

//       {/* Fixed left label */}
//       <div className="relative flex min-h-[66px] items-center">
//         <div className="relative z-20 flex min-h-[66px] shrink-0 items-center border-r border-white/15 bg-gradient-to-r from-blue-950 to-indigo-900 px-4 shadow-[12px_0_30px_rgba(2,6,23,0.75)] sm:px-6">
//           <div className="flex items-center gap-3">
//             <span className="relative flex h-3 w-3">
//               <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />

//               <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500" />
//             </span>

//             <div>
//               <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-cyan-200">
//                 Latest
//               </p>

//               <p className="text-sm font-extrabold text-white sm:text-base">
//                 Updates
//               </p>
//             </div>
//           </div>
//         </div>

//         {/* Moving announcement area */}
//         <div className="group relative flex-1 overflow-hidden">
//           {/* Left and right fade */}
//           <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-blue-950 to-transparent" />

//           <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-indigo-950 to-transparent" />

//           <div className="conference-ticker-track flex w-max items-center group-hover:[animation-play-state:paused]">
//             {/* First copy */}
//             <div className="flex shrink-0 items-center">
//               {announcements.map((announcement, index) => (
//                 <AnnouncementTickerItem
//                   key={`announcement-first-${index}`}
//                   announcement={announcement}
//                 />
//               ))}
//             </div>

//             {/* Duplicate copy for seamless movement */}
//             <div
//               className="flex shrink-0 items-center"
//               aria-hidden="true"
//             >
//               {announcements.map((announcement, index) => (
//                 <AnnouncementTickerItem
//                   key={`announcement-copy-${index}`}
//                   announcement={announcement}
//                 />
//               ))}
//             </div>
//           </div>
//         </div>
//       </div>
//     </section>
//   );
// }

function AnnouncementTickerItem({ announcement }) {
  return (
    <div className="flex min-h-[66px] shrink-0 items-center">
      <div className="flex items-center gap-4 px-6 sm:px-8">
        {/* Decorative icon */}
        <span className="text-lg text-cyan-300 drop-shadow-[0_0_10px_rgba(103,232,249,0.85)]">
          {announcement.icon}
        </span>

        {/* Status label */}
        <span
          className={`whitespace-nowrap rounded-full border px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.15em] ${announcement.labelStyle}`}
        >
          {announcement.label}
        </span>

        {/* Announcement text */}
        <p className="whitespace-nowrap text-sm font-semibold text-blue-50 sm:text-base">
          {announcement.text}
        </p>

        {/* Link */}
        <Link
          to={announcement.link}
          className="group/link inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-white/15 bg-white/10 px-4 py-1.5 text-xs font-bold text-cyan-100 transition hover:border-cyan-200/50 hover:bg-white/20 hover:text-white"
        >
          {announcement.linkText}

          <span className="transition-transform duration-300 group-hover/link:translate-x-1">
            →
          </span>
        </Link>
      </div>

      {/* Separator */}
      <div className="flex items-center gap-1 opacity-50">
        <span className="h-1 w-1 rounded-full bg-cyan-300" />
        <span className="h-1.5 w-1.5 rounded-full bg-purple-300" />
        <span className="h-1 w-1 rounded-full bg-blue-300" />
      </div>
    </div>
  );
}

function ConferenceAnnouncementTicker() {
  const announcements = [
    {
      label: "Open",
      // label: "Coming Soon",
      text: "Abstract submission is now open until 31 August 2026.",
      // text: "Abstract submission will open soon. Stay tuned for the official announcement.",
      link: "/abstract",
      linkText: "Submit Abstract",
      // linkText: "Learn More",
      icon: "✦",
      labelStyle:
        "border-green-300/40 bg-green-400/15 text-green-200",
    },
    // {
    //   label: "Registration",
    //   text: "Registration for Quantum Peeks 2026 is now open.",
    //   link: "/registration",
    //   linkText: "Register Now",
    //   icon: "◆",
    //   labelStyle:
    //     "border-cyan-300/40 bg-cyan-400/15 text-cyan-200",
    // },
    {
      label: "Programme",
      text: "The scientific programme will be announced shortly.",
      link: "/programme",
      linkText: "View Programme",
      icon: "●",
      labelStyle:
        "border-purple-300/40 bg-purple-400/15 text-purple-200",
    },
    {
      label: "Conference",
      text: "Quantum Peeks 2026 will be held at IIT Mandi from 14–18 November 2026.",
      link: "/programme",
      linkText: "Learn More",
      icon: "✧",
      labelStyle:
        "border-yellow-300/40 bg-yellow-400/15 text-yellow-200",
    },
  ];

  return (
    <section
      className="relative z-30 overflow-hidden border-y border-cyan-200/20 bg-gradient-to-r from-slate-950 via-blue-950 to-indigo-950"
      aria-label="Conference announcements"
    >
      {/* Background glows */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-[12%] top-1/2 h-20 w-40 -translate-y-1/2 rounded-full bg-cyan-400/10 blur-2xl" />

        <div className="absolute right-[18%] top-1/2 h-20 w-40 -translate-y-1/2 rounded-full bg-purple-400/10 blur-2xl" />
      </div>

      <div className="relative flex min-h-[66px] items-center">
        {/* Fixed left label */}
        <div className="relative z-20 flex min-h-[66px] shrink-0 items-center border-r border-white/15 bg-gradient-to-r from-blue-950 to-indigo-900 px-4 shadow-[12px_0_30px_rgba(2,6,23,0.75)] sm:px-6">
          <div className="flex items-center gap-3">
            <span className="relative flex h-3 w-3">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />

              <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500" />
            </span>

            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-cyan-200">
                Latest
              </p>

              <p className="text-sm font-extrabold text-white sm:text-base">
                News
              </p>
            </div>
          </div>
        </div>

        {/* Moving ticker */}
        <div className="group relative min-w-0 flex-1 overflow-hidden">
          {/* Edge fades */}
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-blue-950 to-transparent" />

          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-indigo-950 to-transparent" />

          <div className="conference-ticker-track flex w-max items-center group-hover:[animation-play-state:paused]">
            {/* First copy */}
            <div className="flex shrink-0 items-center">
              {announcements.map((announcement, index) => (
                <AnnouncementTickerItem
                  key={`announcement-first-${index}`}
                  announcement={announcement}
                />
              ))}
            </div>

            {/* Duplicate copy for seamless movement */}
            <div
              className="flex shrink-0 items-center"
              aria-hidden="true"
            >
              {announcements.map((announcement, index) => (
                <AnnouncementTickerItem
                  key={`announcement-second-${index}`}
                  announcement={announcement}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HomePage() {
  return (
    <>
      <Hero />
      <ConferenceAnnouncementTicker />
      <About />
      {/* <ConferenceHighlightsSection /> */}
      <ImportantDates />
      <VenueSection />
      <WeatherSection />
      <AttractionsSection />
      <SponsorshipSection/>
      <ContactSection />
    </>
  );
}

function SpeakerNetWave() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) return undefined;

    const ctx = canvas.getContext("2d", {
      alpha: true,
      desynchronized: true,
    });

    if (!ctx) return undefined;

    let animationFrameId;
    let resizeObserver;
    let width = 0;
    let height = 0;
    let time = 0;
    let lastFrameTime = 0;
    let isVisible = true;

    /*
     * Keep the canvas reasonably sharp without rendering
     * at an unnecessarily high device pixel ratio.
     */
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 1.5);

    /*
     * Roughly 30 FPS is more than enough for a slow mesh wave.
     */
    const frameInterval = 1000 / 30;

    /*
     * Performance settings.
     *
     * Increase these values for even better performance.
     */
    const rowSpacing = 13;
    const columnSpacing = 34;
    const horizontalStep = 14;
    const verticalStep = 11;
    const meshDepth = 115;

    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();

      const newWidth = Math.max(1, Math.round(rect.width));
      const newHeight = Math.max(1, Math.round(rect.height));

      if (newWidth === width && newHeight === height) return;

      width = newWidth;
      height = newHeight;

      canvas.width = Math.round(width * pixelRatio);
      canvas.height = Math.round(height * pixelRatio);

      ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    };

    /*
     * One projected surface only.
     *
     * It begins near the lower-left area and rises toward
     * the right side of the banner.
     */
    const projectPoint = (column, row, currentTime) => {
      const xProgress = Math.max(
        0,
        Math.min(1, column / Math.max(width, 1))
      );

      const riseFactor = Math.max(
        0,
        Math.min(1, xProgress + 0.2)
      );

      const smoothRise =
      riseFactor * riseFactor * (3 - 2 * riseFactor);

      const primaryWave =
        Math.sin(
          xProgress * Math.PI * 3.2 -
            currentTime * 0.7 +
            row * 0.012
        ) *
        (10 + smoothRise * 24);

      const secondaryWave =
        Math.sin(
          xProgress * Math.PI * 5.4 +
            currentTime * 0.38
        ) *
        (3 + smoothRise * 7);

      const depthWave =
        Math.sin(
          row * 0.05 +
            xProgress * Math.PI * 1.4 +
            currentTime * 0.45
        ) *
        (2 + smoothRise * 5);

      const baseline =
        height * 0.72 -
        smoothRise * height * 0.22;

      const perspective = 1 + row / 430;

      const projectedX =
        column +
        Math.sin(row * 0.035 + currentTime * 0.25) *
          (1.5 + smoothRise * 3.5);

      const projectedY =
        baseline +
        primaryWave +
        secondaryWave +
        depthWave +
        row * (0.34 + smoothRise * 0.12);

      return {
        x: projectedX,
        y:
          height * 0.5 +
          (projectedY - height * 0.5) / perspective,
      };
    };

    const drawBackgroundGlow = () => {
      const gradient = ctx.createRadialGradient(
        width * 0.76,
        height * 0.48,
        10,
        width * 0.76,
        height * 0.48,
        width * 0.52
      );

      gradient.addColorStop(0, "rgba(96, 165, 250, 0.10)");
      gradient.addColorStop(0.6, "rgba(147, 197, 253, 0.04)");
      gradient.addColorStop(1, "rgba(255, 255, 255, 0)");

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    };

    const drawMesh = () => {
      ctx.save();

      ctx.strokeStyle = "rgba(26, 86, 165, 0.30)";
      ctx.lineWidth = 0.8;

      /*
       * Horizontal mesh rows.
       */
      for (
        let row = -meshDepth;
        row <= meshDepth;
        row += rowSpacing
      ) {
        ctx.beginPath();

        let firstPoint = true;

        for (
          let column = -40;
          column <= width + 60;
          column += horizontalStep
        ) {
          const point = projectPoint(column, row, time);

          if (firstPoint) {
            ctx.moveTo(point.x, point.y);
            firstPoint = false;
          } else {
            ctx.lineTo(point.x, point.y);
          }
        }

        ctx.stroke();
      }

      /*
       * Vertical mesh connections.
       */
      for (
        let column = -20;
        column <= width + 40;
        column += columnSpacing
      ) {
        ctx.beginPath();

        let firstPoint = true;

        for (
          let row = -meshDepth;
          row <= meshDepth;
          row += verticalStep
        ) {
          const point = projectPoint(column, row, time);

          if (firstPoint) {
            ctx.moveTo(point.x, point.y);
            firstPoint = false;
          } else {
            ctx.lineTo(point.x, point.y);
          }
        }

        ctx.stroke();
      }

      ctx.restore();
    };

    const drawFrame = () => {
      ctx.clearRect(0, 0, width, height);

      drawBackgroundGlow();
      drawMesh();
    };

    const animate = (timestamp) => {
      if (!isVisible) {
        animationFrameId = window.requestAnimationFrame(animate);
        return;
      }

      const elapsed = timestamp - lastFrameTime;

      if (elapsed >= frameInterval) {
        lastFrameTime = timestamp - (elapsed % frameInterval);

        time += 0.018;

        drawFrame();
      }

      animationFrameId = window.requestAnimationFrame(animate);
    };

    /*
     * Stop rendering while the tab is hidden.
     */
    const handleVisibilityChange = () => {
      isVisible = !document.hidden;

      if (isVisible) {
        lastFrameTime = performance.now();
      }
    };

    /*
     * Stop rendering when the banner is outside the viewport.
     */
    const intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        isVisible = entry.isIntersecting && !document.hidden;
      },
      {
        threshold: 0.01,
      }
    );

    resizeCanvas();
    drawFrame();

    resizeObserver = new ResizeObserver(resizeCanvas);
    resizeObserver.observe(canvas);

    intersectionObserver.observe(canvas);

    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange
    );

    animationFrameId = window.requestAnimationFrame(animate);

    return () => {
      resizeObserver?.disconnect();
      intersectionObserver.disconnect();

      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );

      window.cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full"
    />
  );
}


function SpeakersPage() {
  const speakers = [
  {
    name: "Rohit Chikkareddy",
    institute: "University of Birmingham, UK",
    // image: "/speakers/Rohit_Chikkareddy.jpg", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
    image: publicPath("speakers/Rohit_Chikkareddy.jpg")
  },
  {
    name: "Sayan Choudhury",
    institute: "HRI, India",
    // image: "/speakers/Sayan_Choudhury.jpeg", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
    image: publicPath("speakers/Sayan_Choudhury.jpeg")
  },
  {
    name: "Paolo Comaron",
    institute: "CNR Nanotec, Italy",
    // image: "/speakers/Paolo_Comaron.jpeg", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
    image: publicPath("speakers/Paolo_Comaron.jpeg")
  },
  {
    name: "Franco Dalfovo",
    institute: "Pitaevskii BEC Center, Italy",
    // image: "/speakers/Franco_Dalfovo.jpg", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
    image: publicPath("speakers/Franco_Dalfovo.jpg")
  },
  {
    name: "Arnab Das",
    institute: "IACS, India",
    // image: "/speakers/Arnab_Das.jpeg", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
    image: publicPath("speakers/Arnab_Das.jpeg")
  },
  {
    name: "Adolfo del Campo",
    institute: "University of Luxembourg, Luxembourg",
    // image: "/speakers/Adolfo_del_Campo.jpeg", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
    image: publicPath("speakers/Adolfo_del_Campo.jpeg")
  },
  {
    name: "Sajal Dhara",
    institute: "IIT Kharagpur, India",
    // image: "/speakers/Sajal_Dhara.webp", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
    image: publicPath("speakers/Sajal_Dhara.webp")
  },
  {
    name: "Sriram Ganeshan",
    institute: "CUNY, USA",
    // image: "/speakers/Sriram_Ganeshan.jpeg", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
    image: publicPath("speakers/Sriram_Ganeshan.jpeg")
  },
  {
    name: "Sandeep Gautam",
    institute: "IIT Ropar, India",
    // image: "/speakers/Sandeep_Gautam.jpeg", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
    image: publicPath("speakers/Sandeep_Gautam.jpeg")
  },
  {
    name: "Jino George",
    institute: "IISER Mohali, India",
    // image: "/speakers/Jino_George.jpeg", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
    image: publicPath("speakers/Jino_George.jpeg")
  },
  {
    name: "Achanta Venu Gopal",
    institute: "TIFR, Mumbai",
    // image: "/speakers/Achanta_Venu_Gopal.jpeg", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
    image: publicPath("speakers/Achanta_Venu_Gopal.jpeg")
  },
  {
    name: "Sonjoy Majumder",
    institute: "IIT Kharagpur, India",
    // image: "/speakers/Sonjoy_Majumder.jpeg", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
    image: publicPath("speakers/Sonjoy_Majumder.jpeg")
  },
  {
    name: "Pankaj Mishra",
    institute: "IIT Guwahati, India",
    // image: "/speakers/Pankaj_Mishra.jpeg", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
    image: publicPath("speakers/Pankaj_Mishra.jpeg")
  },
  {
    name: "Tapan Mishra",
    institute: "NISER, India",
    // image: "/speakers/Tapan_Mishra.jpeg", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
    image: publicPath("speakers/Tapan_Mishra.jpeg")
  },
  {
    name: "Subroto Mukerjee",
    institute: "Indian Institute of Science, India",
    // image: "/speakers/Subroto_Mukerjee.jpeg", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
    image: publicPath("speakers/Subroto_Mukerjee.jpeg")
  },
  {
    name: "Sebabrata Mukherjee",
    institute: "Indian Institute of Science, India",
    // image: "/speakers/Sebabrata_Mukherjee.jpg", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
    image: publicPath("speakers/Sebabrata_Mukherjee.jpg")
  },
  {
    name: "Rejish Nath",
    institute: "IISER Pune, India",
    // image: "/speakers/Rejish_Nath.jpeg", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
    image: publicPath("speakers/Rejish_Nath.jpeg")
  },
  {
    name: "Sanjukta Roy",
    institute: "Raman Research Institute, India",
    // image: "/speakers/Sanjukta_Roy.png", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
    image: publicPath("speakers/Sanjukta_Roy.png")
  },
  {
    name: "Kush Saha",
    institute: "NISER, India",
    // image: "/speakers/Kush_Saha.jpg", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
    image: publicPath("speakers/Kush_Saha.jpg")
  },
  {
    name: "Angom Dilip Kumar Singh",
    institute: "Manipur University, India",
    // image: "/speakers/Angom_Dilip_Kumar_Singh.jpg", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
    image: publicPath("speakers/Angom_Dilip_Kumar_Singh.jpg")
  },
  {
    name: "Rakesh Kumar Singh",
    institute: "IIT BHU, India",
    // image: "/speakers/Rakesh_Kumar_Singh.jpeg", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
    image: publicPath("speakers/Rakesh_Kumar_Singh.jpeg")
  },
  {
    name: "Gopal Dixit",
    institute: "IIT Bombay, India",
    // image: "/speakers/Gopal_Dixit.png", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
    image: publicPath("speakers/Gopal_Dixit.png")
  },
  {
    name: "Rajesh Kushwaha",
    institute: "Physical Research Laboratory, India",
    // image: "/speakers/Rajesh_Kushwaha.jpg", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
    image: publicPath("speakers/Rajesh_Kushwaha.jpg")
  },
  {
    name: "Shubhadeep Biswas",
    institute: "Indian Institute of Science, India",
    // image: "/speakers/Shubhadeep_Biswas.jpg", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
    image: publicPath("speakers/Shubhadeep_Biswas.jpg")
 },
 {
  name: "Mithun Thudiyangal",
  institute: "Christ University, India",
  // image: "/speakers/Mithun_Thudiyangal.jpg", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
  image: publicPath("speakers/Mithun_Thudiyangal.jpg")
 },
 {
  name: "Himanshu Singhal",
  institute: "RRCAT, India",
  // image: "/speakers/Himanshu.jpg", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
  image: publicPath("speakers/Himanshu.jpg")
 },
 {
  name: "Smijesh",
  institute: "Raman Research Institute, India", 
  // image: "/speakers/Smijesh.jpg", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
  image: publicPath("speakers/Smijesh.jpg")
 },
 {
  name: "Stephan Fritzsche",
  institute: "Univ. Jena, Germany",
  // image: "/speakers/Stephan_Fritzsche.png", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
  image: publicPath("speakers/Stephan_Fritzsche.png")
 },
 {
  name: "P. C. Deshmukh",
  institute: "Central University of Kerala, India",
  // image: "/speakers/P_C_Deshmukh.jpg", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
  image: publicPath("speakers/P_C_Deshmukh.jpg")
 },
 {
  name: "Soumya Bera",
  institute: "IIT Bombay, India",
  // image: "/speakers/Soumya_Bera.jpg", // Comment this when using publicPath for IIT Mandi deployment and uncomment the line below
  image: publicPath("speakers/Soumya_Bera.jpg")
 },
 {
  name: "Arif Warsi Laskar",
  institute: "IIT Guwahati, India",
  image: publicPath("speakers/warsi.jpg")
 }



  ];

  return (
    <div>

{/* Speakers Banner */}
<section className="relative isolate mt-[90px] overflow-hidden border-b border-slate-200 bg-[#f7f3ea]">

  {/* Faint dotted texture */}
  <div
    aria-hidden="true"
    className="pointer-events-none absolute inset-0 opacity-[0.18]"
    style={{
      backgroundImage:
        "radial-gradient(rgba(16,61,102,0.34) 0.8px, transparent 0.8px)",
      backgroundSize: "12px 12px",
      maskImage:
        "linear-gradient(to right, black, transparent 30%, transparent 72%, black)",
      WebkitMaskImage:
        "linear-gradient(to right, black, transparent 30%, transparent 72%, black)",
    }}
  />

  {/* Large soft circular shape */}
  <div className="pointer-events-none absolute -left-[190px] top-1/2 h-[460px] w-[900px] -translate-y-1/2 rounded-[50%] border border-[#d7a900]/10 bg-white/15" />

  {/* Animated 3D net */}
  <SpeakerNetWave />

  {/* Protect heading from the mesh */}
  <div className="pointer-events-none absolute inset-y-0 left-0 w-[52%] bg-gradient-to-r from-[#f8f4eb] via-[#f8f4eb]/95 to-transparent" />


  {/* Main banner content */}
  <div className="relative z-10 mx-auto flex min-h-[185px] max-w-[1250px] items-center px-6 py-7 lg:px-8">

    <div className="max-w-[620px] translate-y-5">

      {/* Conference label */}
      <div className="flex items-center gap-4">

        <span className="h-[3px] w-14 bg-[#103d66]" />

        <span className="h-[9px] w-[9px] rounded-full bg-[#d7a900]" />

        <p className="text-[10px] font-bold uppercase tracking-[0.34em] text-[#103d66] sm:text-xs sm:tracking-[0.42em]">
          Quantum Peeks 2026
        </p>

      </div>

      {/* Heading */}
      <h1 className="mt-5 font-serif text-[44px] font-semibold leading-[0.88] tracking-[-0.055em] text-[#102c4e] sm:text-6xl lg:text-[72px]">

        Invited

        <span className="block italic font-normal text-[#d7a900] sm:inline sm:ml-3">
          Speakers
        </span>

      </h1>

    </div>

  </div>

  {/* Mobile footer */}
  <div className="relative z-10 flex items-center justify-between border-t border-[#d7a900]/70 bg-[#062e51] px-6 py-4 lg:hidden">

    <span className="text-[10px] font-bold uppercase tracking-[0.26em] text-yellow-300 sm:text-xs">
      IIT Mandi
    </span>

    <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white sm:text-xs sm:tracking-[0.25em]">
      November 14–18, 2026
    </span>

  </div>

</section>

<div className="max-w-[1400px] mx-auto px-6 pt-0 pb-24">
      {/* Back button */}
        <Link
          to="/"
          className="
            group mb-12 my-10 inline-flex items-center gap-3
            text-sm font-semibold text-slate-600
            transition-colors duration-300
            hover:text-blue-700
          "
        >
          <span className="transition-transform duration-300 group-hover:-translate-x-1">
            ←
          </span>
          Back to Home
        </Link>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-10 items-stretch">

          {speakers.map((s, i) => (

            <div key={i} className="group cursor-pointer overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 bg-white flex flex-col h-full">

              {/* Speaker image */}
            {/*  <div className="h-64 bg-gray-300 bg-cover bg-center transition-transform duration-500 group-hover:scale-110" /> */}
        <div className="h-64 overflow-hidden">
          <img
    src={s.image}
    alt={s.name}
    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
  />
</div>

              {/* Gradient info panel */}
      <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-blue-800 text-white p-5 flex flex-col justify-center flex-grow">
      <h2 className="text-xl font-bold mb-2">
        {s.name}
      </h2>

      <p className="text-sm text-gray-200 leading-relaxed">
        {s.institute}
      </p>

              </div>

            </div>

          ))}

        </div>

      </div>

    </div>
  );
}

function CommitteePage() {
  const convenors = [
    {
      name: "Arko Roy",
      institute: "IIT Mandi",
      // image: "/committee/Arko_Roy.jpg",
      image: publicPath("/committee/Arko_Roy.jpg"),
    },
    {
      name: "Hari Varma",
      institute: "IIT Mandi",
      // image: "/committee/Hari_varma.jpg",
      image: publicPath("/committee/Hari_varma.jpg"),
    },
  ];

  const localOrganizers = [
    {
      name: "Subhadeep Patra",
      institute: "IIT Mandi",
      // image: "/committee/Subhadeep.png",
      image: publicPath("/committee/Subhadeep.png"),
    },
    {
      name: "Sivasankar P.M",
      institute: "IIT Mandi",
      // image: "/committee/Sivasankar.jpeg",
      image: publicPath("/committee/Sivasankar.jpeg"),
    },
    {
      name: "Kartik Madan",
      institute: "IIT Mandi",
      // image: "/committee/Kartik.jpeg",
      image: publicPath("/committee/Kartik.jpeg"),
    },
    {
      name: "SunilKumar V",
      institute: "IIT Mandi",
      // image: "/committee/sunil.jpeg",
      image: publicPath("/committee/sunil.jpeg"),
    },
    {
      name: "Mahaveer",
      institute: "IIT Mandi",
      // image: "/committee/Mahaveer.jpg",
      image: publicPath("/committee/Mahaveer.jpg"),
    },
    {
      name: "Kuldeep Prajapat",
      institute: "IIT Mandi",
      // image: "/committee/Kuldeep.jpeg",
      image: publicPath("/committee/Kuldeep.jpeg"),
    },
    {
      name: "Shibshankar Karmakar",
      institute: "IIT Mandi",
      // image: "/committee/Shibshankar.jpeg",
      image: publicPath("/committee/Shibshankar.jpeg"),
    }


  ];

  return (
    <div>
    {/* Committee Banner */}
<section className="relative isolate mt-[90px] overflow-hidden border-b border-slate-200 bg-[#f7f3ea]">

  {/* Faint dotted texture */}
  <div
    aria-hidden="true"
    className="pointer-events-none absolute inset-0 opacity-[0.18]"
    style={{
      backgroundImage:
        "radial-gradient(rgba(16,61,102,0.34) 0.8px, transparent 0.8px)",
      backgroundSize: "12px 12px",
      maskImage:
        "linear-gradient(to right, black, transparent 30%, transparent 72%, black)",
      WebkitMaskImage:
        "linear-gradient(to right, black, transparent 30%, transparent 72%, black)",
    }}
  />

  {/* Large soft circular shape */}
  <div className="pointer-events-none absolute -left-[190px] top-1/2 h-[460px] w-[900px] -translate-y-1/2 rounded-[50%] border border-[#d7a900]/10 bg-white/15" />

  {/* Animated 3D net */}
  <SpeakerNetWave />

  {/* Protect heading from the mesh */}
  <div className="pointer-events-none absolute inset-y-0 left-0 w-[52%] bg-gradient-to-r from-[#f8f4eb] via-[#f8f4eb]/95 to-transparent" />


  {/* Main banner content */}
  <div className="relative z-10 mx-auto flex min-h-[185px] max-w-[1250px] items-center px-6 py-7 lg:px-8">

    <div className="max-w-[620px] translate-y-5">

      {/* Conference label */}
      <div className="flex items-center gap-4">

        <span className="h-[3px] w-14 bg-[#103d66]" />

        <span className="h-[9px] w-[9px] rounded-full bg-[#d7a900]" />

        <p className="text-[10px] font-bold uppercase tracking-[0.34em] text-[#103d66] sm:text-xs sm:tracking-[0.42em]">
          Quantum Peeks 2026
        </p>

      </div>

      {/* Heading */}
      <h1 className="mt-5 font-serif text-[44px] font-semibold leading-[0.88] tracking-[-0.055em] text-[#102c4e] sm:text-6xl lg:text-[72px]">

        Organizing

        <span className="block italic font-normal text-[#d7a900] sm:inline sm:ml-3">
          Committee
        </span>

      </h1>

    </div>

  </div>

  {/* Mobile footer */}
  <div className="relative z-10 flex items-center justify-between border-t border-[#d7a900]/70 bg-[#062e51] px-6 py-4 lg:hidden">

    <span className="text-[10px] font-bold uppercase tracking-[0.26em] text-yellow-300 sm:text-xs">
      IIT Mandi
    </span>

    <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white sm:text-xs sm:tracking-[0.25em]">
      November 14–18, 2026
    </span>

  </div>

</section>

<div className="max-w-[1400px] mx-auto px-6 pt-0 pb-24">
      {/* Back button */}
        <Link
          to="/"
          className="
            group mb-12 my-10 inline-flex items-center gap-3
            text-sm font-semibold text-slate-600
            transition-colors duration-300
            hover:text-blue-700
          "
        >
          <span className="transition-transform duration-300 group-hover:-translate-x-1">
            ←
          </span>
          Back to Home
        </Link>

        {/* Convenors */}
        <h2 className="text-3xl font-bold text-blue-900 mb-8">
          Convenors
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-10 mb-20">
        {convenors.map((member, i) => (
          <div
            key={i}
            className="group overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 bg-white flex flex-col h-full"          
          >
            <div className="h-64 overflow-hidden">
              <img
                src={member.image}
                alt={member.name}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
            </div>
        
            <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-blue-800 text-white p-5 flex flex-col justify-center flex-grow">
              <h3 className="text-xl font-bold mb-2">
                {member.name}
              </h3>

              <p className="text-sm text-gray-200 leading-relaxed">
                {member.institute}
              </p>
            </div>
          </div>
        ))}
                </div>
        
                {/* Local Organizers */}
                <h2 className="text-3xl font-bold text-blue-900 mb-8">
                  Local Organizers
                </h2>
        
                <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-10">
        {localOrganizers.map((member, i) => (
          <div
            key={i}
            className="group overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 bg-white flex flex-col h-full"
          >
          <div className="h-64 overflow-hidden">
              <img
                src={member.image}
                alt={member.name}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
            </div>
        
            <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-blue-800 text-white p-5 flex flex-col justify-center flex-grow">
              <h3 className="text-xl font-bold mb-2">
                {member.name}
              </h3>

              <p className="text-sm text-gray-200 leading-relaxed">
                {member.institute}
              </p>
            </div>
          </div>
        ))}
                </div>
              </div>
            </div>
          );
        }


function RegistrationPage() { 
// Change it when you want to close the registration. You can also uncomment the code below to display a "Registration Closed" message instead of the registration form.
  const registrationOpen = false; // Change to false to display the "Registration Closed" message

// This is important to declaire
  // if (!registrationOpen) {
  //   return (
  //     <div className="py-24 max-w-[1100px] mx-auto px-6 text-center">
  //       <h1 className="text-4xl font-bold mb-6">Registration Closed</h1>
  //       <p className="text-lg text-gray-700">
  //         We regret to inform you that the registration for QuantumPeeks 2026 is now closed. 
  //         Thank you for your interest in participating in our conference.
  //       </p>
  //     </div>
  //   );linkText: "Learn More",
  // }
  return (
    <div className="py-32 max-w-[1100px] mx-auto px-6">
        <Link
          to="/"
          className="
            group mb-12 my-10 inline-flex items-center gap-3
            text-sm font-semibold text-slate-600
            transition-colors duration-300
            hover:text-blue-700
          "
        >
          <span className="transition-transform duration-300 group-hover:-translate-x-1">
            ←
          </span>
          Back to Home
        </Link>
      <h1 className="text-4xl font-bold mb-6">Registration</h1>

      <p className="text-lg text-gray-700 mb-10">
        Please follow the two-step process below to complete your conference
        registration. Participants must first pay the registration fee and
        then submit the registration form along with the payment receipt.
      </p>

      <div className="mb-16">
        <div className="bg-blue-900 text-white p-6 mb-8 rounded-lg">
          <h2 className="text-2xl font-bold">
            Step 1: Pay the Registration Fee
          </h2>
        </div>

        <h3 className="text-xl font-bold mb-6">Registration Fees</h3>

        {/* Participants */}
        <div className="mb-12">
          <h4 className="text-lg font-semibold mb-3 text-blue-800">
            Participants
          </h4>

          <div className="overflow-x-auto rounded-lg border border-gray-300">
            <table className="w-full text-left border-collapse">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-4 border border-gray-300">
                    Category
                  </th>

                  <th className="p-4 border border-gray-300">
                    Early Registration
                  </th>

                  <th className="p-4 border border-gray-300">
                    Late Registration
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr className="hover:bg-gray-50">
                  <td className="p-4 border border-gray-300 font-medium">
                    PhDs
                  </td>

                  <td className="p-4 border border-gray-300">
                    ₹5,000
                  </td>

                  <td className="p-4 border border-gray-300">
                    ₹6,250
                  </td>
                </tr>
                
                <tr className="hover:bg-gray-50">
                  <td className="p-4 border border-gray-300 font-medium">
                    Postdocs/RAs
                  </td>

                  <td className="p-4 border border-gray-300">
                    ₹7,500
                  </td>

                  <td className="p-4 border border-gray-300">
                    ₹9,375
                  </td>
                </tr>  

                <tr className="hover:bg-gray-50">
                  <td className="p-4 border border-gray-300 font-medium">
                    Faculty / Scientists 
                  </td>

                  <td className="p-4 border border-gray-300">
                    ₹10,000
                  </td>

                  <td className="p-4 border border-gray-300">
                    ₹12,500
                  </td>
                </tr>

                <tr className="hover:bg-gray-50">
                  <td className="p-4 border border-gray-300 font-medium">
                    Industry Personnel
                  </td>

                  <td className="p-4 border border-gray-300">
                    ₹10,000
                  </td>

                  <td className="p-4 border border-gray-300">
                    ₹12,500
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-sm text-gray-500 mt-4">
            Early-registration fees apply until the early-registration
            deadline. Late-registration fees apply after that date.
          </p>
        </div>

        {/* Payment Method */}
        <h3 className="text-xl font-bold mb-4">Payment Method</h3>

        <div className="bg-gray-100 border border-gray-200 p-6 md:p-8 rounded-lg shadow-sm">
          <h4 className="text-lg font-semibold text-blue-800 mb-6">
            Bank Transfer
          </h4>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-5 text-gray-700">
            <div>
              <p className="font-semibold text-gray-900">
                Account Name
              </p>
              <p>IIT MANDI SBI CCE</p>
            </div>


            <div>
              <p className="font-semibold text-gray-900">
                Branch
              </p>
              <p>Kamand, IIT Mandi, VPO kamand, IIT Mandi North Campus, Sadar Mandi, Himachal Pradesh,Pin - 175005</p>
            </div>

            <div>
              <p className="font-semibold text-gray-900">
                Account Number
              </p>
              <p>44738807305</p>
            </div>

            <div>
              <p className="font-semibold text-gray-900">
                IFSC Code
              </p>
              <p>SBIN0013711</p>
            </div>

            <div>
              <p className="font-semibold text-gray-900">
                Bank Name
              </p>
              <p>State Bank of India</p>
            </div>
          </div>

          <div className="mt-8 p-4 bg-yellow-50 border-l-4 border-yellow-500 text-gray-700">
            <p>
              <strong>Important:</strong> Please mention your full name and
              “Quantum Peeks 2026” in the transaction remarks. Keep the payment
              receipt or transaction confirmation, as it must be uploaded
              during registration.
            </p>
          </div>
        </div>
      </div>


      <div>
        <div className="bg-blue-900 text-white p-6 mb-8">
          <h2 className="text-2xl font-bold">Step 2 : Fill Registration Form and Upload Receipt</h2>
        </div>

        <p className="text-lg text-gray-700 mb-6">
          After completing the payment, please fill out the registration
          form and upload the payment receipt or transaction screenshot
          for verification.
        </p>

        <div className="bg-gray-100 p-8 mb-8">
          <p className="mb-4 font-semibold">The registration form will require:</p>

          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>Full name</li>
            <li>Institution / affiliation</li>
            <li>Email address</li>
            <li>Category (student / academic / industry)</li>
            <li>Abstract submission ID (if applicable)</li>
            <li>Upload of payment receipt</li>
          </ul>
        </div>
        <div className="text-center mt-10">

{registrationOpen ? (
  <Link
    to="/registration-form"
    className="group inline-block px-10 py-3 font-semibold text-white rounded-md bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-600 transition-all duration-500 shadow-md hover:shadow-2xl hover:scale-105 relative overflow-hidden"
  >
    <span className="relative z-10">Register Now</span>

    <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-0 group-hover:opacity-100 -translate-x-full group-hover:translate-x-full transition-all duration-700" />

    <span className="absolute inset-0 rounded-md opacity-0 group-hover:opacity-100 bg-blue-400/20 blur-md transition-opacity duration-500" />
  </Link>
) : (
  <button
    type="button"
    disabled
    className="px-10 py-3 font-semibold rounded-md bg-gray-400 text-white cursor-not-allowed"
  >
    Register Now
  </button>
)}

{!registrationOpen && (
  <p className="mt-4 text-red-600 font-semibold">
    Registration has not started yet.
  </p>
)}

</div>
      </div>

    </div>
  );
}

function ReceiptUploader({ onFileReady }) {
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [previewURL, setPreviewURL] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const formatSize = (bytes) => {
    if (!bytes) return "0 KB";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
  };

  const handleFile = (f) => {
    if (!f) return;
  
    setFile(f);
    setProgress(0);
    setUploaded(false);
    setUploading(false);
  
    // The selected file is not ready until Upload is completed.
    if (onFileReady) {
      onFileReady(null);
    }
  
    try {
      if (
        f.type.startsWith("image/") ||
        f.type === "application/pdf"
      ) {
        const url = URL.createObjectURL(f);
        setPreviewURL(url);
      } else {
        setPreviewURL(null);
      }
    } catch (err) {
      console.error("Preview creation failed:", err);
      setPreviewURL(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    handleFile(f);
  };

  const startUpload = () => {
    if (!file || uploading || uploaded) return;
  
    setUploading(true);
    setProgress(0);
  
    const interval = setInterval(() => {
      setProgress((previousProgress) => {
        const nextProgress = previousProgress + 10;
  
        if (nextProgress >= 100) {
          clearInterval(interval);
  
          setUploading(false);
          setUploaded(true);
  
          // Only now send the file to the parent form.
          if (onFileReady) {
            onFileReady(file);
          }
  
          return 100;
        }
  
        return nextProgress;
      });
    }, 200);
  };

  const removeFile = () => {
    if (previewURL) {
      URL.revokeObjectURL(previewURL);
    }
  
    setFile(null);
    setProgress(0);
    setUploading(false);
    setUploaded(false);
    setPreviewURL(null);
    setShowPreview(false);
  
    // Clear the file in the parent registration form.
    if (onFileReady) {
      onFileReady(null);
    }
  };

  const uploadedSize = file ? (file.size * progress) / 100 : 0;
  // const [receiptFile, setReceiptFile] = useState(null);
  // const [receiptReady, setReceiptReady] = useState(false);

  return (
    <div className="space-y-4">

      {!file && (
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition"
        >
          <label className="flex flex-col items-center cursor-pointer">
            <UploadCloud size={42} className="text-blue-600 mb-2" />
            <p className="text-sm font-medium text-gray-700">
              Click or drag file to upload receipt
            </p>
            <p className="text-xs text-gray-400">PNG, JPG or PDF</p>

            <input
              type="file"
              className="hidden"
              onChange={(e) => handleFile(e.target.files[0])}
            />
          </label>
        </div>
      )}

      {file && (
        <div className="bg-gray-50 border rounded-lg p-4">
          <div className="flex items-center justify-between gap-4">

            <div className="flex items-center gap-3 flex-1">
              <UploadCloud size={20} className="text-blue-600" />

              <div className="flex flex-col flex-1">

                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-700 truncate max-w-[220px]">
                    {file.name}
                  </span>

                  {previewURL && uploaded && (
                    <button
                      type="button"
                      onClick={() => setShowPreview(true)}
                      className="text-xs px-2 py-0.5 border rounded hover:bg-gray-100"
                    >
                      Preview
                    </button>
                  )}

                  {uploading && (
                    <div className="w-28 h-2 bg-gray-200 rounded overflow-hidden">
                      <div
                        className="h-2 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  )}
                </div>

                {!uploaded && (
                  <span className="text-xs text-gray-500 mt-1">
                    {formatSize(uploadedSize)} / {formatSize(file.size)}
                  </span>
                )}

                {uploaded && (
                  <span className="text-xs text-green-600 mt-1">
                    Uploaded • {formatSize(file.size)}
                  </span>
                )}

              </div>
            </div>

            <div className="flex items-center gap-2">

              {!uploaded && (
                <button
                  type="button"
                  onClick={startUpload}
                  disabled={uploading}
                  className="px-4 py-1.5 text-xs font-semibold text-white rounded bg-blue-600 hover:bg-blue-700"
                >
                  {uploading ? "Uploading" : "Upload"}
                </button>
              )}

              <button
                type="button"
                onClick={removeFile}
                className="flex items-center gap-1 px-2 py-1 text-xs rounded border hover:bg-red-50 text-red-600"
              >
                <Trash2 size={14} />
              </button>

            </div>

          </div>
        </div>
      )}

      {showPreview && previewURL && file && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full p-4 relative">

            <button
              type="button"
              onClick={() => setShowPreview(false)}
              className="absolute top-2 right-2 text-sm px-2 py-1 border rounded hover:bg-gray-100"
            >
              Close
            </button>

            {file.type.startsWith("image/") && (
              <img src={previewURL} alt="receipt preview" className="max-h-[70vh] mx-auto" />
            )}

            {file.type === "application/pdf" && (
              <iframe src={previewURL} title="PDF preview" className="w-full h-[70vh]" />
            )}

          </div>
        </div>
      )}

    </div>
  );
}

function RegistrationFormPage() {
  const [receiptFile, setReceiptFile] = useState(null);
  // const [formData, setFormData] = useState({
  //   name: "",
  //   institution: "",
  //   email: "",
  //   category: "PhD",
  //   abstractId: ""
  // });

  const [receiptResetKey, setReceiptResetKey] = useState(0);
  const [submitStatus, setSubmitStatus] = useState("idle");
  // idle | submitting | success
  
  const GOOGLE_SCRIPT_URL =
  "https://script.google.com/macros/s/AKfycbwQHlee4S_14Ano8hp8PkMzLEOIwDfUNpfXKAysz6BGXUWO8OWQKjy3bHwTK7bOq-A/exec";

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
  
      reader.onload = () => {
        const result = reader.result;
        resolve(result.split(",")[1]);
      };
  
      reader.onerror = () => {
        reject(new Error("Unable to read the payment receipt."));
      };
  
      reader.readAsDataURL(file);
    });
  };

  const [formData, setFormData] = useState({
    title: "Mr.",
    name: "",
    institution: "",
    email: "",
    category: "PhD",
    preference: "Oral Presentation",
    invitedSpeaker: "No",
    abstractId: "",
  });
  
  const handleSubmit = async (e) => {
    e.preventDefault();
  
    // alert("Registration submission is not active yet.");
    // return;
    setSubmitStatus("submitting");
  
    
    try {
      if (!receiptFile) {
        throw new Error("Please select the receipt and click Upload before submitting.");
      }
  
      const receiptBase64 = await fileToBase64(receiptFile);
  
      const payload = {
        title: formData.title,
        name: formData.name,
        institution: formData.institution,
        email: formData.email,
        category: formData.category,
        invitedSpeaker: formData.invitedSpeaker,
        abstractId: formData.abstractId,
        timestamp: new Date().toISOString(),
        receiptName: receiptFile.name,
        receiptType: receiptFile.type,
        receiptBase64,
      };
  
      const response = await fetch(GOOGLE_SCRIPT_URL, {
        method: "POST",
        body: JSON.stringify(payload),
      });
  
      const text = await response.text();
      const result = JSON.parse(text);
  
      if (!result.success) {
        throw new Error(
          result.message || "Submission failed."
        );
      }
  
      setSubmitStatus("success");

      setTimeout(() => {
        setSubmitStatus("idle");
      }, 15000);
  
      setFormData({
        title: "Mr.",
        name: "",
        institution: "",
        email: "",
        category: "PhD",
        invitedSpeaker: "No",
        abstractId: "",
      });
  
      setReceiptFile(null);
      setReceiptResetKey((prev) => prev + 1); 
    } catch (err) {
      console.error("Submission error:", err);
  
      setSubmitStatus("error");
  
      setTimeout(() => {
        setSubmitStatus("idle");
      }, 15000);
    }
    
  };


  return (
    <div className="py-32 max-w-[700px] mx-auto px-6">
    <Link to="/registration" className="inline-block mb-6 text-blue-600 font-semibold hover:underline">
      ← Registration
    </Link>

    <Link to="/" className="inline-block mb-6 ml-6 text-blue-600 font-semibold hover:underline">
      ← Home
    </Link>

      <h1 className="text-4xl font-bold mb-8 text-center">Registration Form</h1>

      <form onSubmit={handleSubmit} className="space-y-5 bg-white border rounded-lg p-8 shadow">
      <div>
        <label className="block font-semibold mb-1">Title</label>
        <select
          name="title"
          value={formData.title}
          onChange={handleChange}
          className="w-full border rounded p-3"
        >
          <option>Mr.</option>
          <option>Ms.</option>
          <option>Mrs.</option>
          <option>Dr.</option>
          <option>Prof.</option>
        </select>
      </div>
        <div>
          <label className="block font-semibold mb-1">Full Name</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="w-full border rounded p-3"
            placeholder="Enter your full name"
            required
          />
        </div>

        <div>
          <label className="block font-semibold mb-1">Institution / Affiliation</label>
          <input
            type="text"
            name="institution"
            value={formData.institution}
            onChange={handleChange}
            className="w-full border rounded p-3"
            placeholder="University / Institute"
            required
          />
        </div>

        <div>
          <label className="block font-semibold mb-1">Email Address</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="w-full border rounded p-3"
            placeholder="your@email.com"
            required
          />
        </div>

        <div>
          <label className="block font-semibold mb-1">Category</label>
          <select
            name="category"
            value={formData.category}
            onChange={handleChange}
            className="w-full border rounded p-3"
          >
          <option>PhD</option>
          <option>Postdoctoral Researcher</option>
          <option>Research Associate (RA)</option>
          <option>Faculty / Scientist</option>
          <option>Industry</option>
          </select>
        </div>

        <div>
        <label className="block font-semibold mb-3">
          Are you an Invited Speaker?
        </label>

        <div className="flex gap-8">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="invitedSpeaker"
              value="Yes"
              checked={formData.invitedSpeaker === "Yes"}
              onChange={handleChange}
              className="h-4 w-4 accent-blue-600"
              required
            />
            <span>Yes</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="invitedSpeaker"
              value="No"
              checked={formData.invitedSpeaker === "No"}
              onChange={handleChange}
              className="h-4 w-4 accent-blue-600"
              required
            />
            <span>No</span>
          </label>
        </div>
      </div>


        <div>
          <label className="block font-semibold mb-1">Abstract Submission ID (optional)</label>
          <input
            type="text"
            name="abstractId"
            value={formData.abstractId}
            onChange={handleChange}
            className="w-full border rounded p-3"
          />
        </div>

        <div>
        <label className="block font-semibold mb-3">
          Upload Payment Receipt
        </label>

        <ReceiptUploader
          key={receiptResetKey}
          onFileReady={setReceiptFile}
        />
        </div>

        <div className="text-center pt-4">
        {submitStatus === "success" ? (
          <div className="inline-flex items-center justify-center px-8 py-3 rounded-md bg-green-100 border border-green-500 text-green-700 font-semibold transition-all duration-300">
            ✓ Registration submitted successfully!
          </div>
        ) : submitStatus === "error" ? (
          <div className="inline-flex items-center justify-center px-8 py-3 rounded-md bg-red-100 border border-red-500 text-red-700 font-semibold transition-all duration-300">
            Please click Upload before submitting.
          </div>
        ) : (
          <button
            type="submit"
            disabled={submitStatus === "submitting"}
            className="group px-10 py-3 font-semibold text-white rounded-md bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-600 transition-all duration-500 shadow-md hover:shadow-2xl hover:scale-105 relative overflow-hidden disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            <span className="relative z-10">
              {submitStatus === "submitting"
                ? "Submitting..."
                : "Submit Registration"}
            </span>
          </button>
        )}
        </div>
      </form>
    </div>
  );
}

// function AnnouncementsPage() {
//   const announcements = [
//     // { title: "Abstract submission is now open", date: "March 2026" },
//     // { title: "Invited speakers list updated", date: "April 2026" },
//     // { title: "Registration portal is live", date: "May 2026" },
//     // { title: "Travel grants announced", date: "June 2026" }
//   ];

//   return (
//     <div className="pt-32 pb-24 max-w-[1000px] mx-auto px-6">
//       <Link to="/" className="inline-block mb-6 text-blue-600 font-semibold hover:underline">← Back to Home</Link>

//       <h1 className="text-4xl font-bold mb-20">Announcements</h1>

//       <div className="space-y-6">
//         {announcements.map((a, i) => (
//           <div key={i} className="p-6 border rounded-lg shadow-sm hover:shadow-md transition">
//             <p className="text-sm text-gray-500 mb-1">{a.date}</p>
//             <h3 className="text-lg font-semibold">{a.title}</h3>
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// }

function AnnouncementsPage() {
  // We need to add here for further announcements
  const announcements = [
    {
      number: "01",
      date: "20 JUL 2026",
      title: "Quantum Peeks 2026 announced",
      description:
        "Quantum Peeks 2026 will be held from 14–18 November 2026 at the Indian Institute of Technology Mandi. Further information regarding invited speakers, abstract submission, registration, accommodation, travel, and the scientific programme will be announced progressively.",
      status: "Official Notice",
    },

  {
    number: "02",
    date: "24 JUL 2026",
    title: "Invited Speakers Announced",
    description:
      "The list of invited speakers for Quantum Peeks 2026 has been released. Please visit the Invited Speakers page for the latest updates.",
    status: "Speakers",
  },

  {
    number: "03",
    date: "24 JUL 2026",
    title: "Abstract Submission Now Open",
    description:
      "Abstract submission for Quantum Peeks 2026 is now open. Researchers and students are invited to submit their abstracts for oral or poster presentations. The submission portal will remain open until 31 August 2026. Participants are encouraged to complete their submissions before the deadline.",
    status: "Abstract Submission",
  },
];

  const upcomingUpdates = [
    "Registration",
    "Scientific programme",
  ];

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#f3f1ec] pt-32 pb-24 text-slate-900">
      {/* Background decoration */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -right-20 top-20 select-none text-[120px] font-black tracking-[-0.08em] text-slate-200/40 sm:text-[180px] lg:text-[240px]">
          NEWS
        </div>

        <div className="absolute -left-40 top-[420px] h-[420px] w-[420px] rounded-full bg-blue-300/10 blur-3xl" />

        <div className="absolute -right-40 bottom-20 h-[420px] w-[420px] rounded-full bg-cyan-300/10 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-[1280px] px-8">
        {/* Back button */}
        <Link
          to="/"
          className="
            group mb-12 my-10 inline-flex items-center gap-3
            text-sm font-semibold text-slate-600
            transition-colors duration-300
            hover:text-blue-700
          "
        >
          <span className="transition-transform duration-300 group-hover:-translate-x-1">
            ←
          </span>
          Back to Home
        </Link>

        {/* Header */}
        <section className="grid gap-10 border-b border-slate-300 pb-14 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
          <div>
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.34em] text-blue-700">
              Quantum Peeks 2026
            </p>

            <h1 className="max-w-4xl text-5xl font-bold leading-[0.94] tracking-[-0.045em] sm:text-6xl lg:text-7xl">
              Conference
              <span className="block text-slate-400">Bulletin</span>
            </h1>
          </div>

          <div className="lg:pb-2">
            <p className="max-w-md text-base leading-7 text-slate-600">
              Official updates concerning the conference, participation,
              scientific programme, and related activities will be published
              on this page.
            </p>
          <div className="mt-6 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />

            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.5)]" />
          </span>

          Bulletin active
          </div>
          </div>
        </section>

        {/* Announcements */}
        <section className="divide-y divide-slate-300 border-b border-slate-300">
          {announcements.map((announcement) => (
            <article
              key={announcement.number}
              className="relative grid overflow-hidden lg:grid-cols-[190px_1fr]"
            >
              {/* Accent line */}
              <div className="absolute left-0 top-0 h-full w-1.5 bg-gradient-to-b from-blue-700 via-cyan-500 to-sky-400" />

              {/* Number column */}
              <div
                className="
                  relative border-b border-slate-300
                  py-10 pl-6
                  lg:border-b-0 lg:border-r lg:py-16 lg:pl-8
                "
              >
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-slate-500">
                  Notice
                </p>

                <p className="mt-3 text-7xl font-bold tracking-[-0.07em] text-blue-700 sm:text-8xl">
                  {announcement.number}
                </p>

                <div className="mt-6 hidden lg:block">
                  <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400">
                    Published
                  </p>

                  <p className="mt-2 text-sm font-semibold text-slate-600">
                    {announcement.date}
                  </p>
                </div>
              </div>

              {/* Content column */}
              <div className="relative overflow-hidden py-10 pl-6 pr-2 sm:pl-10 lg:px-14 lg:py-16">
                {/* Large faded number */}
                <div className="pointer-events-none absolute -right-4 -top-8 select-none text-[150px] font-black tracking-[-0.08em] text-blue-700/[0.045] sm:text-[210px]">
                  {announcement.number}
                </div>

                {/* Scientific wave decoration */}
                <svg
                  className="
                    pointer-events-none absolute
                    -bottom-8 right-[-50px]
                    h-52 w-[560px]
                    opacity-35
                  "
                  viewBox="0 0 600 220"
                  fill="none"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M0 112 C55 20, 115 198, 175 108 S300 20, 365 112 S495 198, 600 108"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="text-blue-500"
                  />

                  <path
                    d="M0 132 C70 62, 120 182, 195 122 S325 62, 395 128 S520 180, 600 126"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    className="text-cyan-500"
                  />

                  <path
                    d="M0 90 C65 45, 125 150, 205 90 S350 40, 420 92 S530 150, 600 88"
                    stroke="currentColor"
                    strokeWidth="1"
                    className="text-sky-400"
                  />

                  <path
                    d="M0 152 C80 98, 150 175, 225 140 S370 95, 445 142 S535 170, 600 148"
                    stroke="currentColor"
                    strokeWidth="1"
                    className="text-violet-400"
                  />

                  <circle
                    cx="175"
                    cy="108"
                    r="5"
                    fill="currentColor"
                    className="text-blue-700"
                  />

                  <circle
                    cx="365"
                    cy="112"
                    r="5"
                    fill="currentColor"
                    className="text-cyan-600"
                  />
                </svg>

                <div className="relative z-10 max-w-3xl">
                  <div className="flex flex-wrap items-center gap-4">
                    <span
                      className="
                        border border-blue-700/20
                        bg-blue-50
                        px-3 py-1
                        text-[10px] font-bold
                        uppercase tracking-[0.22em]
                        text-blue-700
                      "
                    >
                      {announcement.status}
                    </span>

                    <span className="text-xs font-bold uppercase tracking-[0.22em] text-slate-500 lg:hidden">
                      {announcement.date}
                    </span>
                  </div>

                  <h2 className="mt-6 text-3xl font-bold leading-tight tracking-[-0.025em] text-slate-900 sm:text-4xl lg:text-[42px]">
                    {announcement.title}
                  </h2>

                  <p className="mt-6 max-w-3xl text-base leading-8 text-slate-600 sm:text-lg">
                    {announcement.description}
                  </p>

                  {/* Publication stamp */}
                  <div className="mt-10 max-w-xl border-t border-slate-300 pt-5">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-400">
                          Published by
                        </p>

                        <p className="mt-1 text-sm font-semibold text-slate-700">
                          Organizing Committee
                        </p>
                      </div>

                      <div
                        className="
                          border border-blue-700/25
                          px-4 py-2
                          text-[10px] font-bold
                          uppercase tracking-[0.22em]
                          text-blue-700
                        "
                      >
                        Released
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </section>

        {/* Upcoming updates */}
        <section className="grid gap-12 pt-16 lg:grid-cols-[0.75fr_1.25fr]">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-blue-700">
              Coming next
            </p>

            <h2 className="mt-5 max-w-md text-3xl font-bold leading-tight tracking-[-0.025em] sm:text-4xl">
              More information will be added progressively.
            </h2>

            <p className="mt-5 max-w-md text-base leading-7 text-slate-600">
              Please revisit this page for new conference notices and official
              updates.
            </p>
          </div>

          <div className="divide-y divide-slate-300 border-y border-slate-300">
            {upcomingUpdates.map((item, index) => (
              <div
                key={item}
                className="
                  group flex items-center justify-between
                  py-6
                  transition-colors duration-300
                  hover:text-blue-700
                "
              >
                <div className="flex items-center gap-5">
                  <span className="text-sm font-semibold text-slate-400">
                    0{index + 1}
                  </span>

                  <span className="text-base font-semibold sm:text-lg">
                    {item}
                  </span>
                </div>

                <span
                  className="
                    text-xl text-slate-400
                    transition-all duration-300
                    group-hover:translate-x-1
                    group-hover:text-blue-700
                  "
                >
                  →
                </span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
}

function ReachPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="py-28 max-w-[1000px] mx-auto px-4">
              <Link
                to="/"
                className="
                  group mb-12 my-10 inline-flex items-center gap-3
                  text-sm font-semibold text-slate-600
                  transition-colors duration-300
                  hover:text-blue-700
                "
              >
                <span className="transition-transform duration-300 group-hover:-translate-x-1">
                  ←
                </span>
                Back to Home
              </Link>

      <div className="w-full h-64 md:h-80 mb-8 overflow-hidden">
      <img
        src={campus3}
        alt="IIT Mandi Campus"
        className="w-full h-full object-cover"
        style={{
          WebkitMaskImage:
            "linear-gradient(to right, transparent, black 12%, black 88%, transparent), linear-gradient(to bottom, transparent, black 12%, black 88%, transparent)",
          WebkitMaskComposite: "source-in",
          maskImage:
            "linear-gradient(to right, transparent, black 12%, black 88%, transparent), linear-gradient(to bottom, transparent, black 12%, black 88%, transparent)",
          maskComposite: "intersect",
        }}
      />
      </div>

      <h1 className="text-4xl font-bold mb-8">How to Reach IIT Mandi</h1>

      <h2 className="text-2xl font-bold mt-8 mb-4">By Air</h2>
      <p className="text-gray-700 mb-4">
        The nearest major airport is <strong>Chandigarh International Airport (IXC)</strong>,
        approximately 200 km from IIT Mandi. The journey from the airport to the
        campus takes about 4–5 hours by road.
      </p>

      <p className="text-gray-700 mb-4">
        <strong>Indira Gandhi International (IGI) Airport, New Delhi (DEL)</strong>, is
        another convenient option with excellent domestic and international
        connectivity. The journey to IIT Mandi takes approximately 8–10 hours by road.
      </p>

      <h2 className="text-2xl font-bold mt-8 mb-4">By Train</h2>
      <p className="text-gray-700 mb-4">
      The nearest major railway station is <strong>Chandigarh Railway Station</strong>, which is well
      connected to major cities across India. IIT Mandi is approximately 4–5 hours by road from Chandigarh. 
      Participants may also choose to travel via <strong>Kiratpur Sahib Railway Station</strong> or <strong>Anandpur Sahib Railway Station</strong>, which are significantly closer to IIT Mandi. From Kiratpur Sahib or Anandpur Sahib , the journey to the campus takes about 2.5–3 hours by road depending on traffic conditions. Taxis and other road transport options are readily available from all three railway stations.
      </p>

      <h2 className="text-2xl font-bold mt-8 mb-4">By Road</h2>
      <p className="text-gray-700">
        IIT Mandi is well connected by road from Chandigarh, Delhi, and other major
        cities in North India.
      </p>
    </div>
  );
}

function Footer() {
  return (
    <footer className="bg-gradient-to-b from-[#07182a] to-black text-white">

      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="grid gap-10 lg:grid-cols-[1.4fr_1fr_1.35fr]">

          {/* Conference */}
          <div>
            <div className="flex items-center gap-5">
              <img
                src={conflogo}
                alt="Quantum Peeks 2026"
                className="h-24 w-24 object-contain"
              />

              <img
                src={iitLogo}
                alt="IIT Mandi"
                className="h-20 w-20 object-contain"
              />
            </div>

            <h2 className="mt-4 font-serif text-2xl font-semibold">
              Quantum Peeks 2026
            </h2>

            <p className="mt-2 max-w-md text-sm leading-6 text-gray-400">
              A Himalayan Summit on Coherence in Light and Matter.
            </p>

            <div className="mt-6 space-y-3 text-sm text-gray-400">
              <div>
                <p className="font-semibold text-white">
                  Conference Dates
                </p>
                <p>November 14–18, 2026</p>
              </div>

              <div>
                <p className="font-semibold text-white">
                  Venue
                </p>
                <p>Indian Institute of Technology Mandi</p>
                <p>Kamand, Mandi, Himachal Pradesh, India</p>
              </div>
            </div>
          </div>

          {/* Organized By */}
          <div>
            <h3 className="mb-5 text-lg font-semibold">
              Organized By
            </h3>

            <div className="space-y-5 text-sm text-gray-400">

              <div>
                <p className="font-semibold text-white">
                  School of Physical Sciences
                </p>
                <p>Indian Institute of Technology Mandi</p>
              </div>

              <div>
                <p className="font-semibold text-white">
                  Email
                </p>

                <a
                  href="mailto:quantumpeeks2026@iitmandi.ac.in"
                  className="text-yellow-400 hover:underline"
                >
                  quantumpeeks2026@iitmandi.ac.in
                </a>
              </div>

              <div>
                <p className="font-semibold text-white mb-1">
                  Institute Website
                </p>

                <a
                  href="https://www.iitmandi.ac.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-yellow-400 hover:underline break-all"
                >
                  www.iitmandi.ac.in
                </a>
              </div>

              <Link
                to="/reach"
                className="inline-flex items-center rounded-md border border-yellow-500 px-5 py-2 text-sm font-medium text-yellow-400 transition-all duration-300 hover:bg-yellow-500 hover:text-black"
              >
                How to Reach →
              </Link>

            </div>
          </div>

          {/* Location */}
          <div>
            <h3 className="mb-5 text-lg font-semibold">
              Location
            </h3>

            <div className="overflow-hidden rounded-lg border border-white/10 shadow-lg">
              <iframe
                title="IIT Mandi Map"
                src="https://maps.google.com/maps?q=IIT%20Mandi%20Kamand%20Campus&t=&z=13&ie=UTF8&iwloc=&output=embed"
                className="h-40 w-full border-0"
                loading="lazy"
              />
            </div>

            <p className="mt-4 text-sm leading-6 text-gray-400">
              IIT Mandi, Kamand Campus
              <br />
              Mandi, Himachal Pradesh 175075
              <br />
              India
            </p>
          </div>

        </div>
      </div>

      {/* Bottom */}
      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 py-4 text-center text-sm text-gray-500">
          © 2026 Quantum Peeks. All rights reserved.
        </div>
      </div>
    </footer>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [pathname]);

  return null;
}

export default function ConferenceSite() {
  return (
    <Router basename="/quantumpeeks2026">
      <ScrollToTop />
      <Navbar />

      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/speakers" element={<SpeakersPage />} />
        <Route path="/committee" element={<CommitteePage />} />
        <Route path="/programme" element={<ProgrammePage />} />
        <Route path="/registration" element={<RegistrationPage />} />
        <Route path="/abstract" element={<AbstractFormPage />} />
        <Route
          path="/registration-form"
          element={<RegistrationFormPage />}
        />
        <Route path="/reach" element={<ReachPage />} />
        <Route path="/announcements" element={<AnnouncementsPage />} />
      </Routes>

      <Footer />
    </Router>
  );
}
